/**
 * View models + defensive mappers for the domains the five tabs render.
 *
 * The published ERPat OpenAPI specifies only the response *envelope*, not
 * field-level shapes, so every mapper reads several likely key spellings
 * (snake_case first) and falls back gracefully. When the server's real field
 * names are confirmed (`specs/end-user-api/08-ENDPOINT-CATALOG.md`), tighten
 * the `pick([...])` key lists below — the call sites won't need to change.
 */
import { asRecord, pick, toList, domainBody, type Json } from './http';
// The Compliance mappers `mapDisciplinaryDetail` composes. Aliased: this file has local `text` /
// `dateLabel` variables, and the readers there are not interchangeable with these.
import {
  dateLabel as complianceDateLabel,
  flag as complianceFlag,
  idText as complianceIdText,
  mapCorrectiveActionRow,
  mapDisciplinaryCapabilities,
  text as complianceText,
} from './compliance';
import {
  buildDayDetail,
  clockToMinutes,
  compactDur,
  groupSessionsByDay,
  rollupDayMetrics,
  rowDayKey,
  tallyWorkday,
  type AttendanceDayDetailVM,
  type DayFigureVM,
  type FigureSource,
  type WorkdayTally,
} from './attendance-day';
import { mapAttendanceMetrics, otHours, type AttendanceMetricsVM, type MetricsSource } from './attendance-metrics';
import {
  readAttendanceBreaks,
  readAttendanceEdit,
  type AttendanceBreakPairVM,
  type AttendanceEditVM,
} from './attendance-edit';
// Correcting a record's time logs — re-exported so views import from one place.
export {
  CLOSED_ATTENDANCE_EDIT,
  attendanceEditFormFrom,
  attendanceNextStep,
  buildAttendanceEditBody,
  mapAttendanceScheduleOptions,
  readAttendanceBreaks,
  readAttendanceEdit,
  validateAttendanceEdit,
} from './attendance-edit';
export type {
  AttendanceBreakPairVM,
  AttendanceEditBlockReason,
  AttendanceEditBody,
  AttendanceEditBreakRow,
  AttendanceEditErrors,
  AttendanceEditForm,
  AttendanceEditSource,
  AttendanceEditValidation,
  AttendanceEditVM,
  AttendanceScheduleOptionVM,
} from './attendance-edit';
import {
  OPEN_ATTENDANCE_LOCK,
  attendanceLockLead,
  readAttendanceLock,
  type AttendanceLockVM,
} from './attendance-lock';
// Which attendance lock, if either, shuts a record — re-exported for the views.
export { OPEN_ATTENDANCE_LOCK, attendanceLockLead, readAttendanceLock } from './attendance-lock';
export type { AttendanceLockSource, AttendanceLockVM } from './attendance-lock';
// The metrics block's types + mappers live in `attendance-metrics.ts` (which
// `attendance-day.ts` needs, and which must not import this file back). They are
// re-exported here so every existing import site keeps resolving.
export {
  attendanceHoursLabel,
  mapAttendanceMetrics,
  normalizeOtReviewState,
  readOtBuckets,
  readRowMetrics,
} from './attendance-metrics';
export type {
  AttendanceMetricsVM,
  AttendanceOtBuckets,
  AttendanceOtReviewState,
  AttendanceOtStatus,
  MetricsSource,
  RowMetrics,
} from './attendance-metrics';
import { htmlToText } from '@/utils/sanitize-html';
import { htmlToLines } from '@/utils/html-to-lines';
import { ticketStatusLabel } from '@/utils/ticket-status';
import { extensionOf, fileNameFromUrl, formatBytes, isImageExtension } from '@/utils/file-kind';
import { API_BASE_URL } from '@/utils/env';
import { labelTitleFor } from '@/config/erpat-labels';
import { parseScene, type SketchScene } from '@/utils/sketch-scene';
import { describeUserAgent } from '@/utils/device';
import {
  formatTime,
  formatDate,
  formatRelative,
  formatDurationMinutes,
  formatLeaveDays,
  formatLeaveHours,
  formatMoney,
  initialsOf,
  toDate,
  toMillis,
  localDateKey,
  datesInRange,
} from '@/utils/format';

/** Strip HTML tags / collapse whitespace for plain-text previews (advisory + ticket bodies are HTML). */
function stripHtml(html: unknown): string {
  if (typeof html !== 'string') return '';
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}
import type { RouteLocationRaw } from 'vue-router';
import type { CorrectiveActionVM, DisciplinaryCapabilities } from '@/types/compliance';
import type { Conversation, ChatMessage, MessageAttachment, Contact, GroupMember, PresenceStatus, ConversationVariant, ConversationKind } from '@/types/message';
import type {
  CanonicalNotification,
  NotificationItem,
  NotifCategory,
  NotificationIconVariant,
  NotificationDetailRow,
  NotificationLink,
  NotificationTarget,
  NotificationTargetKind,
} from '@/types/notification';
import {
  REALTIME_ACTION_ROUTES,
  REALTIME_PRIVACY_CLASSES,
  type RealtimeAction,
  type RealtimeActionRoute,
  type RealtimeActor,
  type RealtimePrivacyClass,
  type RealtimeResource,
} from '@/contracts/realtime-envelope-v1';
import { resolveRealtimeRoute, resolveResourceRoute } from '@/contracts/realtime-routes';
import type {
  Ticket,
  TicketMessage,
  TicketStatus,
  TicketRawStatus,
  TicketPriority,
  TicketPerson,
  TicketRole,
  TicketCapabilities,
  TicketEditFields,
  TicketFile,
  TicketTypeInfo,
  TicketTypeOption,
  TicketFormOptions,
  TicketMentionable,
  TicketTemplateOption,
  TicketKbOption,
} from '@/types/ticket';
import type {
  AssetLifecycle,
  AssetDisplayStatus,
  AssetFieldSchemaField,
  AssetCategoryVM,
  AssetCategoryRef,
  AssetOpenIssueVM,
  AssetVM,
  AssetAccessoryVM,
  AssetActivityVM,
  AssetDetailVM,
  AssetIssueVM,
  ReportIssueResultVM,
  EndorsementRole,
  AssetEndorsementVM,
  AssetCustodyItemVM,
  AssetEndorsementDetailVM,
  CaretakerAssetVM,
} from '@/types/asset';
import type {
  TaskDueBucket,
  ProjectLabel,
  ProjectUserRef,
  ProjectPoints,
  ProjectVM,
  ProjectDetailVM,
  ProjectTaskStatusVM,
  ProjectTaskVM,
  ProjectTaskDetailVM,
  ProjectChecklistItemVM,
  ProjectMilestoneVM,
  ProjectMemberVM,
  ProjectCommentVM,
  ProjectTimeLogVM,
  ProjectTimerVM,
  ProjectTimersVM,
} from '@/types/project';

const str = (rec: Json, keys: string[], fallback = ''): string => {
  const v = pick<unknown>(rec, keys, fallback);
  return v == null ? fallback : String(v);
};
const bool = (rec: Json, keys: string[], fallback = false): boolean => {
  const v = pick<unknown>(rec, keys, fallback);
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'string') return ['1', 'true', 'yes', 'on'].includes(v.toLowerCase());
  return fallback;
};
const idOf = (rec: Json, idx = 0): string => str(rec, ['id', 'uuid', '_id', 'key'], String(idx));

// ── Dashboard ──────────────────────────────────────────────────────────────
export interface DashboardAnnouncement {
  id: string;
  title: string;
  desc: string;
  meta: string;
  unread: boolean;
}
export interface DashboardTodo {
  id: string;
  title: string;
  meta: string;
  priority: 'high' | 'medium' | 'low';
}
export interface DashboardVM {
  greetingName: string;
  shift: { startLabel: string; endLabel: string; location: string } | null;
  attendance: { statusLabel: string; timeLabel: string; chipLabel: string; clockedIn: boolean } | null;
  leaveBalanceLabel: string;
  payday: { dateLabel: string; daysToGo: string } | null;
  announcements: DashboardAnnouncement[];
  todos: DashboardTodo[];
}

const normalizePriority = (v: unknown): 'high' | 'medium' | 'low' => {
  const s = String(v ?? '').toLowerCase();
  return s === 'high' || s === 'low' ? s : 'medium';
};

export function mapDashboard(data: unknown): DashboardVM {
  // GET /me/dashboard → { dashboard: { greeting_name, shift, attendance, leave_balance_days,
  //   upcoming_payday, advisories[], todos[] } }. Shift start/end are pre-formatted clock
  //   strings ("10:00 PM"); advisory bodies are HTML.
  const rec = domainBody(data, ['dashboard']);
  const shiftRec = asRecord(pick(rec, ['shift', 'today_shift', 'schedule'], {}));
  const payRec = asRecord(pick(rec, ['upcoming_payday', 'payday', 'next_payday'], {}));

  const shiftStart = str(shiftRec, ['start', 'start_at', 'start_time', 'time_in', 'in_time'], '');
  const shift = shiftStart
    ? {
        startLabel: shiftStart,
        endLabel: str(shiftRec, ['end', 'end_at', 'end_time', 'time_out', 'out_time'], ''),
        location: str(shiftRec, ['location', 'work_mode', 'mode'], 'In Office'),
      }
    : null;

  const paydayDate = pick<unknown>(payRec, ['date', 'pay_date', 'paid_on'], null);
  const daysUntil = pick<number>(payRec, ['days_until', 'days_to_go'], 0);
  const payday = paydayDate
    ? {
        dateLabel: formatDate(paydayDate),
        daysToGo: daysUntil >= 0 ? `${daysUntil} days to go` : 'Recently paid',
      }
    : null;

  const leaveDays = pick<unknown>(rec, ['leave_balance_days', 'leave_balance', 'available_leave_days'], null);

  return {
    greetingName: str(rec, ['greeting_name', 'first_name', 'name'], ''),
    shift,
    // The dashboard `attendance` block is the live clock state; the chip mirrors it.
    attendance: mapDashboardAttendance(pick(rec, ['attendance', 'today_attendance', 'today'], {})),
    leaveBalanceLabel: leaveDays == null ? '—' : String(leaveDays),
    payday,
    announcements: toList(pick(rec, ['advisories', 'announcements'], [])).map((a, i) => {
      const r = asRecord(a);
      return {
        id: idOf(r, i),
        title: str(r, ['title', 'subject'], ''),
        desc: stripHtml(pick(r, ['excerpt', 'summary', 'description', 'body'], '')),
        meta: [
          formatDate(pick<unknown>(r, ['date_created', 'posted_at', 'created_at', 'start_date'], null)),
          str(r, ['author_name', 'author', 'department'], ''),
        ]
          .filter(Boolean)
          .join(' • '),
        unread: !bool(r, ['is_read', 'read']) && pick<unknown>(r, ['is_read', 'read'], null) !== null
          ? true
          : bool(r, ['unread', 'is_unread']),
      };
    }),
    todos: toList(pick(rec, ['todos', 'tasks'], [])).map((t, i) => {
      const r = asRecord(t);
      return {
        id: idOf(r, i),
        title: str(r, ['title', 'name'], ''),
        meta: str(r, ['meta', 'due_label', 'description'], ''),
        priority: normalizePriority(pick(r, ['priority'], 'medium')),
      };
    }),
  };
}

function mapDashboardAttendance(value: unknown): DashboardVM['attendance'] {
  const a = asRecord(value);
  const clockedIn = bool(a, ['is_clocked_in', 'clocked_in']);
  const inAt = pick<unknown>(a, ['clock_in_at', 'time_in', 'in_at'], null);
  if (!clockedIn && !inAt) {
    return { statusLabel: 'Not clocked in', timeLabel: '', chipLabel: 'Out', clockedIn: false };
  }
  return {
    statusLabel: clockedIn ? 'Clocked in' : 'Clocked out',
    timeLabel: formatTime(inAt),
    chipLabel: titleCase(str(a, ['status', 'punctuality'], clockedIn ? 'On time' : 'Done')),
    clockedIn,
  };
}

// ── Attendance (today) ───────────────────────────────────────────────────────
export interface TodayAttendanceVM {
  status: string;
  clockInLabel: string;
  clockOutLabel: string;
  method: string;
  totalHoursLabel: string;
  workMode: string;
  scheduledShiftLabel: string;
  dateLabel: string;
  isClockedIn: boolean;
  isClockedOut: boolean;
  breakTimes: string[];
  /**
   * The punch-in's own LOCAL calendar date (`YYYY-MM-DD`, '' when not clocked
   * in) — the one fact `clockInLabel` throws away by formatting to "8:00 AM".
   *
   * `open_record` is whatever shift is currently open, which is not necessarily
   * one that started today: an overnight shift, or a punch nobody ever clocked
   * out of. Surfaces that present it as today's activity need to be able to
   * tell the difference.
   */
  clockInDateKey: string;
  /**
   * The record was soft-deleted server-side (removed in the ERPat web app).
   *
   * FORWARD-COMPATIBLE, and false against today's backend.
   * `EuapiAttendanceRecord` carries no deletion field yet, so a deleted row and a
   * live one are indistinguishable here — which is exactly the defect described
   * in `docs/attendance-audit-backend-proposal.md`. The flag is read across the
   * spellings that proposal may land under, in the defensive style the mappers
   * use throughout, so the client behaves correctly the moment the backend ships
   * one — and either half of that proposal can land first.
   */
  isDeleted: boolean;
  /**
   * Who removed it, as a DISPLAY label — expected to be a role ("HR") rather
   * than a named administrator; see the disclosure note in the proposal. '' when
   * the API says nothing.
   */
  deletedBy: string;
}

/**
 * Build the Activity tab's today VM by merging two endpoints:
 * - GET /me/attendance/today → `{ is_clocked_in, worked_seconds_today, open_record }`
 *   (worked time is in **seconds**; there is no scheduled-shift info here).
 * - GET /me/schedule/today    → `{ in_time, out_time, schedule_name, is_workday }`
 *   (shift times are pre-formatted clock strings, e.g. "10:00 PM").
 */
export function mapTodayAttendance(attData: unknown, schedData?: unknown): TodayAttendanceVM {
  const a = asRecord(attData);
  const sched = domainBody(schedData, ['schedule']);
  const open = asRecord(pick(a, ['open_record', 'open_attendance', 'record'], {}));

  const clockedIn = bool(a, ['is_clocked_in', 'clocked_in']);
  const inAt = pick<unknown>(open, ['in_time_local', 'in_time', 'clock_in_at', 'time_in'], null);
  const outAt = pick<unknown>(open, ['out_time_local', 'out_time', 'clock_out_at', 'time_out'], null);
  const workedSeconds = pick<number>(a, ['worked_seconds_today', 'worked_seconds'], 0);

  const inTime = str(sched, ['in_time', 'start', 'start_time'], '');
  const outTime = str(sched, ['out_time', 'end', 'end_time'], '');

  // Break taps on the open shift (server-authoritative). Each entry is a
  // { utc, local } object; older/absent responses degrade to []. The array
  // length's PARITY drives the on-break state, so keep exactly one slot per tap
  // — never filter. A placeholder stands in for a malformed empty label so the
  // odd/even count (and the Break Out/Break In alternation) can't desync.
  const breakRaw = pick<unknown>(open, ['break_time', 'breaks'], []);
  const breakTaps: string[] = Array.isArray(breakRaw)
    ? breakRaw.map((entry) => {
        const label =
          typeof entry === 'string' ? entry : str(asRecord(entry), ['local', 'utc', 'time'], '');
        return label !== '' ? label : '—';
      })
    : [];

  // A soft-deleted record describes a shift that no longer exists, so it must not
  // supply live state: not the clock button, not the on-break parity, not any
  // figure derived from the punch. Read across the spellings the backend proposal
  // may land under. `status` is safe to test because its documented values are
  // incomplete|pending|approved|rejected|clockout — none of them a deletion.
  const isDeleted =
    bool(open, ['deleted', 'is_deleted', 'trashed'], false) ||
    str(open, ['deleted_at', 'date_deleted'], '').trim() !== '' ||
    str(open, ['status'], '').trim().toLowerCase() === 'deleted';

  // A break belongs to a PUNCH: without a clock-in there is no shift for one to
  // sit inside, so taps reported alongside a missing or blanked `in_time` are
  // orphans and are dropped. Otherwise a record cleared server-side keeps its
  // break rows on screen — the punch they hang off disappears, the taps do not —
  // and the odd/even count would go on claiming the employee is mid-break with
  // no shift open at all. A deleted record is the same case: its punch is gone.
  const inRaw = isDeleted || typeof inAt !== 'string' ? '' : inAt.trim();
  const breakTimes: string[] = inRaw === '' ? [] : breakTaps;

  const live = clockedIn && !isDeleted;

  return {
    status: live ? 'clocked-in' : workedSeconds > 0 && !isDeleted ? 'clocked-out' : 'not-clocked-in',
    clockInLabel: inAt && !isDeleted ? formatTime(inAt) : '',
    clockOutLabel: outAt && !isDeleted ? formatTime(outAt) : '',
    method: str(open, ['method', 'capture_method', 'log_type'], ''),
    totalHoursLabel: workedSeconds ? formatDurationMinutes(workedSeconds / 60) : '0h 00m',
    workMode: str(sched, ['work_mode', 'mode', 'schedule_name'], 'In Office'),
    scheduledShiftLabel: inTime ? `${inTime} – ${outTime}` : '',
    dateLabel: formatDate(pick<unknown>(sched, ['date'], new Date())),
    isClockedIn: live,
    isClockedOut: !live && workedSeconds > 0 && !isDeleted,
    breakTimes,
    clockInDateKey: inRaw.slice(0, 10).match(/^\d{4}-\d{2}-\d{2}$/)?.[0] ?? '',
    isDeleted,
    deletedBy: str(open, ['deleted_by', 'deleted_by_name', 'removed_by'], '').trim(),
  };
}

// ── Weekly schedule (Home "This Week Overview") ──────────────────────────────
/**
 * One day's state in the week overview, derived from the weekly schedule + the
 * caller's own attendance:
 *   present   — an attendance punch exists that is approved or still in progress
 *               (an open, clocked-in shift is `incomplete`, which reads present)
 *   pending   — a completed punch awaiting manager approval (row status `pending`)
 *   absent    — a past workday with no punch
 *   scheduled — a future workday
 *   today     — today's workday, not yet punched
 *   rest      — a non-workday (no shift assigned)
 *   incomplete — a past punch that was never clocked out (see below)
 *   holiday   — a workday on a non-working holiday that applies to the employee
 *   leave     — a workday covered by the caller's approved paid leave
 */
export type WeekDayStatus =
  | 'present'
  | 'pending'
  | 'absent'
  | 'scheduled'
  | 'today'
  | 'rest'
  /**
   * A PAST day whose punch was never clocked out — ERPat's own `incomplete`
   * row status, or the `clockout` its auto-clock-out job leaves on a punch nobody
   * closed. Only ever a past day: an open punch on the CURRENT day is a shift in
   * progress, which reads present.
   */
  | 'incomplete'
  /**
   * A scheduled workday that falls on a NON-WORKING holiday (regular or special)
   * applying to the employee — the same `holiday` the server's
   * `/me/attendance/summary` files such a day under. Never an absence. A rest day
   * stays `rest`, and a punch still outranks it (see `buildScheduleDays`).
   */
  | 'holiday'
  /**
   * A scheduled workday covered by approved PAID leave — the summary's
   * `on_leave`. Read from two sources that follow one rule: the caller's own
   * approved leave (`mapLeaveDays`, the same `leave_cover()` test the server
   * applies, so a leave day still ahead reads on leave too) and, on the payroll
   * cut-off for the days it has judged, the summary's own verdict. Never an
   * absence. A holiday outranks it (no shift to excuse) and a punch outranks
   * both, exactly as on the server (see `buildScheduleDays`).
   */
  | 'leave';

/**
 * ERPat's verdict on a holiday's PAY, as `/me/attendance/summary` reports it per
 * day (`holiday_pay`). It is the web Attendance → Summary tab's own decision
 * (`AttendancesTrait::regular_holiday_status()`), made only for a REGULAR holiday
 * on a Shift Planner date: a holiday whose rule is `required_to_work` is paid
 * without working when the employee has APPROVED attendance on their last
 * scheduled shift before it (paid leave on that shift counts only alongside
 * approved attendance in the days just before; a shift still awaiting approval
 * counts for nothing yet); one whose rule is `paid_no_operation` is paid
 * regardless; any other rule — the EMPTY rule every preset holiday ships with
 * included — is deducted regardless of the day before. That is the web's
 * reading of the DOLE rule, not the rule itself; the gaps are recorded in the
 * HumanResource CHANGELOG (1.62.0, "Known divergence").
 *   ''              — not a holiday for the caller;
 *   not_evaluated   — a non-working holiday ERPat did not judge (a special
 *                     holiday, or a day the Shift Planner did not schedule) —
 *                     read exactly as a holiday always was;
 *   paid            — a regular holiday paid without working;
 *   forfeited       — a regular holiday with NO holiday pay, so ERPat counts the
 *                     day as an ABSENCE (its row is `status: absent`,
 *                     `absent_days: 1`, `scheduled: true`);
 *   worked          — the caller punched on the holiday itself. On a holiday
 *                     ERPat would have forfeited, the day is judged as the
 *                     ordinary shift it was (`scheduled: true`, status present /
 *                     late / half_day); on a paid or unjudged one the row stays a
 *                     `holiday` (`scheduled: false`).
 * An older backend omits the key; that reads '' and changes nothing.
 */
export type HolidayPayVerdict = '' | 'not_evaluated' | 'paid' | 'forfeited' | 'worked';

/** The holiday's own `special_rule` — informational, it shapes the day copy only. */
export type HolidayRule = '' | 'required_to_work' | 'no_business_operation' | 'paid_no_operation';

/**
 * ONE of the caller's own leave applications that reaches a day — pending,
 * endorsed (`for_review`) or approved. Rejected and cancelled applications are
 * never carried: they say nothing about the day.
 *
 * Deliberately NOT the same thing as `WeekDayVM.leaveTitle` / the `leave`
 * status. Those follow the server's `leave_cover()` — approved AND paid, the
 * only leave that excuses an absence — while this is the applicant's own record
 * of what they filed, shown so an Absent day can say a request covering it is
 * still awaiting a decision, or was approved without pay. See
 * {@link mapLeaveApplicationsByDay}.
 */
export interface DayLeaveVM {
  id: string;
  /** The leave type's title, e.g. "Sick Leave". */
  type: string;
  /** The leave type's colour ('' when none) — a hex the Leaves page also renders. */
  typeColor: string;
  statusVariant: LeaveStatusVariant;
  /** As the APPLICANT reads it: "Pending", "Pending final approval", "Approved". */
  statusLabel: string;
  /** The leave type's `paid` flag — an approved UNPAID leave is deducted like an absence. */
  paid: boolean;
  /** YYYY-MM-DD */
  startDate: string;
  /** YYYY-MM-DD — equals `startDate` for a single-day application. */
  endDate: string;
  /** "Sep 21 – Sep 23, 2026 (3 days)" — the Leaves page's own range label. */
  dateRangeLabel: string;
  totalDays: number;
  reason: string;
  /** Who gave the FIRST approval under dual approval ('' when nobody has). */
  endorsedByName: string;
  /** When it was endorsed, e.g. "Sep 13, 2026" ('' when it was not). */
  endorsedAtLabel: string;
  /** Who made the final decision ('' while none has been made). */
  checkerName: string;
}

export interface WeekDayVM {
  /** Local calendar date, YYYY-MM-DD (also the list key). */
  key: string;
  /** Three-letter weekday, e.g. "Sun". */
  dayLabel: string;
  /** Day-of-month number, e.g. "18". */
  dayNumber: string;
  /** True for the current local date. */
  isToday: boolean;
  /** True when the weekly schedule marks this weekday a workday. */
  isWorkday: boolean;
  /** Shift clock-in label from the schedule, e.g. "08:00" ('' on rest days). */
  inLabel: string;
  /** Shift clock-out label from the schedule, e.g. "17:00" ('' on rest days). */
  outLabel: string;
  status: WeekDayStatus;
  statusLabel: string;
  /**
   * The holiday's name when this WORKDAY is a non-working holiday for the
   * employee ('' otherwise). Set even when a punch outranks the `holiday`
   * status, so a worked holiday still says which holiday it was.
   */
  holidayTitle: string;
  /**
   * The leave type's title (e.g. "Vacation Leave") when this WORKDAY is covered
   * by the caller's approved paid leave ('' otherwise). Like `holidayTitle`, set
   * even when a punch outranks the `leave` status — a worked leave day still says
   * which leave it was — and '' on a rest day.
   */
  leaveTitle: string;
  /**
   * ERPat's holiday-pay verdict for the day ({@link HolidayPayVerdict}). Only
   * `/me/attendance/summary` can set it — the schedule-built day reads '', and a
   * summary row that is a holiday but predates the field reads `not_evaluated`.
   * A `forfeited` day is `status: absent` and still carries its `holidayTitle`,
   * so a surface can say WHICH holiday earned no pay and why.
   */
  holidayPay: HolidayPayVerdict;
  /** The holiday's `special_rule` when known ('' otherwise) — shapes the copy only. */
  holidayRule: HolidayRule;
  /**
   * The caller's own leave applications reaching this date that are pending,
   * endorsed or approved ({@link DayLeaveVM}) — every status the applicant
   * would want to see beside an Absent day. Set on rest days too: a leave
   * filed over a rest day is still the applicant's record, and hiding it would
   * make a Saturday look as if the request skipped it. Empty when none.
   */
  leaves: DayLeaveVM[];
}

export interface WeekScheduleVM {
  /** Schedule name, e.g. "Regular Office Hours" ('' when none assigned). */
  scheduleName: string;
  /** Human week range, e.g. "May 18 – May 24, 2025". */
  rangeLabel: string;
  days: WeekDayVM[];
}

/** Weekday keys as the schedule's `days` object spells them, indexed by Date.getDay() (0=Sun). */
const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

const WEEK_DAY_STATUS_LABELS: Record<WeekDayStatus, string> = {
  present: 'Present',
  incomplete: 'Incomplete',
  pending: 'Pending',
  absent: 'Absent',
  scheduled: 'Scheduled',
  today: 'Today',
  rest: 'Rest Day',
  holiday: 'Holiday',
  leave: 'On Leave',
};

function weekRangeLabel(weekDates: Date[]): string {
  const first = weekDates[0];
  const last = weekDates[weekDates.length - 1];
  if (!first || !last) return '';
  const start = first.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const end = last.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${start} – ${end}`;
}

/**
 * Build the Home "This Week Overview" by overlaying the caller's own attendance
 * history onto their default weekly schedule, for the seven `weekDates`
 * (Sunday-first). When attendance is missing (e.g. the list failed) days still
 * render from the schedule alone — past workdays just can't be marked present.
 *
 * - GET /me/schedule    → { schedule: { schedule_name, days: { mon..sun: { in_time, out_time, is_workday } } } }
 * - GET /me/attendance  → rows with `in_time_local` ("Y-m-d H:i:s"); the date portion is the local punch day.
 * - `holidays` / `leaves` → {@link mapHolidayDays}' and {@link mapLeaveDays}' date → title maps.
 * - `leaveApps` → {@link mapLeaveApplicationsByDay}' date → applications map,
 *   built from the SAME `/me/leaves` rows as `leaves` so the two can't disagree.
 */
export function mapWeekSchedule(
  schedData: unknown,
  attendanceData: unknown,
  weekDates: Date[],
  holidays: ReadonlyMap<string, string> = new Map(),
  leaves: ReadonlyMap<string, string> = new Map(),
  leaveApps: ReadonlyMap<string, DayLeaveVM[]> = new Map(),
): WeekScheduleVM {
  const sched = domainBody(schedData, ['schedule']);
  return {
    scheduleName: str(sched, ['schedule_name', 'name'], ''),
    rangeLabel: weekRangeLabel(weekDates),
    days: buildScheduleDays(sched, attendanceData, weekDates, holidays, leaves, leaveApps),
  };
}

/**
 * Index the caller's own attendance rows by the day each is FILED under —
 * `rowDayKey`: ERPat's `metrics.log_date` when the row carries figures, else
 * the local punch-in date — carrying the approval state of each day. Keyed the
 * same way `groupSessionsByDay` keys the sessions, so a night shift's status
 * and its punches can never land on different days. Any row on a day counts as
 * a punch — except a `rejected` one, which is not attendance (the server's
 * summary skips it, and so does `countedSessions`); `approved` and `pending`
 * track the row `status` field (same vocabulary as the manager view, via
 * normalizeTeamAttendanceStatus) so a day can be shown as awaiting review.
 */
function punchDaysOf(
  attendanceData: unknown,
): Map<string, { approved: boolean; pending: boolean; incomplete: boolean }> {
  const punchDays = new Map<string, { approved: boolean; pending: boolean; incomplete: boolean }>();
  for (const row of toList(attendanceData)) {
    const r = asRecord(row);
    const key = rowDayKey(r);
    if (!key) continue;
    const rowStatus = normalizeTeamAttendanceStatus(pick(r, ['status'], ''));
    if (rowStatus === 'rejected') continue;
    const agg = punchDays.get(key) ?? { approved: false, pending: false, incomplete: false };
    if (rowStatus === 'approved') agg.approved = true;
    if (rowStatus === 'pending') agg.pending = true;
    // A punch with no real clock-out, in either state ERPat leaves one in:
    // `incomplete` (still open — every row's status at clock-in) or `clockout`,
    // which the auto-clock-out job (`AttendanceAutoClockinAndOutJob`) stamps on a
    // punch nobody closed, with out_time = in_time. Once that job has run, a
    // forgotten punch is never `incomplete` again, so reading only that status
    // let the day fall through to "present". The Attendance list already files
    // both under Incomplete (`isIncompleteStatus`).
    //
    // When the punch had break taps the job closes it at the last tap as
    // `pending` instead; ERPat web queues that for approval, so it stays pending.
    if (rowStatus === 'incomplete' || rowStatus === 'clockout') agg.incomplete = true;
    punchDays.set(key, agg);
  }
  return punchDays;
}

/**
 * Overlay the caller's attendance history onto their weekly schedule for an
 * arbitrary run of `dates`. Shared by the week strip and the payroll-cutoff
 * card — the only difference between them is how long the run is.
 *
 * `sched` is the already-unwrapped `{ schedule: … }` domain body. When
 * attendance is missing (e.g. the list failed) days still render from the
 * schedule alone — past workdays just can't be marked present. `holidays` is
 * {@link mapHolidayDays}' date → title map and `leaves` is {@link mapLeaveDays}';
 * an empty one marks no day a holiday / on leave. `leaveApps` is
 * {@link mapLeaveApplicationsByDay}' — the applications themselves, attached to
 * every day they reach (rest days included) without deciding any status.
 *
 * `holidayPay` is always '' here: only the server can judge a holiday's pay
 * ({@link applySummaryDay} overlays it), and the schedule alone treats every
 * non-working holiday as it always has.
 */
function buildScheduleDays(
  sched: Json,
  attendanceData: unknown,
  dates: Date[],
  holidays: ReadonlyMap<string, string>,
  leaves: ReadonlyMap<string, string> = new Map(),
  leaveApps: ReadonlyMap<string, DayLeaveVM[]> = new Map(),
): WeekDayVM[] {
  const days = asRecord(pick(sched, ['days'], {}));
  const punchDays = punchDaysOf(attendanceData);
  const todayKey = localDateKey(new Date());

  return dates.map((date) => {
    const key = localDateKey(date);
    const slot = asRecord(pick(days, [WEEKDAY_KEYS[date.getDay()]!], {}));
    const isWorkday = bool(slot, ['is_workday', 'workday'], false);
    const isToday = key === todayKey;
    const punch = punchDays.get(key);

    // A punch overrides the schedule-derived states. Within a punched day, show
    // `pending` only while it awaits approval (and nothing on it is approved);
    // approved or not-yet-reviewed punches read `present`.
    let status: WeekDayStatus;
    // A PAST punch the server still calls `incomplete` was never clocked out.
    // `incomplete` is the status ERPat gives every row AT clock-in (clock-out moves
    // it to `pending`), so on a past day it means exactly "no clock-out" — and
    // ERPat web labels the row "Incomplete". Syntry says the same rather than
    // calling the day Present.
    //
    // Deliberately past-only: an open punch on the CURRENT day carries the very
    // same `incomplete` status while the employee is simply still working, and
    // labelling a shift in progress "Incomplete" would be wrong every morning.
    if (punch && punch.incomplete && key < todayKey) status = 'incomplete';
    else if (punch) status = punch.pending && !punch.approved ? 'pending' : 'present';
    else if (!isWorkday) status = 'rest';
    // A non-working holiday has no shift to miss, so it outranks today /
    // scheduled / absent. The schedule alone cannot judge holiday PAY, so a past
    // regular holiday ERPat FORFEITED still reads Holiday on a schedule-only
    // surface (the Home week strip) while the summary-backed cut-off and report
    // read it Absent — only `applySummaryDay` can close that gap.
    else if (holidays.has(key)) status = 'holiday';
    // Approved paid leave covers the day — already taken, today, or still ahead
    // — so it outranks today / scheduled / absent: the server files a day whose
    // missed halves are ALL covered by paid leave as `on_leave`, whether or not
    // the day has been judged yet. This map marks the whole date, so a half-day
    // leave on a wholly missed shift reads On Leave here where the server reads
    // `half_day` — `applySummaryDay` corrects it wherever the summary has the
    // day. The rest day and holiday tests above still win, as they do in
    // `Absence_tardiness_calculator::tally_day()`.
    else if (leaves.has(key)) status = 'leave';
    else if (isToday) status = 'today';
    else if (key > todayKey) status = 'scheduled';
    else status = 'absent';

    return {
      key,
      dayLabel: date.toLocaleDateString('en-US', { weekday: 'short' }),
      dayNumber: String(date.getDate()),
      isToday,
      isWorkday,
      inLabel: str(slot, ['in_time', 'start', 'start_time'], ''),
      outLabel: str(slot, ['out_time', 'end', 'end_time'], ''),
      status,
      statusLabel: WEEK_DAY_STATUS_LABELS[status],
      holidayTitle: isWorkday ? (holidays.get(key) ?? '') : '',
      leaveTitle: isWorkday ? (leaves.get(key) ?? '') : '',
      holidayPay: '',
      holidayRule: '',
      leaves: leaveApps.get(key) ?? [],
    };
  });
}

/**
 * The dates in [startKey, endKey] that are NON-WORKING holidays for the caller,
 * as `YYYY-MM-DD` → holiday title.
 *
 * Mirrors the server's own rule — `Holidays_model::get_non_working_for_user`,
 * which `/me/attendance/summary` excuses absences by — from two end-user reads.
 * The summary itself can't be used: it clamps its window to today, and a cut-off
 * still in progress has holidays ahead of it.
 *   - `/general/holidays` rows of type `regular` or `special` (`unofficial` is on
 *     the calendar but still worked);
 *   - that cover all establishments, or one the employee is assigned to —
 *     `/work-locations/mine`, the same `assignee` membership the server tests.
 *
 * A null `workLocationsData` (that read failed) keeps only all-establishment
 * holidays: a branch holiday that can't be confirmed for this employee is left
 * off, so a workday is never excused on a guess.
 */
export function mapHolidayDays(
  holidaysData: unknown,
  workLocationsData: unknown,
  startKey: string,
  endKey: string,
): Map<string, string> {
  const assigned = new Set(
    toList(workLocationsData)
      .map((row) => str(asRecord(row), ['id'], ''))
      .filter((id) => id !== '' && id !== '0'),
  );

  const out = new Map<string, string>();
  for (const row of toList(holidaysData)) {
    const r = asRecord(row);
    const type = str(r, ['type'], '').toLowerCase();
    if (type !== 'regular' && type !== 'special') continue;

    const rawIds = pick<unknown>(asRecord(pick(r, ['establishment'], {})), ['ids'], []);
    const ids = Array.isArray(rawIds) ? rawIds.map(String) : [];
    const applies =
      bool(r, ['is_all_establishments'], false) || ids.includes('all') || ids.some((id) => assigned.has(id));
    if (!applies) continue;

    const from = str(r, ['date_from', 'date', 'start_date'], '').slice(0, 10);
    const to = str(r, ['date_to', 'end_date'], '').slice(0, 10) || from;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) continue;

    const title = str(r, ['title', 'name'], '').trim() || 'Holiday';
    // Clipped to the window: the list matches a holiday by either end, so a
    // multi-day one can reach past it.
    for (const date of datesInRange(from < startKey ? startKey : from, to > endKey ? endKey : to)) {
      const key = localDateKey(date);
      if (!out.has(key)) out.set(key, title);
    }
  }
  return out;
}

/**
 * The dates in [startKey, endKey] covered by the caller's approved PAID leave,
 * as `YYYY-MM-DD` → leave type title.
 *
 * Mirrors the server's own rule — `Absence_tardiness_calculator::leave_cover()`,
 * which `/me/attendance/summary` excuses missed halves by — from the end-user
 * leave list (`/me/leaves?status=approved` over the window; the model matches
 * an application that overlaps the window by either end). The summary itself
 * can't stand in for it: it clamps its window to today, and the week strip and
 * a cut-off still in progress have leave days ahead of them.
 *   - `approved` only. The query already says so, but an older backend that
 *     ignored the filter must not turn a pending request into a marked day.
 *   - PAID only — `paid`, the leave type's flag, shipped on every row. The server
 *     skips unpaid leave because payroll deducts it exactly like an absence, so
 *     marking it here would hide an absence the cut-off goes on to count.
 *   - A single-day application covers its `total_days` snapped DOWN to a half:
 *     an hourly leave shorter than half a day covers nothing (it cannot excuse
 *     a missed half), while a zero or missing total is a whole day, which is
 *     what such legacy rows mean. A multi-day application covers every date in
 *     its span.
 *
 * Only WHETHER a day is covered is kept, so the value is the title a day row can
 * show. The server is finer-grained: it files an unattended day `on_leave` only
 * when the leave covers every missed half, and a half-day leave on a wholly
 * missed shift `half_day` — a difference only a summary row can settle
 * ({@link applySummaryDay}). A day two applications reach keeps the first one's
 * title.
 */
export function mapLeaveDays(leavesData: unknown, startKey: string, endKey: string): Map<string, string> {
  const out = new Map<string, string>();
  for (const row of toList(leavesData)) {
    const r = asRecord(row);
    if (leaveStatusVariant(str(r, ['status'], '')) !== 'approved') continue;
    if (!bool(r, ['paid'], false)) continue;

    const from = str(r, ['start_date'], '').slice(0, 10);
    const to = str(r, ['end_date'], '').slice(0, 10) || from;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) continue;

    if (from === to) {
      const total = Number(pick<unknown>(r, ['total_days'], 0));
      const size = Number.isFinite(total) && total > 0 ? Math.floor(Math.min(1, total) * 2) / 2 : 1;
      if (size <= 0) continue;
    }

    const title = str(r, ['leave_type_title', 'leave_type', 'type'], '').trim() || 'Leave';
    // Clipped to the window: the list matches an application by either end, so
    // a multi-day one can reach past it.
    for (const date of datesInRange(from < startKey ? startKey : from, to > endKey ? endKey : to)) {
      const key = localDateKey(date);
      if (!out.has(key)) out.set(key, title);
    }
  }
  return out;
}

/**
 * The caller's own leave APPLICATIONS reaching each date in [startKey, endKey],
 * as `YYYY-MM-DD` → the applications on that date ({@link DayLeaveVM}).
 *
 * The companion to {@link mapLeaveDays}, fed from the SAME `/me/leaves` rows
 * (the list is read once, without a status filter): where that map decides
 * whether a day is EXCUSED — approved and paid, the server's `leave_cover()` —
 * this one carries what the employee FILED, so an Absent day can show the
 * request that covers it and where it stands.
 *   - `pending`, `for_review` and `approved` only, resolved through
 *     {@link leaveStatusVariant}. A rejected or cancelled application says
 *     nothing about the day and is dropped.
 *   - Paid and UNPAID alike: an approved unpaid leave does not excuse the day
 *     (payroll deducts it like an absence — `Absence_tardiness_calculator::
 *     tally_day()` skips it), which is exactly why the day must still say the
 *     leave was approved.
 *   - Every date of the application's span, clipped to the window; a missing
 *     `end_date` is the start date. No half-day snapping — the application is
 *     shown as filed, whatever fraction of the day it covers.
 *   - Stable order: by `created_at`, then id, so a day two applications reach
 *     lists the earlier one first, on every surface.
 * `statusLabel` is the applicant's wording — `for_review` reads "Pending final
 * approval", as on the Leaves page (`APPLICANT_LEAVE_STATUS_LABELS`).
 */
/**
 * Is this leave application an HOURLY one? ERPat marks none: an hourly leave is ONE
 * date with a fractional `total_days` and its `total_hours` (4 of an 8-hour day =
 * 0.5 / 4), and ERPat's own approval recognises it the same way (one date, a
 * fraction below a whole day).
 */
export function isHourlyLeave(start: string, end: string, totalDays: number, totalHours: number): boolean {
  return start === end && totalDays > 0 && totalDays < 1 && totalHours > 0;
}

/**
 * A leave request's length on its own: "4 hours" for an hourly leave, else its
 * day share ("0.25 day", "3 days") — '' without a total. The status sheet, the
 * detail sheet and the notification expansion all read it from here.
 */
export function leaveDurationLabel(r: Pick<LeaveRequestVM, 'isHourly' | 'totalDays' | 'totalHours'>): string {
  if (r.isHourly) return formatLeaveHours(r.totalHours);
  return r.totalDays > 0 ? formatLeaveDays(r.totalDays) : '';
}

/**
 * The " (…)" a leave's date range carries on every list: " (4 hours)" for an hourly
 * leave, else " (N day/days)" — '' without a total. One home, so the employee's
 * requests, the approver's queue and the calendar's day sheet name a leave alike.
 */
function leaveSpanSuffix(start: string, end: string, totalDays: number, totalHours: number): string {
  if (isHourlyLeave(start, end, totalDays, totalHours)) return ` (${formatLeaveHours(totalHours)})`;
  return totalDays ? ` (${totalDays} ${totalDays === 1 ? 'day' : 'days'})` : '';
}

export function mapLeaveApplicationsByDay(
  leavesData: unknown,
  startKey: string,
  endKey: string,
): Map<string, DayLeaveVM[]> {
  const rows = toList(leavesData)
    .map((row, i) => ({ r: asRecord(row), i }))
    .sort((a, b) => {
      const byCreated = str(a.r, ['created_at', 'date_created'], '').localeCompare(
        str(b.r, ['created_at', 'date_created'], ''),
      );
      if (byCreated !== 0) return byCreated;
      return idOf(a.r, a.i).localeCompare(idOf(b.r, b.i), undefined, { numeric: true });
    });

  const out = new Map<string, DayLeaveVM[]>();
  for (const { r, i } of rows) {
    const variant = leaveStatusVariant(str(r, ['status'], ''));
    if (variant !== 'approved' && !isLeaveAwaitingDecision(variant)) continue;

    const from = str(r, ['start_date'], '').slice(0, 10);
    const to = str(r, ['end_date'], '').slice(0, 10) || from;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) continue;

    const totalRaw = Number(pick<unknown>(r, ['total_days'], 0));
    const totalDays = Number.isFinite(totalRaw) && totalRaw > 0 ? totalRaw : 0;
    const hoursRaw = Number(pick<unknown>(r, ['total_hours'], 0));
    const totalHours = Number.isFinite(hoursRaw) && hoursRaw > 0 ? hoursRaw : 0;
    const rangeBase = from === to ? formatDate(from) : `${formatDate(from)} – ${formatDate(to)}`;
    const leave: DayLeaveVM = {
      id: idOf(r, i),
      type: str(r, ['leave_type_title', 'leave_type', 'type'], '').trim() || 'Leave',
      typeColor: str(r, ['leave_type_color', 'color'], '').trim(),
      statusVariant: variant,
      statusLabel: APPLICANT_LEAVE_STATUS_LABELS[variant] ?? titleCase(str(r, ['status'], '')),
      paid: bool(r, ['paid'], false),
      startDate: from,
      endDate: to,
      dateRangeLabel: `${rangeBase}${leaveSpanSuffix(from, to, totalDays, totalHours)}`,
      totalDays,
      reason: str(r, ['reason'], '').trim(),
      endorsedByName: str(r, ['first_checker_name'], '').trim(),
      endorsedAtLabel: formatDate(pick<unknown>(r, ['first_checked_at'], null)),
      checkerName: str(r, ['checker_name'], '').trim(),
    };

    for (const date of datesInRange(from < startKey ? startKey : from, to > endKey ? endKey : to)) {
      const key = localDateKey(date);
      const list = out.get(key);
      if (list) list.push(leave);
      else out.set(key, [leave]);
    }
  }
  return out;
}

// ── Payroll cut-off timesheet (Timesheet tab) ────────────────────────────────
/**
 * Where a cut-off sits relative to today. Drives the status pill only — the day
 * rows carry their own per-day status.
 */
export type CutoffState = 'upcoming' | 'in-progress' | 'completed';

const CUTOFF_STATE_LABELS: Record<CutoffState, string> = {
  upcoming: 'Upcoming',
  'in-progress': 'In Progress',
  completed: 'Completed',
};

/**
 * ONE payroll cut-off, measured.
 *
 * Deliberately carries no name or date label: the Timesheet browser shows a
 * single cut-off at a time and titles it in the navigator above the card, from
 * the ladder it is stepping through. A label here would be a second copy of that
 * string, free to disagree with it.
 */
export interface CutoffPeriodVM {
  /** Stable list key — the server period key, or `start_end` when derived. */
  key: string;
  state: CutoffState;
  stateLabel: string;
  /**
   * Days payroll expects a shift on. Wherever the summary judged the day, this
   * is ERPat's own `scheduled` flag — a regular workday, a day on leave, or a
   * regular holiday ERPat FORFEITED (worked or not: payroll judges it like any
   * other workday, so it stays in the denominator its absence is measured
   * against); a paid or unjudged holiday is not counted. Without a summary row,
   * the weekly schedule's workdays less the device's non-working holidays.
   */
  workingDays: number;
  /**
   * Days attended — ERPat's own `attended` wherever the summary judged the day
   * (the Home widget's days worked), else days with a punch, rest days included.
   */
  daysPresent: number;
  /**
   * Absences in DAYS, half-day steps (1.5 = a whole day plus a missed half): the
   * sum of every day's `tally.absentDays`, so it is the same total the Home
   * widget and the Reports module show for this cut-off.
   */
  daysAbsent: number;
  /**
   * Where absences and elapsed-day statuses came from: `server` — ERPat's
   * `/me/attendance/summary` (Shift Planner, paid leave, holidays and the grace
   * period applied); `device` — the on-device fallback for a backend without
   * that route, or a cut-off that has not started. Each day's lateness,
   * undertime and overbreak carry their own provenance (`CutoffDayVM.sources`).
   */
  source: 'server' | 'device';
  /** Every calendar day in the span, for the toggled-down schedule. */
  days: CutoffDayVM[];
}

/**
 * One day in the cut-off list: the week strip's row, plus everything the day
 * sheet shows when the row is tapped.
 *
 * The detail rides ALONG with the list rather than being fetched per tap: the
 * cut-off already pulls every punch in the span to build these rows, so the
 * sheet is measured from data that is on the device, and opening a day costs no
 * round-trip. It is a separate type from {@link WeekDayVM} so the Home week
 * strip — which shares `buildScheduleDays` but shows no sheet — is untouched.
 */
/** A cut-off day's pay-period figures: the workday tally plus overbreak. */
export interface CutoffDayTally extends WorkdayTally {
  /**
   * Break time past the allowance, in minutes — ERPat's own figure wherever the
   * day has one (`sources.overbreak`), else an estimate against
   * `NORMAL_BREAK_MINUTES`.
   */
  overbreakMinutes: number;
}

/** Provenance of each of a cut-off day's figures — see `FigureSource`. */
export interface CutoffDaySources {
  /** `tally.tardyMinutes` / the sheet's Late figure. */
  late: FigureSource;
  /** `tally.undertimeMinutes` / the sheet's Undertime figure. */
  undertime: FigureSource;
  /** `tally.overbreakMinutes` / the sheet's Over Break figure. */
  overbreak: FigureSource;
  /** The sheet's Work figure. */
  worked: FigureSource;
  /** The sheet's Overtime figure. */
  overtime: FigureSource;
}

export interface CutoffDayVM extends WeekDayVM {
  /** Work / late / OT / overbreak / breaks / punches / notes for the day. */
  detail: AttendanceDayDetailVM;
  /** Human date for the sheet's title, e.g. "Thursday, September 10". */
  fullDateLabel: string;
  /**
   * The day's absence / lateness / undertime as the pay-period totals count
   * them. Summed over the cut-off it is the Timesheet's Days Absent, the Reports
   * totals and — for absences and lateness — the Home widget's figures.
   *
   * `absentDays` already includes a whole missed day, so a reader totals this
   * field alone; it is ERPat's own per-day figure when {@link fromServer}.
   * `tardyMinutes`, `undertimeMinutes` and `overbreakMinutes` each carry their
   * own provenance in {@link sources}: ERPat's figure (stored, or computed live)
   * wherever the day has a clocked-out record — read off the summary row, or
   * off the punches' own `metrics` — else the summary's measured tardiness /
   * device estimates. The day sheet's Late, Undertime and Over Break figures
   * read these same numbers.
   */
  tally: CutoffDayTally;
  /**
   * True when the day started late — `tally.tardyMinutes` is above zero. A tag
   * shown BESIDE the status, never in place of it: a late day was still worked,
   * so it reads Present (or Pending / Incomplete) and Late. A start past the
   * half-day line is a half-day absence with no tardy minutes, so it is not late.
   */
  late: boolean;
  /** True when this day's status and absence came from ERPat's summary. */
  fromServer: boolean;
  /** Where each figure came from — what a surface labels it by. */
  sources: CutoffDaySources;
  /** True when `tally.tardyMinutes` is the device ESTIMATE (`sources.late === 'device'`). */
  tardyEstimated: boolean;
  /**
   * True when `tally.undertimeMinutes` is the device ESTIMATE
   * (`sources.undertime === 'device'`) rather than ERPat's own figure.
   */
  undertimeEstimated: boolean;
  /** The same, for `tally.overbreakMinutes`. */
  overbreakEstimated: boolean;
}

/** One day of `/me/attendance/summary` (`Absence_tardiness_calculator::tally_day`). */
interface SummaryDay {
  /** present | late | half_day | absent | on_leave | holiday | rest_day | scheduled */
  status: string;
  /** A workday under the Shift Planner (else the weekly schedule), not a holiday. */
  scheduled: boolean;
  attended: boolean;
  /** 0, 0.5 or 1 — after approved paid leave. */
  absentDays: number;
  /**
   * Whole minutes — ERPat's own `late` when the day has a clocked-out record
   * (`metricsSource` not `none`), else the calculator's measurement from the
   * punches, grace period applied.
   */
  tardyMinutes: number;
  /** ERPat's undertime minutes for the date; null on a backend whose summary predates the field. */
  undertimeMinutes: number | null;
  /** ERPat's overbreak minutes for the date; null on a backend whose summary predates the field. */
  overbreakMinutes: number | null;
  /**
   * Where the day's tardy / undertime / overbreak came from: `stored` (the
   * approved record's payroll row), `computed` (a live engine run on a pending
   * clocked-out record), `none` (no such record — the figures are the
   * calculator's own, or absent). An older backend sends only
   * `metrics_approved`, which reads `stored` when true and `none` otherwise —
   * see {@link provenanceDeclared} for what that fallback does NOT vouch for.
   */
  metricsSource: MetricsSource;
  /**
   * True when the row named its provenance outright (`metrics_source`). False
   * on the `metrics_approved` fallback: that backend's `undertime_minutes` /
   * `overbreak_minutes` are indeed the stored figures (it sent them for an
   * approved day only), but its `tardy_minutes` came from its own calculator —
   * server-authoritative, yet not the engine's `late` — so the tardy figure
   * reads `summary` there, never `stored`.
   */
  provenanceDeclared: boolean;
  /** `metricsSource === 'stored'` — kept for readers of the older flag. */
  metricsApproved: boolean;
  /**
   * The holiday's title when the date is a non-working holiday for the caller
   * ('' otherwise) — shipped on a FORFEITED row too, whose `status` is `absent`,
   * so the day can still name the holiday it lost pay on. '' on a backend that
   * predates the field.
   */
  holidayTitle: string;
  /** 'regular' | 'special' | '' */
  holidayType: string;
  holidayRule: HolidayRule;
  /** See {@link HolidayPayVerdict}; '' on a backend that predates the field. */
  holidayPay: HolidayPayVerdict;
}

/** The server's `holiday_pay` spelling, or '' for anything unknown — never a guess. */
function holidayPayOf(rec: Json): HolidayPayVerdict {
  const v = str(rec, ['holiday_pay'], '').toLowerCase().trim();
  return v === 'not_evaluated' || v === 'paid' || v === 'forfeited' || v === 'worked' ? v : '';
}

/** The holiday's `holiday_rule` spelling, or '' for anything unknown. */
function holidayRuleOf(rec: Json): HolidayRule {
  const v = str(rec, ['holiday_rule'], '').toLowerCase().trim();
  return v === 'required_to_work' || v === 'no_business_operation' || v === 'paid_no_operation' ? v : '';
}

/** A finite number off a record, or 0. */
function finiteNumber(rec: Json, keys: string[]): number {
  const n = Number(pick<unknown>(rec, keys, 0));
  return Number.isFinite(n) ? n : 0;
}

/**
 * Index a `/me/attendance/summary` body's `days` by date. Empty for a null
 * summary (no such route, or a cut-off that has not started).
 */
function summaryDaysByDate(summary: unknown): Map<string, SummaryDay> {
  const out = new Map<string, SummaryDay>();
  const days = pick<unknown>(asRecord(summary), ['days'], []);
  if (!Array.isArray(days)) return out;
  for (const raw of days) {
    const r = asRecord(raw);
    const date = str(r, ['date'], '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) continue;
    const status = str(r, ['status'], '');
    const absent = Math.max(0, finiteNumber(r, ['absent_days']));
    // `metrics_source` names the provenance outright; a backend that predates it
    // only flags an approved (stored) day, and can say nothing about a pending one.
    const declared = str(r, ['metrics_source'], '').toLowerCase().trim();
    const provenanceDeclared = declared === 'stored' || declared === 'computed' || declared === 'none';
    const metricsSource: MetricsSource =
      declared === 'stored' || declared === 'computed'
        ? declared
        : declared === 'none'
          ? 'none'
          : bool(r, ['metrics_approved'], false)
            ? 'stored'
            : 'none';
    out.set(date, {
      status,
      scheduled: bool(r, ['scheduled'], false),
      attended: bool(r, ['attended'], false),
      // An `absent` row missing its count is still a whole day.
      absentDays: absent > 0 ? absent : status === 'absent' ? 1 : 0,
      tardyMinutes: Math.max(0, Math.round(finiteNumber(r, ['tardy_minutes']))),
      undertimeMinutes:
        'undertime_minutes' in r ? Math.max(0, Math.round(finiteNumber(r, ['undertime_minutes']))) : null,
      overbreakMinutes:
        'overbreak_minutes' in r ? Math.max(0, Math.round(finiteNumber(r, ['overbreak_minutes']))) : null,
      metricsSource,
      provenanceDeclared,
      metricsApproved: metricsSource === 'stored',
      holidayTitle: str(r, ['holiday_title'], '').trim(),
      holidayType: str(r, ['holiday_type'], '').toLowerCase().trim(),
      holidayRule: holidayRuleOf(r),
      holidayPay: holidayPayOf(r),
    });
  }
  return out;
}

/**
 * Re-read one schedule-built day through ERPat's verdict on it.
 *
 * The weekly schedule can't see the Shift Planner, paid leave or the server's
 * holiday rule, so wherever the summary has a row it decides whether the day
 * was a workday and how it resolved. What only the punches know — that a day is
 * `pending` approval, or has no clock-out (`incomplete`) — is kept while the
 * server still counts the day as worked.
 *
 * HOLIDAYS. The row's own `holiday_title` / `holiday_pay` win, so a regular
 * holiday ERPat FORFEITED — `status: absent`, `absent_days: 1`, and the
 * holiday's identity beside them — reads Absent (the absence precedence below
 * is unchanged) while KEEPING its title, which is how a surface can name the
 * holiday and say there was no holiday pay. A row without the fields falls back
 * to the device's holiday map, then to the bare word for a `holiday` row, and
 * reads `not_evaluated` — today's behaviour, byte for byte.
 */
function applySummaryDay(day: WeekDayVM, row: SummaryDay, holidays: ReadonlyMap<string, string>): WeekDayVM {
  const punched = day.status === 'present' || day.status === 'pending' || day.status === 'incomplete';
  // A holiday row is filed before the server sets `scheduled`, but it was a
  // shift day — the same reading `buildScheduleDays` gives a holiday. (A
  // forfeited holiday arrives `scheduled: true` — ERPat counts it a workday.)
  const isWorkday = row.scheduled || row.status === 'holiday';

  let status: WeekDayStatus;
  if (row.absentDays >= 1) status = 'absent';
  else if (row.status === 'on_leave') status = 'leave';
  else if (punched) status = day.status;
  else if (row.attended) status = 'present';
  else if (row.status === 'holiday') status = 'holiday';
  else if (!isWorkday) status = 'rest';
  // Today, before the shift can be judged (or with only its morning half gone).
  else if (day.isToday) status = 'today';
  else status = 'absent';

  return {
    ...day,
    isWorkday,
    status,
    statusLabel: WEEK_DAY_STATUS_LABELS[status],
    // The server names the holiday outright when it can (a forfeited absent row
    // included); a backend that predates the field is read as before — the
    // device's map, or the bare word, for a `holiday` row and '' otherwise. The
    // server also names it on a REST day the holiday covers; the title is a
    // WORKDAY's (see WeekDayVM), so a rest day keeps the '' `buildScheduleDays`
    // gives it and its sheet header still reads "No shift scheduled".
    holidayTitle: isWorkday ? row.holidayTitle || (row.status === 'holiday' ? holidays.get(day.key) || day.holidayTitle || 'Holiday' : '') : '',
    holidayPay: row.holidayPay || (row.status === 'holiday' ? 'not_evaluated' : ''),
    holidayRule: row.holidayRule,
  };
}

/**
 * The day sheet's figures for a cut-off day, reading the day's `tally` so the
 * sheet shows the same minutes as the report row that opened it: Late becomes
 * the counted lateness; Over Break becomes the counted overbreak; Undertime is
 * inserted after Over Break (before Night diff., when the day has one). Each of
 * the three carries the tally's provenance, is labelled "(est.)" ONLY when the
 * device estimated it, and an undertime estimate on a day with no clock-out
 * reads "—".
 */
function withCutoffFigures(
  detail: AttendanceDayDetailVM,
  tally: CutoffDayTally,
  sources: CutoffDaySources,
  flags: { undertimeUnmeasured: boolean },
): AttendanceDayDetailVM {
  const figures = detail.figures.map((figure) => {
    if (figure.key === 'late') {
      return {
        ...figure,
        label: sources.late === 'device' ? 'Late (est.)' : 'Late',
        value: compactDur(tally.tardyMinutes),
        minutes: tally.tardyMinutes,
        warn: tally.tardyMinutes > 0,
        source: sources.late,
      };
    }
    if (figure.key === 'overbreak') {
      return {
        ...figure,
        label: sources.overbreak === 'device' ? 'Over Break (est.)' : 'Over Break',
        value: compactDur(tally.overbreakMinutes),
        minutes: tally.overbreakMinutes,
        warn: tally.overbreakMinutes > 0,
        source: sources.overbreak,
      };
    }
    return figure;
  });
  const undertime: DayFigureVM = {
    key: 'undertime',
    label: sources.undertime === 'device' ? 'Undertime (est.)' : 'Undertime',
    value: flags.undertimeUnmeasured ? '—' : compactDur(tally.undertimeMinutes),
    minutes: tally.undertimeMinutes,
    warn: tally.undertimeMinutes > 0,
    source: sources.undertime,
    hint: '',
  };
  // After Over Break, before Night diff. — the reading order the sheet keeps.
  const nightDiffAt = figures.findIndex((figure) => figure.key === 'nightdiff');
  if (nightDiffAt === -1) figures.push(undertime);
  else figures.splice(nightDiffAt, 0, undertime);
  return { ...detail, figures };
}

/** One of a cut-off day's three overlaid figures, with where it came from. */
interface SourcedMinutes {
  minutes: number;
  source: FigureSource;
}

/**
 * Measure one payroll cut-off: its day-by-day schedule overlaid with the
 * caller's attendance, plus the three tallies the card summarises.
 *
 * `summary` is the `/me/attendance/summary` body for the cut-off through today.
 * Wherever it has a day, ERPat decides that day's status and absence
 * ({@link applySummaryDay}). Each of the day's LATE / UNDERTIME / OVERBREAK
 * figures then takes the best source available, in this order:
 *   (a) the summary row, when it says ERPat measured the day
 *       (`metrics_source` stored or computed) — the server already summed the
 *       day's records and applied its half-day rule to the tardiness;
 *   (b) else the punches' own `metrics`, rolled up on the device
 *       (`rollupDayMetrics`) — the same figures, when the summary predates them;
 *   (c) else the summary row's own measured tardiness (`summary`), with the
 *       device estimating undertime / overbreak from the punches;
 *   (d) else the device alone (no summary at all).
 * Absence is untouched by this: ERPat's `absent_days` wherever the summary has
 * the day, else the device's half-day rule.
 *
 * The BOUNDARIES are handed in, never derived here — `cutoff.store` owns the
 * ladder (the tenant's published calendar, or the device-derived fallback) so
 * there is only one place that can disagree with payroll about where a period
 * starts. This mapper only measures what it is given — and only the rows FILED
 * inside the period (`rowDayKey` within [startKey, endKey]): the caller reads
 * one day either side, as the server's summary does, so an overnight row whose
 * punch-in fell the day before the cut-off but whose `log_date` is inside it
 * is not lost, and a row punched in on the last day but filed under the next
 * belongs to the next cut-off.
 *
 * `leaves` ({@link mapLeaveDays}) marks the days the caller's approved paid
 * leave covers. For a day the summary has judged its verdict still wins — it
 * saw the punches and the paid/unpaid split — so the map only ever decides a
 * day the summary does not cover: one still ahead, or every day on a backend
 * without the route. It also carries the leave's title for the day row.
 * `leaveApps` ({@link mapLeaveApplicationsByDay}, from the same rows) attaches
 * the caller's pending / endorsed / approved applications to every day they
 * reach — informative only, it decides no status.
 *
 * A regular holiday ERPat FORFEITED (see {@link HolidayPayVerdict}) is what the
 * server says it is: a scheduled shift. Unattended it reads Absent with the
 * server's `absent_days` of 1; punched it reads as the worked day it was
 * (`holiday_pay: 'worked'`, status present / late / half_day). Either way its
 * shift stands — a punch on it is judged against the clock window — and it
 * counts in `workingDays`. The server's `scheduled` flag says which holidays
 * those are (true only on the forfeited path; a paid or unjudged holiday
 * returns before it is set), so wherever the summary has the day that flag, not
 * the holiday title, decides whether the day is measured and counted as a
 * workday.
 */
export function mapCutoffPeriod(
  schedData: unknown,
  attendanceData: unknown,
  period: { key: string; startKey: string; endKey: string },
  holidays: ReadonlyMap<string, string> = new Map(),
  summary: unknown = null,
  leaves: ReadonlyMap<string, string> = new Map(),
  leaveApps: ReadonlyMap<string, DayLeaveVM[]> = new Map(),
): CutoffPeriodVM {
  const sched = domainBody(schedData, ['schedule']);
  const dates = datesInRange(period.startKey, period.endKey);
  const todayKey = localDateKey(new Date());
  const serverDays = summaryDaysByDate(summary);
  const attendanceRows = toList(attendanceData).filter((row) => {
    const key = rowDayKey(asRecord(row));
    return key >= period.startKey && key <= period.endKey;
  });

  // Measured from the SAME rows the statuses come from, so a day's sheet can
  // never disagree with the row above it. `groupSessionsByDay` reads the punch
  // stamps, `break_time` taps and each row's `metrics`, filing every punch under
  // ERPat's log date (`rowDayKey` — the key `punchDaysOf` uses too);
  // `buildDayDetail` prefers those figures and measures on the device only what
  // the server has not — see `attendance-day.ts`.
  const sessionsByDay = groupSessionsByDay(attendanceRows);

  const days: CutoffDayVM[] = buildScheduleDays(sched, attendanceRows, dates, holidays, leaves, leaveApps).map((scheduled, i) => {
    // ERPat's verdict wherever it judged the day; the schedule's own reading only
    // for days the summary does not cover (ahead of today, or no summary at all).
    const row = serverDays.get(scheduled.key);
    const day = row ? applySummaryDay(scheduled, row, holidays) : scheduled;
    const punched = sessionsByDay.get(day.key) ?? [];
    // The auto-clock-out job closes a forgotten punch with out_time = in_time — a
    // placeholder, not a departure. On a day already read as incomplete it is
    // measured as the missing clock-out it stands for, so the sheet shows no
    // zero-length shift and the tally judges no half-day departure off it (the
    // Reports module flags that day's undertime as not measurable instead). Its
    // `metrics` go too: the engine can run on the placeholder (it has an
    // out_time), but `/me/attendance/summary` deliberately skips `clockout` rows
    // and keeps its benefit of the doubt, so a rollup off that zeroed shift would
    // contradict the server's own figure for the day.
    const sessions =
      day.status === 'incomplete'
        ? punched.map((s) =>
            s.outAbsolute === s.inMinutes
              ? { ...s, outMinutes: null, outAbsolute: null, metrics: null, metricsSource: 'none' as const }
              : s,
          )
        : punched;
    const shift = {
      // Measured as a shift exactly when payroll expects one: the server's
      // `scheduled` wherever it judged the day — false on a paid or unjudged
      // holiday (so a worked one carries no lateness, undertime or absence),
      // true on a regular holiday ERPat forfeited, punched or not — else the
      // schedule's workday less the device's holiday map.
      isWorkday: row ? row.scheduled : day.isWorkday && !day.holidayTitle,
      // The row already carries the schedule's clock strings; re-reading the
      // slot here would be a second path to the same two values.
      inMinutes: clockToMinutes(day.inLabel),
      outMinutes: clockToMinutes(day.outLabel),
    };
    // Measured from the same sessions and shift as `detail`, so a day's report
    // row and its sheet can never describe different punches. `dayIsOver` is
    // the same past-day test the day statuses above use.
    const measured = tallyWorkday(sessions, shift, day.key < todayKey);
    const baseDetail = buildDayDetail(sessions, shift, day.key === todayKey);
    const figureOf = (key: DayFigureVM['key']): DayFigureVM | undefined =>
      baseDetail.figures.find((figure) => figure.key === key);

    // Neither undertime nor overbreak is estimated on a day ERPat counts as
    // wholly missed or on leave.
    const excused = row != null && (row.absentDays >= 1 || day.status === 'leave');
    const rollup = rollupDayMetrics(sessions);
    // (a) the summary row carries ERPat's own figures for the day.
    const serverRow = row != null && row.metricsSource !== 'none' ? row : null;
    const serverSource: FigureSource = serverRow?.metricsSource === 'stored' ? 'stored' : 'computed';

    // A row that only flagged `metrics_approved` (no `metrics_source`) is a
    // backend whose tardiness came from its own calculator, not the engine's
    // `late`: server-authoritative, pre-provenance — `summary`, never `stored`.
    const late: SourcedMinutes = serverRow
      ? { minutes: serverRow.tardyMinutes, source: serverRow.provenanceDeclared ? serverSource : 'summary' }
      : rollup
        ? { minutes: rollup.lateMinutes, source: rollup.source }
        : row
          ? { minutes: row.tardyMinutes, source: 'summary' }
          : { minutes: measured.tardyMinutes, source: 'device' };

    const undertime: SourcedMinutes =
      serverRow && serverRow.undertimeMinutes != null
        ? { minutes: serverRow.undertimeMinutes, source: serverSource }
        : rollup
          ? { minutes: rollup.undertimeMinutes, source: rollup.source }
          : { minutes: excused ? 0 : measured.undertimeMinutes, source: 'device' };

    const overbreak: SourcedMinutes =
      serverRow && serverRow.overbreakMinutes != null
        ? { minutes: serverRow.overbreakMinutes, source: serverSource }
        : rollup
          ? { minutes: rollup.overbreakMinutes, source: rollup.source }
          : { minutes: excused ? 0 : (figureOf('overbreak')?.minutes ?? 0), source: 'device' };

    const tally: CutoffDayTally = {
      // Absence exactly as before: ERPat's count wherever the summary has the
      // day; else the device's — `tallyWorkday` measures a PUNCHED day, so a
      // whole missed day is added here, leaving one field a reader totals.
      absentDays: row ? row.absentDays : measured.absentDays + (day.status === 'absent' ? 1 : 0),
      tardyMinutes: late.minutes,
      undertimeMinutes: undertime.minutes,
      overbreakMinutes: overbreak.minutes,
    };
    const sources: CutoffDaySources = {
      late: late.source,
      undertime: undertime.source,
      overbreak: overbreak.source,
      worked: figureOf('worked')?.source ?? 'device',
      overtime: figureOf('overtime')?.source ?? 'device',
    };
    return {
      ...day,
      // Read off the same lateness the day sheet's Late figure and the Reports
      // module count, so the tag and those figures always agree.
      late: tally.tardyMinutes > 0,
      fullDateLabel:
        dates[i]?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) ?? '',
      detail: withCutoffFigures(baseDetail, tally, sources, {
        // ERPat's own figure stands even where the punches can't be measured.
        undertimeUnmeasured: day.status === 'incomplete' && undertime.source === 'device',
      }),
      tally,
      fromServer: row != null,
      sources,
      tardyEstimated: late.source === 'device',
      undertimeEstimated: undertime.source === 'device',
      overbreakEstimated: overbreak.source === 'device',
    };
  });

  let state: CutoffState;
  if (todayKey < period.startKey) state = 'upcoming';
  else if (todayKey > period.endKey) state = 'completed';
  else state = 'in-progress';

  return {
    key: period.key,
    state,
    stateLabel: CUTOFF_STATE_LABELS[state],
    // The same test the shift above uses: ERPat's `scheduled` flag wherever the
    // summary judged the day (a forfeited holiday, worked or not, is a workday;
    // a paid or unjudged one is not), else the schedule less the holiday map.
    workingDays: days.filter((d) => {
      const row = serverDays.get(d.key);
      return row ? row.scheduled : d.isWorkday && !d.holidayTitle;
    }).length,
    daysPresent: days.filter((d) => {
      const row = serverDays.get(d.key);
      if (row) return row.attended;
      // An incomplete day was still punched — only its clock-out is missing.
      return d.status === 'present' || d.status === 'pending' || d.status === 'incomplete';
    }).length,
    daysAbsent: days.reduce((sum, d) => sum + d.tally.absentDays, 0),
    source: summary != null ? 'server' : 'device',
    days,
  };
}

// ── Team attendance (manager listing + approval) ─────────────────────────────
/**
 * The approval-workflow status of an attendance row, as the manager-level
 * `/v1/api/attendance` surface returns it. NOTE: this is the *record* status
 * (an HR approval lifecycle), NOT the punctuality of the punch — the backend
 * vocabulary is `incomplete | pending | approved | rejected | clockout`, plus
 * an empty string we normalise to `unset`.
 */
export type TeamAttendanceStatus =
  | 'incomplete'
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'clockout'
  | 'unset';

/** The three statuses the PATCH `/attendance/{id}/status` endpoint accepts. */
export type TeamAttendanceAction = 'approved' | 'rejected' | 'pending';

// `AttendanceOtStatus`, `AttendanceOtReviewState`, `AttendanceOtBuckets`,
// `AttendanceMetricsVM`, `attendanceHoursLabel` and `mapAttendanceMetrics` moved
// verbatim to `attendance-metrics.ts` (re-exported at the top of this file).

// ── Approval gate (detail route only) ────────────────────────────────────────
/** Which lunch treatment an approval stores. Server vocabulary, sent verbatim. */
export type AttendanceLunchChoice = 'deduct_lunch_break' | 'skip_lunch_break';

/**
 * The approver's call on the measured overtime, as the API takes it:
 *   'approved' authorises the hours;
 *   'rejected' is an ATTRIBUTED rejection recorded against this approver;
 *   'policy'   the approver chose to let the tenant's standing policy decide —
 *              a real answer, and the one a batch sends so the server does not
 *              refuse it for "never answering".
 */
export type AttendanceOtDecision = 'approved' | 'rejected' | 'policy';

/** Why the server refuses this caller this record ('' when it didn't say). */
export type AttendanceBlockReason =
  | 'no_permission'
  /**
   * The caller's OWN record, and their role can't manage their own attendance
   * logs (`attendance_self_manage`; administrators are exempt) — HumanResource
   * 1.60.0. The web never offers a self decision without that permit either.
   */
  | 'self_not_allowed'
  | 'needs_correction'
  | 'already_decided'
  | 'not_clocked_out'
  | 'attendance_locked'
  | '';

const ATTENDANCE_BLOCK_LABELS: Record<Exclude<AttendanceBlockReason, ''>, string> = {
  no_permission: 'View only — you don’t have attendance-approval permission.',
  self_not_allowed: 'This is your own attendance, so you can’t decide it. Ask your supervisor or HR to approve it.',
  needs_correction: 'No clock-out was recorded. Correct the time logs before this can be approved.',
  already_decided: 'This attendance has already been decided.',
  not_clocked_out: 'Still clocked in — approval needs a completed shift.',
  attendance_locked: 'This payroll period is locked, so the attendance can’t be changed.',
};

/**
 * The footer's reason text. A lock is worded for the lock that applies — the
 * payroll period (Log Controls), or the company's "Days to Lock Attendance"
 * window — from `approval.lock`; an older backend that names no source reads as
 * the payroll period, the only lock it knew.
 */
export function attendanceBlockLabel(
  reason: AttendanceBlockReason,
  lock: AttendanceLockVM = OPEN_ATTENDANCE_LOCK,
): string {
  if (reason === 'attendance_locked' && lock.source === 'rolling_window') {
    return `${attendanceLockLead(lock)}, so it can’t be changed.`;
  }
  return reason ? ATTENDANCE_BLOCK_LABELS[reason] : '';
}

/**
 * What the detail sheet's footer shows — the ONE place that decides whether
 * Approve / Reject / Mark pending may appear:
 *
 *   checking   the record's own verdict (GET /attendance/{id}) is still loading;
 *   failed     it could not be loaded — no verdict, so no buttons;
 *   correction a placeholder whose time logs must be corrected first;
 *   actions    the server said this caller may decide it;
 *   blocked    the server said why not (a lock, a decision already made, …).
 *
 * The buttons NEVER render from a list row: the list carries no verdict, so
 * until the detail arrives the sheet would otherwise offer decisions on a record
 * the server is about to call locked. A detail that loads without an `approval`
 * block at all (an older backend) still fails OPEN — `approval.allowed` stays
 * true and the server refuses what it must.
 */
export type AttendanceSheetFooter = 'checking' | 'failed' | 'correction' | 'actions' | 'blocked';

export function attendanceSheetFooter(input: {
  loading: boolean;
  failed: boolean;
  needsCorrection: boolean;
  approval: AttendanceApprovalVM;
}): AttendanceSheetFooter {
  if (input.loading) return 'checking';
  if (input.failed) return 'failed';
  if (input.needsCorrection) return 'correction';
  return input.approval.allowed ? 'actions' : 'blocked';
}

/**
 * The read-only overtime notice for a PENDING record whose overtime this viewer
 * cannot decide — what an approval will do to the hours instead, as the web's
 * two read-only notices say. '' when there is nothing to say:
 *
 *  - a locked record can't be approved at all, so no policy "applies" — the
 *    footer names the lock instead;
 *  - the viewer's own record, which they may not decide (`self_not_allowed`):
 *    they decide nothing here and the footer says why. The server narrows
 *    `can_decide` for them, which would otherwise be worded below as a lock.
 */
export function attendanceOtNotice(approval: AttendanceApprovalVM, status: TeamAttendanceStatus): string {
  const ot = approval.overtime;
  if (approval.lock.locked) return '';
  if (approval.reason === 'self_not_allowed') return '';
  if (!ot.approvalEnabled || status !== 'pending' || ot.canDecide) return '';
  if (!ot.hasOvertime && !ot.hasOvertimeSkipLunch) return '';
  const policy = ot.autoPolicyLabel || 'your company’s standing overtime policy';
  return ot.hasPermission
    ? `Overtime was detected, but this date is locked, so it can’t be decided here. ${policy} applies.`
    : `Overtime was detected. You can’t decide it here, so “${policy}” applies when this attendance is approved.`;
}

/**
 * The overtime half of the server's verdict — the fields the sheet re-evaluates
 * when the lunch choice changes, exactly as the web's `syncOtDecisionGate()` does.
 */
export interface AttendanceApprovalOvertimeVM {
  /** The tenant's `overtime_approval` setting. */
  approvalEnabled: boolean;
  /** Caller holds `attendance_ot_approval` at all. */
  hasPermission: boolean;
  /**
   * …AND the record is not locked (neither lock — the server clamps this to
   * false on a locked record) — may actually rule on the hours here.
   */
  canDecide: boolean;
  /** Overtime was MEASURED with the opening (deduct-lunch) figures. */
  hasOvertime: boolean;
  /** Overtime the approval would store under "Skip Lunch Break". */
  hasOvertimeSkipLunch: boolean;
  /** The server's own answer FOR THE OPENING lunch choice (first paint only). */
  decisionRequired: boolean;
  /** Decimal hours, opening figures. */
  measuredTotal: number;
  /** Decimal hours under Skip Lunch Break; null when not measured. */
  measuredTotalSkipLunch: number | null;
  /** The standing Overtime-On-Approval policy: 'approved' | 'pending' | 'rejected' | ''. */
  autoPolicy: string;
  /**
   * The same policy, already worded by the server (e.g. "Submit to the Overtime
   * approval tab"). Named in the read-only notice so an approver who cannot decide
   * still knows what their click does to the hours. '' when the server sent none.
   */
  autoPolicyLabel: string;
}

/**
 * The server's per-record verdict on what THIS caller may do, and what it will
 * insist on first — the API counterpart of the web approval window's
 * `_ot_decision_gate()` plus `can_bypass_lunch`.
 *
 * Rides the DETAIL route only. A list row carries none of this, which is
 * deliberate: computing the gate needs the engine, and the list must not pay for
 * it a hundred times per page.
 *
 * Absent keys fail OPEN, exactly as `readLeaveApproval` does — an older deployment
 * must not silently hide the decision buttons from every real approver. The server
 * refuses what it must; the app reports the refusal. That is about a detail that
 * LOADED without the block: a list row, a detail still loading, or one that failed
 * to load never shows the buttons (`attendanceSheetFooter`).
 */
export interface AttendanceApprovalVM {
  /** May this caller set the attendance status at all. */
  allowed: boolean;
  reason: AttendanceBlockReason;
  /**
   * Which attendance lock, if either, shuts the record — reported whatever
   * `reason` is (a viewer without the approval permit still learns it is locked).
   */
  lock: AttendanceLockVM;
  /** The record belongs to the caller. */
  selfRecord: boolean;
  /** Caller may choose Deduct / Skip Lunch Break (`attendance_update`). */
  canChooseLunch: boolean;
  overtime: AttendanceApprovalOvertimeVM;
  /**
   * True when `metrics` is a LIVE recomputation rather than the stored payroll
   * row — the figures are what approving WOULD store, not what has been stored.
   */
  metricsPreview: boolean;
}

const OPEN_APPROVAL_OVERTIME: AttendanceApprovalOvertimeVM = {
  approvalEnabled: false,
  hasPermission: false,
  canDecide: false,
  hasOvertime: false,
  hasOvertimeSkipLunch: false,
  decisionRequired: false,
  measuredTotal: 0,
  measuredTotalSkipLunch: null,
  autoPolicy: '',
  autoPolicyLabel: '',
};

const OPEN_APPROVAL: AttendanceApprovalVM = {
  allowed: true,
  reason: '',
  lock: OPEN_ATTENDANCE_LOCK,
  selfRecord: false,
  canChooseLunch: false,
  overtime: OPEN_APPROVAL_OVERTIME,
  metricsPreview: false,
};

function readAttendanceBlockReason(value: unknown): AttendanceBlockReason {
  const s = String(value ?? '').toLowerCase().trim();
  return s === 'no_permission' || s === 'self_not_allowed' || s === 'needs_correction' || s === 'already_decided'
    || s === 'not_clocked_out' || s === 'attendance_locked'
    ? s
    : '';
}

function readAttendanceApproval(r: Json, status: TeamAttendanceStatus): AttendanceApprovalVM {
  // `metrics_source` is on the row itself (list and detail). Only an approved
  // record has a stored metrics row, so anything else we are shown was computed
  // for this request — the fallback when the server did not say.
  const source = str(r, ['metrics_source'], '').toLowerCase().trim();
  const metricsPreview = source ? source === 'computed' : status !== 'approved';

  const raw = pick<unknown>(r, ['approval', 'gate'], null);
  if (raw == null) return { ...OPEN_APPROVAL, metricsPreview };
  if (typeof raw === 'boolean') return { ...OPEN_APPROVAL, allowed: raw, metricsPreview };

  const g = asRecord(raw);
  const ot = asRecord(pick(g, ['overtime'], {}));
  const skipTotal = pick<unknown>(ot, ['measured_total_skip_lunch'], null);
  const reason = readAttendanceBlockReason(pick(g, ['reason', 'block_reason'], ''));

  return {
    allowed: bool(g, ['allowed', 'can_decide', 'can_approve'], true),
    reason,
    // An older backend names no lock object; its `attendance_locked` was Log Controls.
    lock: readAttendanceLock(pick<unknown>(g, ['lock'], null), reason === 'attendance_locked'),
    selfRecord: bool(g, ['self_record']),
    canChooseLunch: bool(g, ['can_choose_lunch', 'can_bypass_lunch']),
    overtime: {
      approvalEnabled: bool(ot, ['approval_enabled', 'overtime_approval']),
      hasPermission: bool(ot, ['has_permission', 'has_attendance_ot_permission']),
      canDecide: bool(ot, ['can_decide', 'has_attendance_ot_approval']),
      hasOvertime: bool(ot, ['has_overtime']),
      hasOvertimeSkipLunch: bool(ot, ['has_overtime_skip_lunch']),
      decisionRequired: bool(ot, ['decision_required', 'ot_decision_required']),
      measuredTotal: otHours(ot, ['measured_total']),
      measuredTotalSkipLunch: skipTotal == null ? null : otHours(ot, ['measured_total_skip_lunch']),
      autoPolicy: str(ot, ['auto_policy', 'overtime_auto_policy'], '').toLowerCase().trim(),
      autoPolicyLabel: str(ot, ['auto_policy_label', 'overtime_auto_policy_label'], ''),
    },
    metricsPreview,
  };
}

export interface TeamAttendanceVM {
  id: string;
  userId: string;
  employeeName: string;
  /** Absolute avatar URL, or '' — render with the initials fallback. */
  employeeImage: string;
  employeeInitials: string;
  jobTitle: string;
  departmentName: string;
  scheduleName: string;
  status: TeamAttendanceStatus;
  statusLabel: string;
  /**
   * A placeholder whose time logs must be corrected before it can be approved —
   * a `clockout` row ERPat's auto clock-out job closed at its own clock-in
   * (out = in), or the same zero-span shift merely marked pending. Its figures
   * measure nothing. The server's own flag, falling back to `status === 'clockout'`.
   */
  needsCorrection: boolean;
  /** Local clock-in time label, e.g. "9:00 AM" ('' when not clocked in). */
  clockInLabel: string;
  /**
   * Local clock-out time label ('' while the shift is still open, and '' for a
   * record that needs correction — its stored clock-out is the clock-in again).
   */
  clockOutLabel: string;
  /** True once the shift has clocked out — approval is only allowed then. */
  isClockedOut: boolean;
  /** The raw company-local times ("YYYY-MM-DD HH:MM:SS", '' when absent) — the edit form's source. */
  inTimeLocal: string;
  outTimeLocal: string;
  /** attendance.log_type: 'schedule' | 'overtime' | 'workhour'. */
  logType: string;
  /** The assigned schedule template id ('' when none). */
  schedId: string;
  /** Stored breaks as positional pairs — detail route only ([] on a list row). */
  breaks: AttendanceBreakPairVM[];
  /** Whether THIS caller may correct the time logs — detail route only; fails CLOSED. */
  edit: AttendanceEditVM;
  /** Worked-hours label from the metrics row, e.g. "8h 00m" ('' when none). */
  workedLabel: string;
  /** Late label from metrics, e.g. "12m late" ('' when on time / no metrics). */
  lateLabel: string;
  /**
   * The full computed payroll metrics — the stored row for an approved record, or
   * (detail route only) a live engine preview for a pending one. Null on a pending
   * LIST row, which never computes, and when the engine could not run.
   * `workedLabel`/`lateLabel` above stay as the two-figure summary for a list row.
   */
  metrics: AttendanceMetricsVM | null;
  /**
   * The server's decision gate for this record — populated by the detail route
   * only; a list row carries the fail-open default.
   */
  approval: AttendanceApprovalVM;
  note: string;
  /** Local calendar date (YYYY-MM-DD) of the punch — used to group by day. */
  dateKey: string;
  /** Human day label for the group header, e.g. "May 23, 2025". */
  dateLabel: string;
  /** Epoch ms for sorting (local-date based; 0 when unknown). */
  dateMillis: number;
  /** Approver's display name ('' when not yet checked). */
  checkedByName: string;
  /** Local "checked at" label ('' when not yet checked). */
  checkedAtLabel: string;
}

const TEAM_ATTENDANCE_STATUS_LABELS: Record<TeamAttendanceStatus, string> = {
  incomplete: 'Incomplete',
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  clockout: 'Clocked Out',
  unset: 'No Status',
};

function normalizeTeamAttendanceStatus(value: unknown): TeamAttendanceStatus {
  const s = String(value ?? '').toLowerCase().trim();
  if (s === 'incomplete' || s === 'pending' || s === 'approved' || s === 'rejected' || s === 'clockout') {
    return s;
  }
  return 'unset';
}

// ── Employment stage ─────────────────────────────────────────────────────────
/**
 * HR-controlled employment classification (`users_meta.employment_stage`).
 *
 * These five tokens are ERPat's own closed set — the same list the web app's
 * employment dropdowns offer. `''` means HR has not set a stage, which is a real
 * and common state, NOT an error: surfaces must render nothing for it rather
 * than guessing or defaulting to 'regular'.
 */
export type EmploymentStage = 'regular' | 'probationary' | 'fixed_term' | 'project_based' | 'seasonal' | '';

/**
 * Raw API token → the typed stage. Anything unrecognised (a legacy or
 * tenant-custom value) collapses to '' so callers render nothing rather than a
 * broken icon — the same fail-quiet posture as {@link normalizeTeamAttendanceStatus}.
 *
 * No label map lives here on purpose: `titleCase` already turns every one of
 * these tokens into its exact backend label ('fixed_term' → "Fixed Term"), so
 * `ProfileVM.employmentStage` is the display string and this is the lookup key.
 */
export function normalizeEmploymentStage(value: unknown): EmploymentStage {
  const s = String(value ?? '').toLowerCase().trim();
  if (s === 'regular' || s === 'probationary' || s === 'fixed_term' || s === 'project_based' || s === 'seasonal') {
    return s;
  }
  return '';
}

/** Parse the date portion of a tenant-local "Y-m-d H:i:s" string as a LOCAL Date. */
function localDateMillis(localDateTime: string): number {
  const m = localDateTime.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return 0;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getTime();
}

/**
 * Map one shaped row from the manager-level `/v1/api/attendance` surface
 * (`TeamAttendance::shape($row, true)`) into a view model. The row carries the
 * employee it belongs to, both UTC + tenant-local times, the approver
 * ("checker"), and — once approved — the computed `metrics` (decimal-hour
 * figures). Keys are read defensively, as elsewhere.
 */
export function mapTeamAttendanceRecord(value: unknown, idx = 0): TeamAttendanceVM {
  // Accept both a raw row (from the list's `toList`) and a single-resource
  // payload wrapped under the `attendance` domain key (get / updateStatus) —
  // `domainBody` returns the inner object when wrapped, else the record itself.
  const r = domainBody(value, ['attendance']);
  const emp = asRecord(pick(r, ['employee', 'user', 'applicant'], {}));
  const metrics = asRecord(pick(r, ['metrics'], {}));
  const checker = asRecord(pick(r, ['checker'], {}));

  const name = str(emp, ['name', 'full_name'], '') || str(r, ['applicant_name', 'created_by_user'], '');
  const inLocal = str(r, ['in_time_local'], '');
  const outLocal = str(r, ['out_time_local'], '');
  const dateKey = (inLocal || str(r, ['date_created'], '')).slice(0, 10);

  const workedHours = pick<number>(metrics, ['worked'], 0);
  const lateHours = pick<number>(metrics, ['late'], 0);
  const checkedAtLocal = str(r, ['checked_at_local'], '');
  const departmentName =
    str(emp, ['department_name', 'department', 'team_name', 'team', 'unit_name', 'unit'], '') ||
    str(r, ['department_name', 'department', 'team_name', 'team', 'unit_name', 'unit'], '');
  const status = normalizeTeamAttendanceStatus(pick(r, ['status'], ''));
  // The server decides (`attendance_needs_correction()`); an older backend that
  // sends no flag still gets the status reading.
  const needsCorrection = bool(r, ['needs_correction'], status === 'clockout');
  const schedRaw = pick<unknown>(r, ['sched_id'], null);

  return {
    id: idOf(r, idx),
    userId: str(emp, ['id'], '') || str(r, ['user_id'], ''),
    employeeName: name,
    // Read every spelling the other people-mappers accept, and fall back to the
    // row itself — the photo may ride the record rather than the nested
    // `employee` object (the API doesn't document this field either way).
    employeeImage:
      profileImageUrl(pick<unknown>(emp, EMPLOYEE_IMAGE_KEYS, null)) ||
      profileImageUrl(pick<unknown>(r, EMPLOYEE_IMAGE_KEYS, null)),
    employeeInitials: initialsOf(name) || '?',
    jobTitle: str(emp, ['job_title', 'title', 'position'], '') || str(r, ['schedule_name'], ''),
    departmentName,
    scheduleName: str(r, ['schedule_name'], ''),
    status,
    statusLabel: TEAM_ATTENDANCE_STATUS_LABELS[status],
    needsCorrection,
    clockInLabel: inLocal ? formatTime(inLocal.replace(' ', 'T')) : '',
    clockOutLabel: outLocal && !needsCorrection ? formatTime(outLocal.replace(' ', 'T')) : '',
    isClockedOut: outLocal !== '',
    inTimeLocal: inLocal,
    outTimeLocal: outLocal,
    logType: str(r, ['log_type'], 'schedule') || 'schedule',
    schedId: schedRaw == null || schedRaw === '' ? '' : String(schedRaw),
    breaks: readAttendanceBreaks(r),
    edit: readAttendanceEdit(r),
    workedLabel: typeof workedHours === 'number' && workedHours > 0 ? formatDurationMinutes(workedHours * 60) : '',
    lateLabel: typeof lateHours === 'number' && lateHours > 0 ? `${formatDurationMinutes(lateHours * 60)} late` : '',
    // The list shapes every row as detailed, but computes NO metrics for a pending
    // one; the detail route does (one record, one engine run), which is why the
    // approval sheet fetches it on open.
    metrics: mapAttendanceMetrics(pick(r, ['metrics'], null)),
    approval: readAttendanceApproval(r, status),
    note: str(r, ['note'], ''),
    dateKey,
    dateLabel: dateKey ? formatDate(`${dateKey}T00:00:00`) : '',
    dateMillis: localDateMillis(inLocal) || localDateMillis(str(r, ['date_created'], '')),
    checkedByName: str(checker, ['name'], ''),
    checkedAtLabel: checkedAtLocal ? formatDate(checkedAtLocal.replace(' ', 'T')) : '',
  };
}

/** Map the `/v1/api/attendance` list payload (`{ attendance: [...] }`) to VMs. */
export function mapTeamAttendanceList(data: unknown): TeamAttendanceVM[] {
  return toList(data).map((row, i) => mapTeamAttendanceRecord(row, i));
}

/** What the team attendance list says about the CALLER, beside the page of rows. */
export interface TeamAttendanceListMeta {
  /**
   * The caller may decide their own attendance: their role can manage their own
   * attendance logs (`attendance_self_manage`), or they are an administrator.
   * Their own records are always in the list; without this the server refuses
   * to decide one (403 `self_approval_not_allowed`).
   */
  canSelfManage: boolean;
}

/**
 * Read the list's caller flags from its `meta`. ABSENT reads as `true`: a backend
 * older than HumanResource 1.60.0 sends no flag and never refused a self
 * decision, so the app keeps offering the rows and the server refuses what it
 * must. Only an explicit `false` withholds them.
 */
export function readTeamAttendanceListMeta(meta: unknown): TeamAttendanceListMeta {
  return { canSelfManage: asRecord(meta).can_self_manage !== false };
}

/**
 * Why a Team Attendance row can't join a bulk approve or reject — '' when it
 * can. The reason is appended to the row's aria-label in selection mode, so an
 * unselectable row is explained, not merely ignored.
 *
 *  - the caller's OWN record, when the list says they can't decide their own
 *    attendance — the web leaves these rows out of its bulk approve too. `meId`
 *    is '' until the profile has loaded, and an empty id never matches, so no
 *    row is withheld on a guess;
 *  - a system clock-out: no hours to approve — the server refuses it (409
 *    `attendance_needs_correction`) until someone corrects its time logs;
 *  - not clocked out — the server 409s on an open shift (the rule the
 *    single-record Approve button follows);
 *  - already approved: there is nothing to do;
 *  - already rejected — a rejection is a considered decision by a human, and
 *    approving a batch must never silently reverse one. Reverting a rejection
 *    stays a deliberate, per-record action through the action sheet.
 */
export function attendanceSelectBlockReason(
  rec: Pick<TeamAttendanceVM, 'userId' | 'needsCorrection' | 'isClockedOut' | 'status'>,
  ctx: TeamAttendanceListMeta & { meId: string },
): string {
  if (!ctx.canSelfManage && ctx.meId !== '' && rec.userId === ctx.meId) {
    return 'Your own attendance — you can’t approve or reject it';
  }
  if (rec.needsCorrection) return 'No clock-out recorded — correct its time logs first';
  if (!rec.isClockedOut) return 'Still clocked in, cannot be approved yet';
  if (rec.status === 'approved') return 'Already approved';
  if (rec.status === 'rejected') return 'Rejected — reopen it individually to change this';
  return '';
}

// ── Capabilities (UI gating) ─────────────────────────────────────────────────
export interface CapabilitiesVM {
  isAdmin: boolean;
  userType: string;
  /** Raw permission map (e.g. `{ attendance: "all", attendance_approval: "1" }`). */
  permissions: Record<string, string>;
  /** Per-module enablement flags (e.g. `{ attendance: true, leave: true }`). */
  modules: Record<string, boolean>;
  /**
   * Tenant has attendance on AND the caller holds the `attendance` permit.
   *
   * NOTE: the attendance/leave APPROVAL surfaces deliberately do NOT gate on
   * this or on `canApproveAttendance`. Those screens let the server — which
   * already holds the caller's ERPat role — answer with a 403/404, rather than
   * mirroring its RBAC in a second place that can drift. There is no
   * leave equivalent here for the same reason: a `leave_approval` permit was
   * never verified against the backend, so guessing one would be worse than
   * asking the endpoint. These two remain because they are verified and part of
   * the mapper's documented surface.
   */
  canViewTeamAttendance: boolean;
  /** Above, plus the `attendance_approval` permit (mutate capability). */
  canApproveAttendance: boolean;
  /** Caller holds the `syntry_clockinout_app` permit (admin override). */
  canClockInOut: boolean;
  /**
   * Caller may LIST events: `is_admin || event`, which is exactly the server's
   * `requirePermit('event')` on `GET /v1/api/me/events` (`Events_euapi::gate()`,
   * read from the same guard map). `modules.events` is deliberately NOT part of
   * it: that probe has answered false on fully enabled tenants before, and a
   * false "no" would silently switch events off for everyone. A module that is
   * really off answers 404, which `services/events-access.ts` remembers.
   */
  canListEvents: boolean;
}

/** A permit is "granted" when present and not an empty / falsey string. */
function permitGranted(perms: Json, key: string): boolean {
  const v = perms[key];
  if (v == null) return false;
  const s = String(v).toLowerCase().trim();
  return s !== '' && s !== '0' && s !== 'false' && s !== 'no';
}

export function mapCapabilities(data: unknown): CapabilitiesVM {
  // GET /me/capabilities → { is_admin, user_type, permissions{...}, modules{...} }.
  const rec = domainBody(data, ['capabilities']);
  const permsRec = asRecord(pick(rec, ['permissions'], {}));
  const modulesRec = asRecord(pick(rec, ['modules'], {}));
  const isAdmin = bool(rec, ['is_admin']);

  const permissions: Record<string, string> = {};
  for (const [k, v] of Object.entries(permsRec)) permissions[k] = String(v ?? '');
  const modules: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(modulesRec)) modules[k] = Boolean(v);

  const canViewTeamAttendance =
    (modules.attendance !== false) && (isAdmin || permitGranted(permsRec, 'attendance'));
  const canApproveAttendance =
    canViewTeamAttendance && (isAdmin || permitGranted(permsRec, 'attendance_approval'));
  // Time In/Out/Break gate — the Syntry app permit (admin override), matching the
  // backend's `requirePermit` semantics (admin || truthy permit).
  const canClockInOut = isAdmin || permitGranted(permsRec, 'syntry_clockinout_app');
  const canListEvents = isAdmin || permitGranted(permsRec, 'event');

  return {
    isAdmin,
    userType: str(rec, ['user_type'], ''),
    permissions,
    modules,
    canViewTeamAttendance,
    canApproveAttendance,
    canClockInOut,
    canListEvents,
  };
}

export interface LeaveBalanceVM {
  type: string;
  /** Numeric leave-type id — the apply API's preferred selector (`leave_type_id`). */
  typeId: string;
  label: string;
  daysLabel: string;
}
export function mapLeaveBalances(data: unknown): LeaveBalanceVM[] {
  return toList(data).map((b, i) => {
    const r = asRecord(b);
    return {
      type: str(r, ['type', 'leave_type', 'code'], String(i)),
      typeId: String(pick<unknown>(r, ['leave_type_id'], '')),
      label: str(r, ['label', 'name', 'type_label'], ''),
      daysLabel: String(pick<unknown>(r, ['days_available', 'available', 'balance', 'remaining'], 0)),
    };
  });
}

// ── Leave filing rules (`GET /v1/api/me/leaves/filing-rules`) ───────────────

/**
 * One leave type's filing window, as ERPat resolved it FOR THIS EMPLOYEE.
 *
 * Every date here is SERVER-COMPUTED (`LeavesTrait::leave_filing_policy_payload()`
 * — the same bounds the web modal pins its date picker to and the same guard
 * `POST /me/leaves` re-checks). The app compares a chosen date against these;
 * it never counts working days itself, because "working day" is the employee's
 * shift plan, default schedule, weekends setting and holidays — all of which
 * only ERPat holds (`Workday_resolver`).
 */
export interface LeaveFilingRuleVM {
  /** `leave_types.id`, as a string — the key of `LeaveFilingRulesVM.byTypeId`. */
  typeId: string;
  label: string;
  /** The server's type slug (e.g. `vacation`), for icons and fuzzy matching. */
  type: string;
  /** `type` when the leave type carries its own custom policy; `global` when it inherits. */
  source: 'global' | 'type';
  /** May a start date before `today` be filed at all? */
  allowBackdate: boolean;
  /** The back-date window in WORKING days (`0` = unlimited). Display only — never counted here. */
  lockDays: number;
  /** Advance notice in CALENDAR days for a start on/after today (`0` = none). */
  futureDays: number;
  /** The server's plain-text summary of the rule, e.g. "Up to 3 working days back · 0 days notice". */
  hint: string;
  /** The server's civil "today" (`YYYY-MM-DD`) — authoritative over the device clock. */
  today: string;
  /**
   * The earliest PAST start date still inside the lock window, or `null` when
   * the window is unbounded (`lockDays === 0`) or the server could not resolve
   * it. Meaningful only when `allowBackdate` is true.
   */
  earliestStartDate: string | null;
  /** The earliest start date the advance-notice rule accepts (≥ `today`). */
  minStartDate: string;
}

/** One hour count an employee can file as an hourly leave. */
export interface LeaveHourlyOptionVM {
  /** Whole hours — what `POST /me/leaves` takes as `hours`. */
  hours: number;
  /** The share of a day it charges (hours ÷ work hours per day, 2 decimals); null when not sent. */
  days: number | null;
}

/**
 * The filing-rules `hourly` block (HumanResource ≥ 1.85.0): HR Settings › Hourly
 * Leave and the Available Hourly Options that fit THIS employee's workday. It is
 * exactly the set `POST /me/leaves` accepts for `duration: hours` — the client
 * offers these and never computes its own.
 */
export interface LeaveHourlyFilingVM {
  enabled: boolean;
  /** The employee's work hours per day (salary record); 0 when none is on record. */
  hoursPerDay: number;
  /** Ascending; empty when the employee cannot file hourly leave. */
  options: LeaveHourlyOptionVM[];
}

/**
 * The filing-rules `attachments` block (HumanResource ≥ 1.95.0): whether this
 * company takes supporting documents on a leave, and on what terms. Every limit
 * the app shows or checks comes from here — none is hardcoded — and the server
 * re-checks each upload anyway (it screens the CONTENT, so `accept` is a hint
 * for the picker, not a guarantee).
 */
export interface LeaveAttachmentPolicyVM {
  /** `false` → offer no uploads (a short note at most) and send no tokens. */
  available: boolean;
  /** Why not, when not: `columns_missing` | `store_absent` | `store_failed` | `s3_unready` | `no_formats` | `server_limits` ('' when available). */
  reason: string;
  /** Documents per leave (the server's cap; 5 today). */
  maxFiles: number;
  /** Bytes per file; 0 when the server sent no limit. */
  maxFileBytes: number;
  /** Accepted extensions — lower-case, no dot, once each; empty when the server listed none. */
  accept: string[];
  /** `signed`: a document opens through a short-lived URL. `web_only`: no URL — it opens in ERPat on the web. */
  links: 'signed' | 'web_only';
  /** How long a staged upload waits for the leave to be filed. */
  expiresInSeconds: number;
}

/** The whole filing-rules payload, indexed for the form and the calendar. */
export interface LeaveFilingRulesVM {
  today: string;
  rules: LeaveFilingRuleVM[];
  byTypeId: Record<string, LeaveFilingRuleVM>;
  /** The earliest bounded back-date across the allowing rules (else `today`) — the calendar's lookback start. */
  windowStart: string;
  /** Rest days and non-working holidays over [`windowStart`, `today`] — selectable, but marked. */
  nonWorkingDates: Set<string>;
  /** Where the employee's workdays came from: `shift_planner` | `default_schedule` | `weekends` | `calendar`. */
  workdaySource: string;
  /**
   * The hours the employee can file as an hourly leave — `null` when the server
   * sent no `hourly` block. An ERPat older than HumanResource 1.85.0 ignores
   * `duration: hours` and would file a FULL day, so null means "never offer Hours".
   */
  hourly: LeaveHourlyFilingVM | null;
  /**
   * The supporting-documents policy — `null` when the server sent no
   * `attachments` block. An ERPat older than HumanResource 1.95.0 has no such
   * feature, so null means "render nothing and send no request".
   */
  attachments: LeaveAttachmentPolicyVM | null;
}

/**
 * Map the filing-rules `hourly` block. Anything that is not an object carrying an
 * `enabled` flag is `null` (not sent). Options keep whole hours ≥ 1 only, once
 * each, ascending; a disabled block offers none, whatever it listed.
 */
export function mapLeaveHourlyFiling(value: unknown): LeaveHourlyFilingVM | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const r = asRecord(value);
  if (!('enabled' in r)) return null;

  const enabled = r.enabled === true || r.enabled === 1 || r.enabled === '1' || r.enabled === 'true';
  const hoursPerDay = Number(r.hours_per_day);

  const byHours = new Map<number, LeaveHourlyOptionVM>();
  if (enabled && Array.isArray(r.options)) {
    for (const raw of r.options) {
      const o = asRecord(raw);
      const hours = Number(o.hours);
      if (!Number.isInteger(hours) || hours < 1) continue;
      const days = Number(o.days);
      const option: LeaveHourlyOptionVM = {
        hours,
        days: o.days !== undefined && o.days !== null && Number.isFinite(days) ? days : null,
      };
      if (!byHours.has(hours)) byHours.set(hours, option);
    }
  }

  return {
    enabled,
    hoursPerDay: enabled && Number.isFinite(hoursPerDay) && hoursPerDay > 0 ? hoursPerDay : 0,
    options: [...byHours.values()].sort((a, b) => a.hours - b.hours),
  };
}

/** A bare civil date (`YYYY-MM-DD`), trimmed, or '' — nothing else is trusted as one. */
const CIVIL_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
function civilDateOrEmpty(value: unknown): string {
  if (typeof value !== 'string') return '';
  const text = value.trim();
  return CIVIL_DATE_RE.test(text) ? text : '';
}

/** A non-negative whole number, or `0` for anything that is not one. */
function nonNegativeCount(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
}

/**
 * Normalise an `accept` list to bare extensions: lower-case, trimmed, leading
 * dots stripped, blanks and non-text entries dropped, each once (first order
 * kept) — so `['.PDF', ' jpg ', 'jpg']` reads `['pdf', 'jpg']` and the picker
 * and the device check compare like with like. A comma-separated string is
 * accepted too.
 */
function normalizeAcceptList(value: unknown): string[] {
  const entries: unknown[] = Array.isArray(value) ? value : typeof value === 'string' ? value.split(',') : [];
  const seen = new Set<string>();
  for (const entry of entries) {
    if (typeof entry !== 'string') continue;
    const extension = entry.trim().replace(/^\.+/, '').trim().toLowerCase();
    if (extension) seen.add(extension);
  }
  return [...seen];
}

/**
 * Map the filing-rules `attachments` block. `null` unless the server sent an
 * object (an older server sends nothing: the feature does not exist there).
 * Limits read through `numOf` (some arrive as strings) with the documented
 * defaults — 5 files, no byte limit — and any link mode but `web_only` is a
 * signed link, the only other one.
 */
export function mapLeaveAttachmentPolicy(raw: unknown): LeaveAttachmentPolicyVM | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = asRecord(raw);
  return {
    available: bool(r, ['available']),
    reason: str(r, ['reason'], '').trim(),
    maxFiles: nonNegativeCount(numOf(r, ['max_files'], 5)),
    maxFileBytes: nonNegativeCount(numOf(r, ['max_file_bytes'], 0)),
    accept: normalizeAcceptList(r.accept),
    links: str(r, ['links'], '').trim().toLowerCase() === 'web_only' ? 'web_only' : 'signed',
    expiresInSeconds: nonNegativeCount(numOf(r, ['expires_in_seconds'], 0)),
  };
}

/**
 * Map `GET /v1/api/me/leaves/filing-rules`.
 *
 * Defensive on every field: a garbage or missing date reads as "unknown"
 * (`earliestStartDate: null`, `today` falling back to the device's civil day)
 * rather than throwing, so a half-migrated tenant still gets a usable form —
 * the POST guard remains the authority either way. A rule without a
 * `leave_type_id` is skipped: there is nothing to key it by.
 */
export function mapLeaveFilingRules(data: unknown): LeaveFilingRulesVM {
  const body = domainBody(data, ['filing_rules', 'data']);
  const today = civilDateOrEmpty(body.today) || localDateKey(new Date());

  const rules: LeaveFilingRuleVM[] = [];
  const byTypeId: Record<string, LeaveFilingRuleVM> = {};
  const rawRules = Array.isArray(body.rules) ? body.rules : [];
  for (const raw of rawRules) {
    const r = asRecord(raw);
    const typeId = String(pick<unknown>(r, ['leave_type_id'], '')).trim();
    if (!typeId) continue;

    const ruleToday = civilDateOrEmpty(r.today) || today;
    const rule: LeaveFilingRuleVM = {
      typeId,
      label: str(r, ['label'], ''),
      type: str(r, ['type'], ''),
      source: r.source === 'type' ? 'type' : 'global',
      allowBackdate: bool(r, ['allow_backdate']),
      lockDays: nonNegativeCount(r.lock_days),
      futureDays: nonNegativeCount(r.future_days),
      hint: str(r, ['hint'], ''),
      today: ruleToday,
      earliestStartDate: civilDateOrEmpty(r.earliest_start_date) || null,
      minStartDate: civilDateOrEmpty(r.min_start_date) || ruleToday,
    };
    rules.push(rule);
    byTypeId[typeId] = rule;
  }

  const nonWorkingDates = new Set<string>();
  if (Array.isArray(body.non_working_dates)) {
    for (const value of body.non_working_dates) {
      const date = civilDateOrEmpty(value);
      if (date) nonWorkingDates.add(date);
    }
  }

  return {
    today,
    rules,
    byTypeId,
    windowStart: civilDateOrEmpty(body.window_start) || today,
    nonWorkingDates,
    workdaySource: str(body, ['workday_source'], '') || 'calendar',
    hourly: mapLeaveHourlyFiling(body.hourly),
    attachments: mapLeaveAttachmentPolicy(body.attachments),
  };
}

/**
 * A leave-type choice for the Request Leave modal, sourced from the account's
 * ERPat balances.
 *
 * `value` MUST stay the leave-type LABEL — the apply API fuzzy-matches on it
 * and every host resolves the id from it. `filing` is the type's filing window
 * when the rules endpoint has answered; a host without rules leaves it absent
 * and the server's POST guard alone decides.
 */
export interface LeaveTypeOption {
  value: string;
  label: string;
  /** Numeric leave-type id, when the host has it (`leave_type_id`). */
  id?: string;
  /** ERPat's own balance copy, e.g. "5 days". Absent when it reports none. */
  balanceLabel?: string;
  /** The type's filing rule; `null` when rules loaded but named no rule for this type. */
  filing?: LeaveFilingRuleVM | null;
}

/**
 * Build the modal's leave-type options from the balances (and, when loaded,
 * the filing rules keyed by `leave_type_id`). The shape every host used to
 * build inline, now in one place so they cannot drift apart.
 */
export function leaveTypeOptionsFrom(
  balances: LeaveBalanceVM[] | null | undefined,
  rules?: LeaveFilingRulesVM | null,
): LeaveTypeOption[] {
  return (balances ?? []).map((b) => {
    const option: LeaveTypeOption = { value: b.label, label: b.label };
    if (b.typeId) option.id = b.typeId;
    if (b.daysLabel) option.balanceLabel = b.daysLabel;
    if (rules) option.filing = rules.byTypeId[b.typeId] ?? null;
    return option;
  });
}

// ── Profile ──────────────────────────────────────────────────────────────────
export interface ProfileRow {
  id: string;
  label: string;
  value: string;
}
export interface ProfileVM {
  /** The user's id (ERPat `users.id`) — matches contact ids and post author ids. */
  id: string;
  name: string;
  initials: string;
  /** Absolute avatar URL, or '' when none / unusable. Render with an initials fallback. */
  image: string;
  email: string;
  role: string;
  status: string;
  employmentType: string;
  /** Display label for the employment stage, e.g. 'Fixed Term'. '' when HR has not set one. */
  employmentStage: string;
  /**
   * The same value as a RAW token, for branching on (icons, the regular-only
   * badge). `employmentStage` above is title-cased for display and so is a poor
   * lookup key — read this instead of reverse-engineering that label.
   */
  employmentStageKey: EmploymentStage;
  /** Self-authored bio (`users_meta.about_me`); '' when the user hasn't written one. */
  aboutMe: string;
  /** Server-derived presence for this person. 'offline' when never signed in. */
  presence: PresenceStatus;
  /** When they were last active, in epoch ms; 0 when never signed in. */
  lastOnline: number;
  personalInfo: ProfileRow[];
  employmentDetails: ProfileRow[];
  governmentIds: ProfileRow[];
  emergencyContact: ProfileRow[];
}

/**
 * Normalise a profile `image` into a usable absolute URL.
 *
 * The ERPat backend stores avatars at its web root as a relative path
 * (`uploads/avatar/7.jpg`); the profile endpoint may also hand back a full URL
 * (`https://erpat.app/files/profile/avatar.png`) or a root-relative path
 * (`/files/...`). All three resolve to an absolute URL against the API origin.
 *
 * Anything that does NOT look like an image file path is rejected to '' so the
 * UI falls back to initials instead of a broken <img>. That deliberately drops
 * the PHP-serialized string `auth.me`/login return for `user.image`
 * (`s:21:"uploads/avatar/7.jpg";`, `a:2:{...}`) — the `:`/`"`/`;` make it fail
 * the pattern even though a real path is embedded inside it.
 */
function profileImageUrl(value: unknown): string {
  if (typeof value !== 'string') return '';
  const v = value.trim();
  if (!v) return '';
  // Absolute URLs / data URIs are already usable.
  if (/^(https?:|data:)/i.test(v)) return v;
  // Accept only bare/root-relative image file paths (reject serialized junk).
  // A trailing ?query / #hash is allowed — avatars are commonly cache-busted
  // (`/uploads/avatar/7.jpg?v=3`), and dropping those left the initials showing.
  if (!/^\/?[\w.\-/]+\.(?:jpe?g|png|gif|webp|avif|bmp|svg)(?:[?#][^\s]*)?$/i.test(v)) return '';
  // Avatars live at the backend web ROOT, not under the API path prefix, so
  // resolve against the origin and treat bare-relative paths as root-relative.
  try {
    const origin = new URL(API_BASE_URL).origin;
    return new URL(v.startsWith('/') ? v : `/${v}`, origin).toString();
  } catch {
    return '';
  }
}

/** Every spelling the API has been seen to use for a person's avatar. */
const EMPLOYEE_IMAGE_KEYS = [
  'image',
  'avatar',
  'photo',
  'profile_image',
  'profile_photo',
  'image_url',
  'avatar_url',
  'photo_url',
  'employee_image',
];

/**
 * Absolute avatar URL for a person record, whichever key spelling it uses.
 *
 * The shared entry point for mappers that live outside this file (widgets.ts):
 * `profileImageUrl` and the key list are internal, and every caller wants the
 * same "try every spelling, resolve against the API origin, else ''" behaviour.
 */
export function personImageUrl(record: unknown): string {
  return profileImageUrl(pick<unknown>(asRecord(record), EMPLOYEE_IMAGE_KEYS, null));
}

/**
 * Resolve any uploaded-file reference to an absolute URL.
 *
 * Like {@link profileImageUrl}, but for files of ANY type — ERPat serves
 * uploads from its web root and returns them as relative paths, and an
 * attachment is just as likely to be a .pdf or .docx as an image. A trailing
 * query/hash is kept (cache-busters and signed links carry them).
 */
export function uploadedFileUrl(value: unknown): string {
  if (typeof value !== 'string') return '';
  const v = value.trim();
  if (!v) return '';
  if (/^(https?:|data:)/i.test(v)) return v;
  // Reject anything that isn't a plain path (serialized objects, "null", …).
  // An extension is NOT required: ERPat can hand back a download route or a
  // bare uuid, and requiring ".ext" silently dropped those. A path-like shape
  // is still required, so stray words and numbers don't become URLs.
  const pathLike = /^\/?[\w.\-/ %]+([?#][^\s]*)?$/i.test(v);
  const hasExt = /\.[a-z0-9]{1,8}([?#]|$)/i.test(v);
  if (!pathLike || (!hasExt && !v.includes('/'))) return '';
  try {
    const origin = new URL(API_BASE_URL).origin;
    return new URL(v.startsWith('/') ? v : `/${v}`, origin).toString();
  } catch {
    return '';
  }
}

/** A file attached to a note in ERPat. */
export interface NoteAttachmentVM {
  url: string;
  /** File name from the path, e.g. "paint.png". */
  name: string;
  /** Lower-case extension without the dot. */
  ext: string;
}

/** Keys ERPat has been seen to use for an attached file, in any of its shapes. */
const NOTE_FILE_KEYS = [
  'attachments',
  'attachment',
  'files',
  'file',
  'file_url',
  'file_path',
  'file_name',
  'media',
  'uploads',
  'upload',
  'documents',
  'document',
  'images',
  'image',
  'photo',
  'path',
];

/**
 * Pull a note's attachments out of the raw row.
 *
 * The value may be a single path, a comma-separated list, an array of paths, or
 * an array of objects — the shape isn't documented, so each is accepted and
 * anything that doesn't resolve to a real file path is dropped.
 */
export function mapNoteAttachments(rec: Json): NoteAttachmentVM[] {
  const out: NoteAttachmentVM[] = [];
  const seen = new Set<string>();

  /** Record one resolved file, preferring an explicit name over the path's. */
  const add = (rawUrl: string, explicitName = ''): void => {
    const url = uploadedFileUrl(rawUrl);
    if (!url || seen.has(url)) return;

    let name = explicitName.trim();
    if (!name) {
      try {
        name = decodeURIComponent(new URL(url).pathname.split('/').filter(Boolean).pop() ?? '');
      } catch {
        name = '';
      }
    }
    // A download route ends in a uuid or "/download" — no usable name, so fall
    // back to a generic one rather than dropping a real file.
    if (!name || !name.includes('.')) name = name || 'Attachment';

    seen.add(url);
    const ext = name.includes('.') ? (name.split('.').pop()?.toLowerCase() ?? '') : '';
    out.push({ url, name, ext });
  };

  const push = (value: unknown): void => {
    if (Array.isArray(value)) {
      value.forEach(push);
      return;
    }
    if (value && typeof value === 'object') {
      // An object carries the location AND usually the original file name; take
      // both, instead of recursing and losing the name.
      const o = asRecord(value);
      const loc = str(o, ['url', 'file_url', 'download_url', 'path', 'file_path', 'file', 'src'], '');
      const name = str(o, ['name', 'file_name', 'original_name', 'filename', 'title'], '');
      if (loc) add(loc, name);
      else if (name) add(name);
      return;
    }
    if (typeof value !== 'string') return;

    // A single field can carry several comma-separated paths.
    for (const part of value.split(',')) add(part);
  };

  for (const key of NOTE_FILE_KEYS) push(rec[key]);
  return out;
}

export function mapProfile(data: unknown): ProfileVM {
  // GET /me/profile → { profile: { first_name, last_name, email, phone, job_title,
  //   status, user_type, dob, street/city/state/country/zip, meta{...}, job_info{...} } }.
  const rec = domainBody(data, ['profile']);
  const meta = asRecord(pick(rec, ['meta'], {}));
  const job = asRecord(pick(rec, ['job_info', 'employment', 'employment_details'], {}));
  // `emergency_contact` is its own top-level object on the profile envelope
  // (sourced from team_member_job_info.contact_*); the older spellings stay as
  // fallbacks so a backend that predates it still resolves.
  const emergency = asRecord(pick(rec, ['emergency_contact', 'emergency', 'emergency_info'], {}));
  const name = `${str(rec, ['first_name'], '')} ${str(rec, ['last_name'], '')}`.replace(/\s+/g, ' ').trim()
    || str(rec, ['full_name', 'name', 'display_name'], '');
  const dob = pick<unknown>(rec, ['dob', 'date_of_birth', 'birth_date', 'birthday'], null);
  const address = [
    str(rec, ['street', 'address', 'home_address'], ''),
    str(rec, ['city'], ''),
    str(rec, ['state'], ''),
    str(rec, ['country'], ''),
    str(rec, ['zip', 'postal_code'], ''),
  ].filter(Boolean).join(', ');
  const firstText = (records: Json[], keys: string[], fallback = ''): string => {
    for (const source of records) {
      const value = str(source, keys, '');
      if (value.trim()) return value.trim();
    }
    return fallback;
  };
  const row = (id: string, label: string, value: string): ProfileRow | null =>
    value ? { id, label, value } : null;
  const ageLabel = (value: unknown): string => {
    const birthDate = toDate(value);
    if (!birthDate) return '';
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDelta = today.getMonth() - birthDate.getMonth();
    if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < birthDate.getDate())) age -= 1;
    return age >= 0 && age < 130 ? String(age) : '';
  };

  return {
    id: str(rec, ['id', 'user_id', 'users_id', 'uuid', '_id'], ''),
    name,
    initials: str(rec, ['initials'], '') || initialsOf(name),
    image: profileImageUrl(pick<unknown>(rec, ['image', 'avatar', 'photo', 'image_url', 'avatar_url'], null)),
    email: str(rec, ['email', 'work_email'], ''),
    role: str(rec, ['job_title', 'role', 'position', 'title'], ''),
    status: titleCase(str(rec, ['status', 'employment_status'], 'Active')),
    employmentType: titleCase(str(rec, ['employment_type', 'user_type', 'type'], '')),
    // `employment_stage` is an HR-controlled users_meta token (probationary,
    // fixed_term, …) the API hands back raw. On /me/profile it arrives inside
    // job_info; on another member's profile job_info is withheld entirely and the
    // token is emitted TOP-LEVEL instead, so both sources are read here.
    //
    // The two fields differ on purpose for an UNRECOGNISED token: the label keeps
    // titleCase so a legacy/custom value still DISPLAYS (matching the web app's
    // own ucwords fallback), while the key collapses to '' so icon/badge branching
    // renders nothing rather than something wrong.
    employmentStage: titleCase(firstText([job, rec], ['employment_stage'])),
    employmentStageKey: normalizeEmploymentStage(firstText([job, rec], ['employment_stage'])),
    aboutMe: firstText([meta, rec], ['about_me', 'about', 'bio']),
    // Derived server-side from `users.last_online` so a profile dot can never disagree with the
    // contacts list. Unknown values fall back to 'offline' rather than inventing presence.
    presence: (() => {
      const p = str(rec, ['presence'], '') as PresenceStatus;
      return PRESENCE.has(p) ? p : 'offline';
    })(),
    // `last_online_local` first — the bare `last_online` is raw UTC and would be misread by the
    // tenant's offset (see the same note in mapContacts).
    lastOnline: toMillis(pick<unknown>(rec, ['last_online_local', 'last_online'], null)),
    personalInfo: [
      row('full-name', 'Full Name', name),
      row('employee-email', 'Employee Email', str(rec, ['email', 'work_email'], '')),
      row('birthday', 'Birthday', formatDate(dob)),
      row('age', 'Age', ageLabel(dob)),
      row('gender', 'Gender', titleCase(str(rec, ['gender', 'sex'], ''))),
      row('civil-status', 'Civil Status', titleCase(firstText([meta, rec], ['civil_status', 'marital_status']))),
      row('citizenship', 'Citizenship', firstText([meta, rec], ['citizenship', 'nationality'])),
      row('religion', 'Religion', firstText([meta, rec], ['religion'])),
      row('mobile', 'Contact Number', str(rec, ['phone', 'mobile', 'contact_number'], '')),
      row('address', 'Address', address),
    ].filter((r): r is ProfileRow => r !== null),
    employmentDetails: [
      row('emp-id', 'Employee Number', firstText([job, rec], ['job_idnum', 'employee_id', 'employee_no', 'employee_number', 'emp_no'])),
      row('department', 'Department', firstText([job, rec, meta], ['department_name', 'department', 'team_name', 'team', 'unit_name', 'unit'])),
      row('position', 'Position', str(rec, ['job_title', 'position', 'title'], '')),
      row('employment-type', 'Employment Type', titleCase(str(rec, ['employment_type', 'user_type', 'type'], ''))),
      row('employment-status', 'Employment Status', titleCase(str(rec, ['status', 'employment_status'], ''))),
      row('employment-stage', 'Employment Stage', titleCase(firstText([job, rec], ['employment_stage']))),
      row('hired', 'Date Hired', formatDate(pick<unknown>(job, ['date_of_hire', 'date_hired', 'hire_date'], null))),
      row('regularized', 'Regularization Date', formatDate(pick<unknown>(job, ['date_regularized', 'regularization_date', 'date_of_regularization'], null))),
      row('bank', 'Bank', str(job, ['bank_name'], '')),
    ].filter((r): r is ProfileRow => r !== null),
    governmentIds: [
      row('tin', 'TIN', firstText([job, meta, rec], ['tin', 'tax_id', 'tax_identification_number'])),
      row('pag-ibig', 'PAG-IBIG', firstText([job, meta, rec], ['pag_ibig', 'pagibig', 'hdmf', 'pag_ibig_number', 'pagibig_number'])),
      row('philhealth', 'PHILHEALTH', firstText([job, meta, rec], ['phil_health', 'philhealth', 'phil_health_number', 'philhealth_number'])),
      row('sss', 'SSS Number', firstText([job, meta, rec], ['sss', 'sss_number'])),
    ].filter((r): r is ProfileRow => r !== null),
    emergencyContact: [
      row('emergency-name', 'Contact Name',
        firstText([emergency], ['contact_name', 'name', 'full_name', 'contact_person']) ||
        firstText([meta, rec], ['contact_name', 'emergency_contact_name', 'emergency_name', 'emergency_contact_person']),
      ),
      row('emergency-phone', 'Contact Phone',
        firstText([emergency], ['contact_phone', 'phone', 'mobile', 'number', 'contact_number', 'phone_number']) ||
        firstText([meta, rec], ['contact_phone', 'emergency_contact_phone', 'emergency_contact_number', 'emergency_phone']),
      ),
      row('emergency-address', 'Contact Address',
        firstText([emergency], ['contact_address', 'address', 'street']) ||
        firstText([meta, rec], ['emergency_contact_address', 'emergency_address']),
      ),
    ].filter((r): r is ProfileRow => r !== null),
  };
}

export interface PreferencesVM {
  notifications: boolean;
  themeMode: 'system' | 'light' | 'dark';
  language: string;
}
export function mapPreferences(data: unknown): PreferencesVM {
  // GET /me/preferences → { preferences: { notifications, theme_mode, language } }.
  const rec = domainBody(data, ['preferences']);
  const theme = str(rec, ['theme_mode', 'theme', 'appearance'], 'system');
  return {
    notifications: bool(rec, ['notifications', 'notifications_enabled', 'push_enabled'], true),
    themeMode: theme === 'light' || theme === 'dark' ? theme : 'system',
    language: str(rec, ['language', 'locale', 'lang'], 'en'),
  };
}

// ── AI Assistant ─────────────────────────────────────────────────────────────
export interface AssistantMessageVM {
  id: string;
  author: 'user' | 'assistant';
  body: string;
  /** Epoch ms the message was sent, or 0 when the server omits a timestamp. */
  at: number;
  card?: { type: string; data: Json };
}
export interface AssistantConversationVM {
  id: string;
  title: string;
  timeLabel: string;
  messages: AssistantMessageVM[];
}
export interface AssistantSuggestionVM {
  id: string;
  label: string;
  desc: string;
  prompt: string;
}

function mapAssistantMessage(m: unknown, i: number): AssistantMessageVM {
  const r = asRecord(m);
  const author = str(r, ['author', 'role', 'sender'], 'assistant');
  const card = pick<unknown>(r, ['card', 'attachment'], null);
  return {
    id: idOf(r, i),
    author: author === 'user' ? 'user' : 'assistant',
    body: str(r, ['body', 'content', 'text', 'message'], ''),
    at: toMillis(pick(r, ['created_at', 'created_at_local', 'sent_at', 'timestamp'], null)),
    ...(card ? { card: { type: str(asRecord(card), ['type'], ''), data: asRecord(card) } } : {}),
  };
}

export function mapAssistantConversation(data: unknown): AssistantConversationVM {
  // Single conversation is wrapped under `{ conversation: {...} }` (start/view);
  // list items arrive already-unwrapped, so domainBody falls back to the record.
  const r = domainBody(data, ['conversation']);
  return {
    id: idOf(r),
    title: str(r, ['title', 'subject', 'prompt'], 'New conversation'),
    timeLabel: formatRelative(pick<unknown>(r, ['updated_at', 'created_at'], null)),
    messages: toList(pick(r, ['messages', 'turns'], [])).map(mapAssistantMessage),
  };
}

/**
 * The assistant's reply to a prompt sent to an existing conversation. The send
 * endpoint returns ONLY the new reply as `{ message: {...} }` — not the whole
 * transcript — so the caller appends this to the running message list.
 */
export function mapAssistantReply(data: unknown): AssistantMessageVM {
  return mapAssistantMessage(domainBody(data, ['message']), 0);
}

export function mapAssistantConversationList(data: unknown): AssistantConversationVM[] {
  // The API already returns conversations newest-first (ORDER BY updated_at
  // DESC, id DESC). Preserve that order — do NOT re-sort by `timeLabel`, which
  // is a relative-time STRING ("Just now" / "3m ago" / "1h ago") that sorts
  // nonsensically and scrambles the ordering (it also broke the assistant
  // recovery poll's "newest conversation" lookup).
  return toList(data).map(mapAssistantConversation);
}

export function mapAssistantSuggestions(data: unknown): AssistantSuggestionVM[] {
  return toList(data).map((s, i) => {
    const r = asRecord(s);
    return {
      id: idOf(r, i),
      label: str(r, ['label', 'title'], ''),
      desc: str(r, ['description', 'desc', 'subtitle'], ''),
      prompt: str(r, ['prompt', 'text', 'query'], ''),
    };
  });
}

// ── Messages ─────────────────────────────────────────────────────────────────
const PRESENCE = new Set<PresenceStatus>(['online', 'away', 'offline']);
const VARIANT_LIST = ['teal', 'purple', 'blue', 'orange', 'green'] as const;
const VARIANTS = new Set<ConversationVariant>(VARIANT_LIST);
const variantAt = (i: number): ConversationVariant => VARIANT_LIST[i % VARIANT_LIST.length] ?? 'teal';

/** Resolve an attachment URL: absolute/data URLs pass through; relative → API origin. */
function resolveAttachmentUrl(value: unknown): string {
  if (typeof value !== 'string') return '';
  const v = value.trim();
  if (!v) return '';
  if (/^(https?:|data:)/i.test(v)) return v;
  try {
    const origin = new URL(API_BASE_URL).origin;
    return new URL(v.startsWith('/') ? v : `/${v}`, origin).toString();
  } catch {
    return '';
  }
}

const IMAGE_EXT = /\.(?:jpe?g|png|gif|webp|avif|bmp|heic|heif|svg)$/i;

/** Parse the euapi message `files` array into attachment VMs (image vs file). */
export function mapMessageAttachments(raw: unknown): MessageAttachment[] {
  return toList<Json>(raw)
    .map((f) => {
      const rec = asRecord(f);
      const name = str(rec, ['name', 'file_name', 'label'], 'Attachment');
      const url = resolveAttachmentUrl(pick<unknown>(rec, ['url', 'download_url', 'path'], ''));
      const size = pick<number>(rec, ['size', 'file_size'], 0);
      return {
        name,
        url,
        size: typeof size === 'number' ? size : 0,
        kind: IMAGE_EXT.test(name) ? ('image' as const) : ('file' as const),
      };
    })
    .filter((a) => a.name && a.url);
}

function mapChatMessage(m: unknown, i: number, meId = ''): ChatMessage {
  const r = asRecord(m);
  // Thread-detail rows name the sender via `from_user_id`; list/other shapes via
  // `author_id`/`sender_id`. Read all so "mine" resolves regardless of source.
  const authorId = str(r, ['from_user_id', 'author_id', 'sender_id', 'user_id', 'from'], '');
  // The server may not send an explicit self flag, so also compare the sender id
  // against the signed-in user's id (`meId`) — same logic as detailPersonName.
  const isMine =
    bool(r, ['mine', 'is_mine', 'is_self']) ||
    authorId === 'me' ||
    (!!meId && authorId === meId);
  const status = str(r, ['status', 'delivery'], '');
  // The sender has left the company: the server already sent `from_name` as
  // "Resigned Employee #<job id>" and no photo. Its own flag — a bare `status`
  // on a message row is the DELIVERY state, never this.
  const separated = !isMine && bool(r, ['from_separated', 'author_separated', 'sender_separated']);
  return {
    id: idOf(r, i),
    authorId: isMine ? 'me' : authorId,
    authorName: str(r, ['from_name', 'author_name', 'sender_name', 'name'], ''),
    body: str(r, ['body', 'content', 'text', 'message'], ''),
    timeLabel: formatTime(pick<unknown>(r, ['sent_at', 'created_at', 'timestamp'], null)),
    sentAt: toMillis(pick<unknown>(r, ['sent_at', 'created_at', 'timestamp'], null)),
    attachments: mapMessageAttachments(pick<unknown>(r, ['files', 'attachments'], [])),
    ...(status === 'sent' || status === 'delivered' || status === 'read' ? { status } : {}),
    ...(separated ? { authorSeparated: true } : {}),
  };
}

/**
 * Map one conversation row/thread. Direct messages and group chats are distinct
 * endpoints with distinct shapes, so the caller passes `kind` rather than us
 * guessing from a field that the API doesn't send.
 *
 * Direct list row (`GET /me/messages` → `{ messages: [...] }`):
 *   `{ thread_id, subject, snippet, status, is_read, counterpart:{id,name,image}, created_at }`
 * Group list row (`GET /me/message-groups` → `{ groups: [...] }`):
 *   `{ id, name, description, status, participant_count, unread_count, last_message_at }`
 * Detail responses wrap the header under `thread`/`group` alongside `messages`.
 */
export function mapConversation(data: unknown, i = 0, kind: ConversationKind = 'direct', meId = ''): Conversation {
  const r = asRecord(data);
  // Detail responses nest the header under a domain key; list rows are flat.
  const detail = asRecord(pick(r, kind === 'group' ? ['group'] : ['thread'], {}));
  // List rows carry `counterpart`; a direct THREAD response carries it beside
  // `thread` (and, defensively, under it) so a deep-linked thread knows who it is
  // with — and whether it is read-only — without the list.
  const counterpart = asRecord(
    pick<unknown>(r, ['counterpart', 'participant', 'with_user', 'user'], null) ??
      pick<unknown>(detail, ['counterpart'], {}),
  );
  // The server sends presence on the COUNTERPART (it describes the other person, not the
  // thread), so read there first. The root read stays as a fallback for any surface that puts it
  // flat, and because the group endpoints send neither.
  const presence = (str(counterpart, ['presence'], '') || str(r, ['presence'], '')) as PresenceStatus;
  const variant = str(r, ['variant', 'color'], '') as ConversationVariant;
  // Detail responses key messages at `r.messages`; list rows carry none.
  const rawMessages = toList(pick(r, ['messages', 'thread'], []));

  // Older servers' direct DETAIL threads carry no `counterpart` object — only
  // numeric from/to ids on the thread header and sender names on the individual
  // messages. So derive the other person's name (and whether they have left the
  // company) from the first message NOT sent by the signed-in user (`meId`). List
  // rows skip this (no messages) and use `counterpart` below.
  let incomingSeparated = false;
  const detailPersonName =
    kind === 'direct'
      ? (() => {
          for (const m of rawMessages) {
            const mr = asRecord(m);
            const fromId = str(mr, ['from_user_id', 'author_id', 'sender_id', 'user_id', 'from'], '');
            const mine = bool(mr, ['mine', 'is_mine', 'is_self']) || (!!meId && fromId === meId);
            const fromName = str(mr, ['from_name', 'author_name', 'sender_name', 'name'], '');
            if (!mine && fromName) {
              incomingSeparated = bool(mr, ['from_separated', 'author_separated', 'sender_separated']);
              return fromName;
            }
          }
          return '';
        })()
      : '';

  // Direct only: has the other person left the company, and may I reply? The
  // server's `counterpart.separated` / `can_reply` decide (never a bare `status`,
  // which means read/unread here and presence on a contact); an older server
  // sends neither — not separated, replyable.
  const counterpartSeparated =
    kind === 'direct' && (bool(counterpart, ['separated', 'is_separated']) || incomingSeparated);
  const canReply =
    kind === 'direct' && bool(r, ['can_reply'], bool(detail, ['can_reply'], true)) && !counterpartSeparated;

  // Name: group → group name; direct → the other person (list `counterpart` or
  // detail-derived), falling back to the thread subject as a last resort.
  const name =
    kind === 'group'
      ? str(r, ['name', 'title', 'display_name'], '') || str(detail, ['name', 'title'], '')
      : str(counterpart, ['name', 'display_name'], '') ||
        detailPersonName ||
        str(r, ['subject', 'name', 'title'], '') ||
        str(detail, ['subject', 'name'], '');

  // Subtitle = the thread title, shown grey beneath the person's name. Falls back
  // to the initial message body when the thread has no subject, so the header
  // still says what the chat is about. Dropped below when it equals `name`.
  const initialMessage = htmlToText(str(asRecord(rawMessages[0]), ['message', 'body', 'content', 'text'], ''));
  const subject =
    kind === 'direct'
      ? str(r, ['subject', 'title'], '') || str(detail, ['subject', 'title'], '') || initialMessage
      : '';

  // List rows carry the count at the top level; detail responses nest the group
  // header (and its `participant_count`) under the `group` key — read both.
  const memberCount =
    pick<number>(r, ['participant_count', 'member_count', 'members_count'], 0) ||
    pick<number>(detail, ['participant_count', 'member_count', 'members_count'], 0);

  // id: direct threads key on `thread_id`; groups (and detail headers) on `id`.
  const rawId =
    str(r, ['thread_id', 'id', 'uuid', '_id', 'key'], '') ||
    str(detail, ['id', 'thread_id'], '') ||
    String(i);
  // Direct thread ids and group ids come from different tables and can collide.
  // Namespace group ids with a `g-` prefix so the single `messages/:id` route
  // can tell them apart; ConversationPage strips it to call the right endpoint.
  const id = kind === 'group' ? `g-${rawId}` : rawId;

  // The other person's USER id (phase6-contract.md §3.2.2): the list row's
  // `counterpart.id`, with the detail header's directional ids as a fallback
  // (the side that is not me). This is the join to a realtime envelope, whose
  // direct `thread_id` is the counterpart user id, not a message id.
  const counterpartId =
    kind === 'group'
      ? ''
      : str(counterpart, ['id', 'user_id'], '') ||
        (() => {
          const from = str(detail, ['from_user_id'], '') || str(r, ['from_user_id'], '');
          const to = str(detail, ['to_user_id'], '') || str(r, ['to_user_id'], '');
          if (!meId) return '';
          if (from && from !== meId) return from;
          if (to && to !== meId) return to;
          return '';
        })();

  // unread: groups expose a count; direct rows only an is_read/status flag.
  const unreadCount = pick<number>(r, ['unread_count', 'unread'], NaN);
  const unread = Number.isFinite(unreadCount)
    ? Number(unreadCount)
    : bool(r, ['is_read', 'read'], str(r, ['status'], '').toLowerCase() !== 'unread')
      ? 0
      : 1;

  const lastAt = pick<unknown>(r, ['last_message_at', 'updated_at', 'created_at'], null);

  // Group rosters: the thread detail carries no member list, only message
  // senders — so derive a partial roster from them. List rows have no messages,
  // so this resolves to [] there (the shelf only shows on the detail page).
  const members = kind === 'group' ? deriveGroupMembers(rawMessages) : [];

  return {
    id,
    kind,
    counterpartId,
    name,
    ...(subject && subject !== name ? { subject } : {}),
    initials: str(r, ['initials'], '') || initialsOf(name),
    variant: VARIANTS.has(variant) ? variant : variantAt(i),
    ...(kind === 'direct' && PRESENCE.has(presence) ? { presence } : {}),
    ...(kind === 'group' ? { memberCount } : {}),
    ...(kind === 'group' && memberCount
      ? { membersLabel: `${memberCount} ${memberCount === 1 ? 'member' : 'members'}` }
      : {}),
    ...(members.length ? { members } : {}),
    // Previews are single-line, so flatten any HTML to text (no raw tags shown).
    lastMessage: htmlToText(str(r, ['last_message', 'preview', 'snippet', 'description'], '')),
    ...(kind === 'group' ? { lastSender: str(r, ['last_sender', 'last_sender_name'], '') } : {}),
    timeLabel: formatRelative(lastAt),
    updatedAt: toMillis(lastAt),
    unread,
    pinned: bool(r, ['pinned', 'is_pinned']),
    messages: rawMessages.map((m, i2) => mapChatMessage(m, i2, meId)),
    ...(kind === 'direct' ? { counterpartSeparated, canReply } : {}),
  };
}

/**
 * Build a group's participant roster from its thread messages.
 *
 * The end-user API exposes no members endpoint, so the only member identities
 * available are the people who've actually posted: group the messages by sender
 * (`from_user_id` / `from_name` / `from_image`), keep their latest activity, and
 * order newest-active first. This is a PARTIAL roster — silent members never
 * appear, so the result can be shorter than the group's `participant_count`.
 * Self-messages collapse under a stable `me` bucket via the `is_mine` flag.
 *
 * Superseded by {@link mapGroupMembers} the moment a real members endpoint lands.
 */
function deriveGroupMembers(rawMessages: unknown): GroupMember[] {
  interface Acc { id: string; name: string; image: string; isMe: boolean; lastAt: number; separated: boolean }
  const byKey = new Map<string, Acc>();

  for (const m of toList(rawMessages)) {
    const r = asRecord(m);
    const isMe = bool(r, ['mine', 'is_mine', 'is_self']);
    const rawId = str(r, ['from_user_id', 'author_id', 'sender_id', 'user_id', 'from'], '');
    // Bucket my own messages together even if the row omits an id.
    const key = isMe ? 'me' : rawId;
    if (!key) continue;

    // A sender who has left the company: the server sends the "Resigned Employee
    // #<job id>" label and no photo; never keep one even if a payload carries it.
    const separated = !isMe && bool(r, ['from_separated', 'author_separated', 'sender_separated']);
    const name = str(r, ['from_name', 'author_name', 'sender_name', 'name'], '');
    const image = separated
      ? ''
      : profileImageUrl(pick<unknown>(r, ['from_image', 'image', 'avatar', 'photo', 'image_url', 'avatar_url'], null));
    const at = toMillis(pick<unknown>(r, ['created_at', 'sent_at', 'timestamp'], null));

    const existing = byKey.get(key);
    if (existing) {
      if (at > existing.lastAt) existing.lastAt = at;
      if (!existing.name && name) existing.name = name;
      if (!existing.image && image) existing.image = image;
      if (separated) {
        existing.separated = true;
        existing.image = '';
        if (name) existing.name = name;
      }
    } else {
      byKey.set(key, { id: rawId || key, name, image, isMe, lastAt: at, separated });
    }
  }

  return [...byKey.values()]
    .sort((a, b) => b.lastAt - a.lastAt)
    .map((m, i) => ({
      id: m.id,
      name: m.name,
      initials: initialsOf(m.name),
      image: m.image,
      variant: variantAt(i),
      isMe: m.isMe,
      ...(m.lastAt ? { lastActiveLabel: formatRelative(m.lastAt), lastActiveAt: m.lastAt } : {}),
      ...(m.separated ? { separated: true } : {}),
    }));
}

/**
 * Map the full participant list from a (future) members endpoint —
 * `GET /me/message-groups/{id}/members` → `{ members: [...] }`. Reads names,
 * avatars, job titles and an admin flag defensively. Used in preference to the
 * thread-derived roster ({@link deriveGroupMembers}) when the endpoint exists.
 */
export function mapGroupMembers(data: unknown): GroupMember[] {
  return toList(data).map((m, i) => {
    const r = asRecord(m);
    const name =
      `${str(r, ['first_name'], '')} ${str(r, ['last_name'], '')}`.replace(/\s+/g, ' ').trim() ||
      str(r, ['name', 'full_name', 'display_name'], '');
    const role = str(r, ['job_title', 'position', 'title', 'role'], '');
    const lastAt = pick<unknown>(r, ['last_active_at', 'last_message_at', 'updated_at'], null);
    // A participant who has left the company: never keep a photo, even if sent.
    const separated = bool(r, ['separated', 'is_separated']);
    return {
      id: idOf(r, i),
      name,
      initials: str(r, ['initials'], '') || initialsOf(name),
      image: separated
        ? ''
        : profileImageUrl(pick<unknown>(r, ['image', 'avatar', 'photo', 'image_url', 'avatar_url'], null)),
      variant: variantAt(i),
      isMe: bool(r, ['is_me', 'is_self', 'mine']),
      ...(role ? { role } : {}),
      isAdmin: bool(r, ['is_admin', 'admin', 'is_owner', 'owner']),
      ...(toMillis(lastAt) ? { lastActiveLabel: formatRelative(lastAt), lastActiveAt: toMillis(lastAt) } : {}),
      ...(separated ? { separated: true } : {}),
    };
  });
}

export function mapConversationList(data: unknown, kind: ConversationKind = 'direct'): Conversation[] {
  return toList(data)
    .map((c, i) => mapConversation(c, i, kind))
    .sort((a, b) => b.updatedAt - a.updatedAt);
}

export function mapContacts(data: unknown): Contact[] {
  return toList(data).map((c, i) => {
    const r = asRecord(c);
    const name = str(r, ['name', 'display_name'], '');
    const presence = str(r, ['presence', 'status'], 'offline') as PresenceStatus;
    const conversationId = str(r, ['conversation_id', 'thread_id'], '');
    const lastOnline = toMillis(pick<unknown>(r, ['last_online_local', 'last_online', 'last_seen'], null));
    return {
      id: idOf(r, i),
      name,
      initials: str(r, ['initials'], '') || initialsOf(name),
      // The documented payload has no photo field; read it defensively anyway so
      // it lights up the day the backend adds one. Until then the compose picker
      // resolves avatars through the shared directory instead.
      image: profileImageUrl(pick<unknown>(r, [
        'image',
        'avatar',
        'photo',
        'profile_image',
        'image_url',
        'avatar_url',
        'photo_url',
      ], null)),
      variant: variantAt(i),
      role: str(r, ['role', 'department', 'title'], ''),
      presence: PRESENCE.has(presence) ? presence : 'offline',
      // The timestamp `presence` is derived from — kept so a surface can say "Last seen 12m ago"
      // rather than leaving a grey dot unexplained. Omitted for anyone who never signed in.
      // `last_online_local` FIRST: the bare `last_online` is raw UTC, and a "Y-m-d H:i:s" string
      // with no zone parses as LOCAL here, which would misread it by the tenant's offset.
      ...(lastOnline ? { lastOnline } : {}),
      ...(conversationId ? { conversationId } : {}),
    };
  });
}

// ── Notifications ────────────────────────────────────────────────────────────
const CATEGORIES: NotifCategory[] = ['Payroll', 'HR', 'System', 'Task', 'Attendance', 'Announcement', 'Projects'];
const ICON_VARIANTS: NotificationIconVariant[] = ['green', 'blue', 'purple', 'orange', 'teal'];

/**
 * Client-side renderer for `event` notifications. The API returns
 * `{ event, actor: { name, image }, context: {...}, is_read, created_at_local }`
 * with NO human title or description — every readable word below is synthesized
 * here, so this is the only place that decides how complete a notification
 * reads.
 *
 * Two rules keep unknown events useful rather than blank:
 *  - the TITLE is the humanized event itself ("leave_application_approved" →
 *    "Leave application approved"), never the family label, so the outcome
 *    (approved / rejected / cancelled) is never dropped;
 *  - every `context` key is surfaced as a labelled row ({@link contextDetails}),
 *    so a new backend event carries its payload through without a code change.
 * The family rule only supplies the things that can't be derived: category,
 * icon, colour and the page to open.
 */
interface EventRule {
  category: NotifCategory;
  variant: NotificationIconVariant;
  icon: string;
  /** Fallback title, used only when the payload carries no `event` at all. */
  title: string;
  /** How to introduce the actor. Defaults to "Action by". */
  actorLabel?: string;
  /** Build the one-line summary from the actor name + context. */
  desc?: (actor: string, ctx: Json) => string;
  /** The page this event is about. Paths must exist in `router/index.ts`. */
  link?: (ctx: Json) => NotificationLink | null;
  /** The record this event is about, so the detail view can fetch its facts. */
  target?: (ctx: Json) => NotificationTarget | null;
  /**
   * `link` is a module LIST page the server never named (the row names no
   * resource and its context no record id), so a tap that reaches the inbox through `?id=`
   * goes straight to it instead of opening the detail card. Only honoured when
   * the server supplied no link of its own — see `mapNotifications`.
   */
  forward?: boolean;
}

/** First present, non-empty context value among `keys`. */
function ctxStr(ctx: Json, keys: string[]): string {
  for (const k of keys) {
    const v = asString(ctx[k]).trim();
    if (v) return v;
  }
  return '';
}

/** `{ kind, id }` for the first context key that names the record, else null. */
function idTarget(ctx: Json, kind: NotificationTargetKind, keys: string[]): NotificationTarget | null {
  const id = ctxStr(ctx, keys);
  return id ? { kind, id } : null;
}

/** `{ to, label }` for `path/:id`, falling back to the list page without one. */
function idLink(ctx: Json, keys: string[], base: string, label: string, detailBase = base): NotificationLink {
  const id = ctxStr(ctx, keys);
  return { to: id ? `${detailBase}/${encodeURIComponent(id)}` : base, label };
}

/** Context keys that have been seen (or are likely) to name a chat thread. */
const MESSAGE_ID_KEYS = ['conversation_id', 'thread_id', 'message_id', 'id'];

const EVENT_RULES: Array<{ match: RegExp; rule: EventRule }> = [
  { match: /^ticket/, rule: { category: 'System', variant: 'blue', icon: 'chatbubbleEllipsesOutline', title: 'Support ticket',
    desc: (a, c) => joinParts([a, ctxStr(c, ['ticket_title', 'title', 'subject'])]),
    link: (c) => idLink(c, ['ticket_id', 'id'], '/ticketing', 'View ticket'),
    target: (c) => idTarget(c, 'ticket', ['ticket_id', 'id']) } },
  // A single leave request has no detail route in Syntry — the list page is the
  // closest thing to "the whole context", so send them there.
  { match: /^leave/, rule: { category: 'HR', variant: 'purple', icon: 'airplaneOutline', title: 'Leave update',
    desc: (a, c) => joinParts([a, ctxStr(c, ['leave_type_title', 'leave_type', 'reason'])]),
    link: () => ({ to: '/leaves', label: 'View leave requests' }),
    target: (c) => idTarget(c, 'leave', ['leave_id', 'leave_application_id', 'id']) } },
  { match: /^(attendance|clock|timesheet)/, rule: { category: 'Attendance', variant: 'teal', icon: 'timeOutline', title: 'Attendance',
    desc: (a, c) => joinParts([a, ctxStr(c, ['date', 'attendance_date', 'status'])]),
    link: () => ({ to: '/attendance/list', label: 'View timesheet' }) } },
  { match: /^(payslip|payroll)/, rule: { category: 'Payroll', variant: 'green', icon: 'walletOutline', title: 'Payroll',
    desc: (a, c) => joinParts([a, ctxStr(c, ['period', 'pay_date', 'status'])]),
    link: (c) => idLink(c, ['payslip_id', 'payroll_id', 'id'], '/payslips', 'View payslip', '/payslips/preview'),
    target: (c) => idTarget(c, 'payslip', ['payslip_id', 'payroll_id', 'id']) } },
  { match: /^(advisory|announcement)/, rule: { category: 'Announcement', variant: 'orange', icon: 'megaphoneOutline', title: 'Announcement',
    desc: (a, c) => joinParts([ctxStr(c, ['title', 'subject']), a]),
    link: (c) => idLink(c, ['advisory_id', 'announcement_id', 'id'], '/announcements', 'View announcement'),
    target: (c) => idTarget(c, 'advisory', ['advisory_id', 'announcement_id', 'id']) } },
  // ── Projects ───────────────────────────────────────────────────────────
  // MUST precede the /^(todo|task)/ rule below: first match wins, and every
  // ERPat project event is named `project_*` — including `project_task_updated`
  // and `project_task_commented`. Left after it they would still not match
  // (`project…` does not start with `todo`/`task`), but the ordering is load
  // bearing the moment anyone shortens these names, and the two features are
  // easy to confuse: "task" in this app means the PERSONAL to-do.
  //
  // The task rule comes first for the same reason the asset-endorsement rule
  // does — `project_task_commented` matches both patterns, and the task is the
  // thing the person is being told about.
  { match: /^project_task/, rule: { category: 'Projects', variant: 'blue', icon: 'briefcaseOutline', title: 'Project task',
    desc: (a, c) => joinParts([a, ctxStr(c, ['task_title', 'title', 'project_title'])]),
    link: (c) => idLink(c, ['task_id', 'id'], '/projects/tasks', 'View task', '/projects/tasks'),
    target: (c) => idTarget(c, 'project_task', ['task_id', 'id']) } },
  { match: /^project/, rule: { category: 'Projects', variant: 'blue', icon: 'briefcaseOutline', title: 'Project',
    desc: (a, c) => joinParts([a, ctxStr(c, ['project_title', 'title', 'subject'])]),
    link: (c) => idLink(c, ['project_id', 'id'], '/projects', 'View project', '/projects'),
    target: (c) => idTarget(c, 'project', ['project_id', 'id']) } },
  // "Task" here is the PERSONAL to-do (/v1/api/me/todos), NOT a project task.
  // The row TITLE says "To-Do" so the two are distinguishable at a glance; the
  // category chip stays 'Task' because it is a filter key the inbox already
  // groups on, and renaming it would be a wider change than the ambiguity costs.
  { match: /^(todo|task)/, rule: { category: 'Task', variant: 'purple', icon: 'checkboxOutline', title: 'To-Do',
    desc: (a, c) => joinParts([a, ctxStr(c, ['task_title', 'title', 'subject'])]),
    link: () => ({ to: '/todo', label: 'View to-dos' }) } },
  // ── Assets (Safekeep) ──────────────────────────────────────────────────
  // ERPat's assets module ships 26 notification events and ten cron jobs that
  // send them (return due, acceptance due, fault reported, caretaker handover…).
  // Without these two rules every one of them landed in the unstyled fallback
  // with no deep link, so a person was told their return was overdue and had
  // nowhere to tap.
  //
  // The endorsement rule MUST come first: an event like
  // `asset_endorsement_accepted` matches both patterns, and the document is the
  // thing the person is being asked about. Neither collides with the `/^ticket/`
  // rule above — `asset_ticket_created` begins with "asset".
  {
    match: /^asset_(endorsement|released_for_acceptance|acceptance_due|transfer_pending|caretaker)/,
    rule: {
      category: 'System', variant: 'teal', icon: 'documentTextOutline', title: 'Endorsement',
      desc: (a, c) => joinParts([a, ctxStr(c, ['reference_no', 'asset_title', 'title'])]),
      link: (c) => idLink(c, ['asset_endorsement_id'], '/my-assets/endorsements', 'View document'),
      target: (c) => idTarget(c, 'asset-endorsement', ['asset_endorsement_id']),
    },
  },
  {
    // Everything else the module sends, plus the `mentioned_on_an_asset` event
    // whose name does not start with "asset".
    match: /^asset|mentioned_on_an_asset/,
    rule: {
      category: 'System', variant: 'teal', icon: 'cubeOutline', title: 'Asset',
      desc: (a, c) => joinParts([a, ctxStr(c, ['asset_title', 'asset_tag', 'title'])]),
      // Every assets payload carries `asset_id` — the module's own notification
      // config requires it, because its legacy audience resolver needs it too.
      link: (c) => idLink(c, ['asset_id'], '/my-assets', 'View asset'),
      target: (c) => idTarget(c, 'asset', ['asset_id']),
    },
  },
  // ── Compliance (investigations, corrective actions) ────────────────────
  // ERPat Compliance 1.6.0 sends four bell-only events for these:
  // `disciplinary_investigation_assigned`, `corrective_action_assigned`,
  // `corrective_action_noted` and `corrective_action_acknowledged`. Every one
  // arrives with `resource: null` and a `context` that names no record — the
  // module's own config says "Syntry opens the right page from the event key" —
  // so there is no detail page to deep-link and no `target` to fetch, and the
  // module's LIST page is the closest place the person can act. Hence `forward`:
  // a tap on the live pop-up or the phone notification (both land on the inbox
  // with `?id=`) goes straight to that list instead of a detail card with
  // nothing on it but a button. No `desc`: the humanized event + actor reads
  // right. Placed before the loose message rule below, which must stay last.
  //
  // The corrective-action rule names its three events, as Syntry Desktop's does.
  // ERPat also declares `corrective_action_accepted` — never sent today, and
  // about a disciplinary case (its payload and link are the case's) — which
  // deliberately falls through to the rules below rather than being titled a
  // corrective action and forwarded to their list.
  { match: /^disciplinary_investigation_/, rule: { category: 'HR', variant: 'orange', icon: 'searchOutline', title: 'Investigation',
    link: () => ({ to: '/investigations', label: 'View investigations' }), forward: true } },
  { match: /^corrective_action_(assigned|noted|acknowledged)/, rule: { category: 'HR', variant: 'orange', icon: 'clipboardOutline', title: 'Corrective action',
    link: () => ({ to: '/corrective-actions', label: 'View corrective actions' }), forward: true } },
  // ── Calendar events ────────────────────────────────────────────────────
  // NOTE the name clash: `EVENT_RULES` here means NOTIFICATION event, and this
  // entry is the one about calendar EVENTS (the /events module).
  //
  // Covers all three spellings the backend sends: `event_reminder` (the cron
  // that fires at the user's reminder lead), plus the legacy
  // `new_event_added_in_calendar` and `calendar_event_modified`. Collides with
  // no earlier rule — none of them start with event_/new_event_/calendar_event_
  // — and it is placed BEFORE the loose message rule below, which is the only
  // unanchored pattern in this list and therefore the only one that could ever
  // grow into these names.
  //
  // Category is 'Announcement' rather than a new 'Events' one on purpose: the
  // inbox only special-cases HR and System in its filter chips, so a new
  // category would mean editing InboxPage for no visible gain, and an event IS
  // a company-to-you broadcast like an advisory.
  { match: /^(event_|new_event_|calendar_event_)/, rule: { category: 'Announcement', variant: 'blue', icon: 'calendarOutline', title: 'Event',
    desc: (a, c) => joinParts([ctxStr(c, ['event_title', 'title', 'subject']), a]),
    // `/events/:id` exists (router/index.ts); without an id this lands on the
    // list. An `event_reminder` payload always carries `event_id`.
    link: (c) => idLink(c, ['event_id'], '/events', 'View event'),
    target: (c) => idTarget(c, 'event', ['event_id']) } },
  // Matched loosely, not anchored: the backend prefixes these ("new_message_sent"),
  // and an anchored /^message/ silently dropped every one of them into the
  // unstyled fallback. Ticket/chat events that start with their own noun are
  // matched by the earlier rules, so this can't steal them.
  { match: /(^|_)(message|chat)/, rule: { category: 'System', variant: 'blue', icon: 'chatbubblesOutline', title: 'New message',
    actorLabel: 'From',
    desc: (a, c) => joinParts([a, ctxStr(c, ['preview', 'message', 'body'])]),
    // Deep-links to the thread when context names one. The id spellings below
    // are unverified against a live payload, so the detail view downgrades this
    // to the message list if the thread fetch comes back empty.
    link: (c) => idLink(c, MESSAGE_ID_KEYS, '/tabs/messages', 'Open message'),
    target: (c) => idTarget(c, 'message', MESSAGE_ID_KEYS) } },
];

/**
 * Display labels for the `context` keys seen in the wild. Anything not listed
 * is title-cased, with a trailing `_id` read as a reference number — so an
 * unknown key still renders as a readable row instead of being hidden.
 */
const DETAIL_LABELS: Record<string, string> = {
  leave_id: 'Reference',
  leave_type_title: 'Leave type',
  leave_type: 'Leave type',
  ticket_id: 'Ticket',
  ticket_title: 'Subject',
  payslip_id: 'Payslip',
  payroll_id: 'Payroll',
  advisory_id: 'Reference',
  announcement_id: 'Reference',
  asset_id: 'Asset',
  asset_tag: 'Asset tag',
  asset_title: 'Asset',
  asset_endorsement_id: 'Document',
  reference_no: 'Reference',
  to_user_name: 'Employee',
  from_user_name: 'From',
  title: 'Subject',
  subject: 'Subject',
  reason: 'Reason',
  status: 'Status',
  date: 'Date',
  date_from: 'From',
  date_to: 'To',
  period: 'Period',
  event_id: 'Reference',
  event_title: 'Event',
  // A recurring series sends the OCCURRENCE this notification is about, which
  // is not the event's own start_date.
  occurrence_date: 'Date',
  lead_minutes: 'Reminder (minutes before)',
};

/**
 * Internal plumbing the reader gains nothing from. Ids that *address* something
 * (leave_id, ticket_id) stay — they're the reference number a person quotes to
 * HR — but a raw user/company row id is noise.
 */
const DETAIL_SKIP_KEYS = new Set([
  'to_user_id',
  'from_user_id',
  'user_id',
  'actor_id',
  'company_id',
  'client_id',
  'tenant_id',
]);

/** Every scalar in `context`, labelled — the "complete details" of a notification. */
function contextDetails(ctx: Json): NotificationDetailRow[] {
  const rows: NotificationDetailRow[] = [];
  for (const [key, raw] of Object.entries(ctx)) {
    if (DETAIL_SKIP_KEYS.has(key)) continue;
    // Nested objects/arrays have no generic rendering; their scalars would be
    // meaningless out of context, so skip rather than print "[object Object]".
    if (raw === null || raw === undefined || typeof raw === 'object') continue;
    const value = asString(raw).trim();
    if (!value) continue;
    const label = DETAIL_LABELS[key] ?? titleCase(key.replace(/_id$/, '_reference'));
    rows.push({ label, value });
  }
  return rows;
}

function asString(v: unknown): string {
  return v == null ? '' : String(v);
}

/** Join the non-empty parts with a middot separator. */
function joinParts(parts: string[]): string {
  return parts.map((p) => p.trim()).filter(Boolean).join(' \u00b7 ');
}

// ── Canonical notification (phase6-contract.md §3.2.1) ───────────────────────

const DECIMAL_ID_RE = /^[0-9]{1,20}$/;
const RFC3339_Z_RE = /^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}(\.[0-9]{1,6})?Z$/;
const SQL_UTC_RE = /^([0-9]{4}-[0-9]{2}-[0-9]{2}) ([0-9]{2}:[0-9]{2}:[0-9]{2})$/;

/**
 * Compare two stream sequences (decimal strings of up to 20 digits — past
 * `Number.MAX_SAFE_INTEGER`, so never `Number()` them): by length, then
 * lexically. Returns `<0`, `0` or `>0` like a sort comparator. A non-decimal
 * value sorts LOWEST, so "unknown" never outranks a real position.
 */
export function compareStreamSequence(a: string | null | undefined, b: string | null | undefined): number {
  const left = typeof a === 'string' && DECIMAL_ID_RE.test(a) ? a.replace(/^0+(?=\d)/, '') : '';
  const right = typeof b === 'string' && DECIMAL_ID_RE.test(b) ? b.replace(/^0+(?=\d)/, '') : '';
  if (left.length !== right.length) return left.length - right.length;
  return left < right ? -1 : left > right ? 1 : 0;
}

/** `Y-m-d H:i:s` (the REST list's UTC form) → RFC3339 `Z`; an RFC3339 value passes through; anything else → ''. */
function toRfc3339Utc(value: string): string {
  const text = value.trim();
  if (RFC3339_Z_RE.test(text)) return text;
  const sql = SQL_UTC_RE.exec(text);
  return sql ? `${sql[1]}T${sql[2]}Z` : '';
}

function isAllowlistedActionRoute(route: string): route is RealtimeActionRoute {
  return (REALTIME_ACTION_ROUTES as readonly string[]).includes(route);
}

/**
 * The server's `action` on a REST row / snapshot item, reduced exactly as the
 * envelope parser reduces it: an allowlisted dotted route with string params,
 * else `null` (an unknown-but-valid route falls back to the inbox).
 */
function mapRealtimeAction(value: unknown): RealtimeAction | null {
  const rec = asRecord(value);
  const route = str(rec, ['route'], '');
  if (!route || !isAllowlistedActionRoute(route)) return null;
  const params: Record<string, string> = {};
  for (const [key, param] of Object.entries(asRecord(rec.params))) {
    if (typeof param === 'string') params[key] = param;
    else if (typeof param === 'number' && Number.isFinite(param)) params[key] = String(param);
  }
  return { route, params };
}

function mapRealtimeResource(value: unknown): RealtimeResource | null {
  const rec = asRecord(value);
  const type = str(rec, ['type'], '').trim().toLowerCase();
  const id = str(rec, ['id'], '').trim();
  return type && id ? { type, id } : null;
}

/**
 * The actor in the envelope's shape. A snapshot item already carries
 * `{id, display_name, avatar_url}`; a REST row carries `{user_id, name, image}`
 * (a `user_id` of 0 is "no actor" — a cron or the system).
 */
function mapRealtimeActor(value: unknown): RealtimeActor | null {
  const rec = asRecord(value);
  if (!Object.keys(rec).length) return null;
  const id = str(rec, ['id', 'user_id'], '').trim();
  if (!id || id === '0') return null;
  if (id !== 'system' && !DECIMAL_ID_RE.test(id)) return null;
  const avatar = typeof rec.avatar_url === 'string' ? rec.avatar_url : personImageUrl(rec);
  return {
    id,
    display_name: str(rec, ['display_name', 'name'], '').trim(),
    avatar_url: avatar || null,
  };
}

/**
 * Reduce one raw row — a `GET /me/notifications` list row OR a bootstrap
 * snapshot item — to the canonical shape the store reconciles on. `null` when
 * the row has no decimal notification id (the gateway's UUID test event, a
 * malformed row): such a row is never stored.
 *
 * Field sources, REST row → snapshot item:
 *   id             `notification_id` / `id`          → `notification.id`
 *   category       `category`                        → `notification.category`
 *   read           `is_read`                         → `read`
 *   dismissed      (absent; the list hides them)     → `dismissed`
 *   revision       `state_revision` (nullable)       → `state_revision` (≥ 1)
 *   time           `created_at_utc` (`Y-m-d H:i:s`)  → `occurred_at` (RFC3339 Z)
 */
export function mapCanonicalNotification(raw: unknown): CanonicalNotification | null {
  const r = asRecord(raw);
  const nested = asRecord(r.notification);
  const notificationId = (str(nested, ['id'], '') || str(r, ['notification_id', 'id'], '')).trim();
  if (!DECIMAL_ID_RE.test(notificationId)) return null;

  const revisionRaw = pick<unknown>(r, ['state_revision'], null);
  const revision =
    typeof revisionRaw === 'number' && Number.isInteger(revisionRaw) && revisionRaw >= 1
      ? revisionRaw
      : typeof revisionRaw === 'string' && /^[0-9]+$/.test(revisionRaw) && Number(revisionRaw) >= 1
        ? Number(revisionRaw)
        : null;

  const privacyRaw = str(r, ['privacy_class'], '').toLowerCase();
  const privacyClass: RealtimePrivacyClass = (REALTIME_PRIVACY_CLASSES as readonly string[]).includes(privacyRaw)
    ? (privacyRaw as RealtimePrivacyClass)
    : 'private';

  const title = typeof nested.title === 'string' ? nested.title : null;
  const body = typeof nested.body === 'string' ? nested.body : null;

  return {
    notificationId,
    event: str(r, ['event'], ''),
    category: (str(nested, ['category'], '') || str(r, ['category'], '') || 'system').toLowerCase(),
    privacyClass,
    action: mapRealtimeAction(r.action),
    resource: mapRealtimeResource(r.resource),
    stateRevision: revision,
    read: bool(r, ['read', 'is_read']),
    dismissed: bool(r, ['dismissed']),
    title,
    body,
    createdAtUtc: toRfc3339Utc(str(r, ['occurred_at', 'created_at_utc'], '')),
    createdAtLocal: str(r, ['created_at_local'], ''),
    actor: mapRealtimeActor(r.actor),
  };
}

/** The bootstrap snapshot's items, canonical; rows without a decimal id are dropped. */
export function mapSnapshotNotifications(rows: unknown[]): CanonicalNotification[] {
  const out: CanonicalNotification[] = [];
  for (const row of rows) {
    const canonical = mapCanonicalNotification(row);
    if (canonical) out.push(canonical);
  }
  return out;
}

/**
 * `resource.type` → the record the detail sheet fetches (§3.2.1's explicit
 * map). `payslip` is deliberately absent: the server emits no resource for
 * payroll events, so that target still comes from `EVENT_RULES`.
 */
const RESOURCE_TARGET_KINDS: Readonly<Record<string, NotificationTargetKind>> = {
  leave_application: 'leave',
  announcement: 'advisory',
  task: 'project_task',
  calendar_event: 'event',
  asset_endorsement: 'asset-endorsement',
  message: 'message',
  ticket: 'ticket',
  project: 'project',
  asset: 'asset',
};

/**
 * The deep-link button label, keyed by the ROUTE NAME the link resolves to
 * (`router/index.ts`). A route not listed here reads "Open".
 */
const LINK_LABELS: Readonly<Record<string, string>> = {
  conversation: 'Open message',
  'group-conversation': 'Open group chat',
  messages: 'Open messages',
  leaves: 'View leave requests',
  'attendance-list': 'View timesheet',
  schedules: 'View schedule',
  disciplinary: 'View case',
  investigations: 'View investigations',
  'corrective-actions': 'View corrective actions',
  'loan-detail': 'View loan',
  loans: 'View loans',
  todo: 'View to-dos',
  'event-detail': 'View event',
  events: 'View events',
  timeline: 'View post',
  'project-detail': 'View project',
  projects: 'View projects',
  'project-task-detail': 'View task',
  'project-my-tasks': 'View tasks',
  'ticketing-detail': 'View ticket',
  ticketing: 'View tickets',
  'asset-detail': 'View asset',
  'my-assets': 'View assets',
  'asset-endorsement-detail': 'View document',
  'asset-endorsements': 'View documents',
  'advisory-detail': 'View announcement',
  announcements: 'View announcements',
};
const LINK_LABEL_DEFAULT = 'Open';

function routeNameOf(location: RouteLocationRaw): string {
  if (typeof location === 'string') return '';
  const name = (location as { name?: unknown }).name;
  return typeof name === 'string' ? name : '';
}

/**
 * The server's `action` / `resource` → a router location, or `null` when they
 * only say "open the inbox" (the row IS the inbox). A specific action (a
 * thread, an advisory) wins; a `notifications.view` / `notifications.inbox`
 * action or no action at all defers to the resource's module page — the same
 * precedence `resolveRealtimeTarget()` gives a live envelope.
 */
function serverLink(action: RealtimeAction | null, resource: RealtimeResource | null): NotificationLink | null {
  let location: RouteLocationRaw | null = null;
  if (action && action.route !== 'notifications.view' && action.route !== 'notifications.inbox') {
    location = resolveRealtimeRoute(action, resource);
  } else {
    location = resolveResourceRoute(resource);
  }
  if (location === null) return null;
  const name = routeNameOf(location);
  if (name === 'inbox') return null;
  return { to: location, label: LINK_LABELS[name] ?? LINK_LABEL_DEFAULT };
}

function serverTarget(resource: RealtimeResource | null): NotificationTarget | null {
  if (!resource) return null;
  const kind = RESOURCE_TARGET_KINDS[resource.type];
  const id = resource.id.trim();
  return kind && id ? { kind, id } : null;
}

/**
 * `GET /me/notifications` rows → inbox items.
 *
 * Every row first reduces to its {@link CanonicalNotification} (identity,
 * revision, read state, the server's `action` / `resource`); the presentation
 * fields are then layered on top. The server's `action` / `resource` decide the
 * deep link and the detail target; `EVENT_RULES` is the LAST fallback for the
 * events the server stamps no resource on (payslip, payroll, an attendance row
 * without an id) — DEC-082. The title is still guessed from the event name: the
 * REST list carries no `title` (that lands only in the snapshot / envelope).
 */
export function mapNotifications(data: unknown): NotificationItem[] {
  return toList(data).map((n, i) => {
    const r = asRecord(n);
    const canonical = mapCanonicalNotification(r);
    // Already-rendered notifications (title/description present) pass through.
    const presetTitle = str(r, ['title', 'subject'], '');
    const event = canonical?.event || str(r, ['event', 'type', 'group'], '');
    const actorRec = asRecord(r.actor);
    const actor = str(actorRec, ['name'], '') || canonical?.actor?.display_name || '';
    const ctx = asRecord(r.context);
    const matched = EVENT_RULES.find((e) => e.match.test(event))?.rule;

    // Title precedence (DEC-082): the snapshot's presenter text when a row
    // carries one, else the row's own title, else the humanized event name
    // ("Leave application approved" says what happened, "Leave update"
    // doesn't), else the family title, else the generic word.
    const title = canonical?.title || presetTitle || humanizeEvent(event) || matched?.title || 'Notification';
    const description =
      canonical?.body ||
      str(r, ['description', 'body', 'message'], '') ||
      (matched?.desc ? matched.desc(actor, ctx) : actor);

    const rawCat = canonical?.category ?? str(r, ['category'], '');
    const category = (CATEGORIES.find((c) => c.toLowerCase() === rawCat.toLowerCase()) ?? matched?.category ?? 'System') as NotifCategory;
    const rawVariant = str(r, ['icon_variant', 'variant', 'color'], '') as NotificationIconVariant;
    const high = str(r, ['priority'], '').toLowerCase() === 'high';
    const sentAt = pick<unknown>(r, ['created_at_local', 'created_at', 'sent_at', 'timestamp'], null);
    const dateLabel = joinParts([formatDate(sentAt), formatTime(sentAt)]);

    // Server first, then the event family, then nothing.
    const action = canonical?.action ?? null;
    const resource = canonical?.resource ?? null;
    const fromServer = serverLink(action, resource);
    const link = fromServer ?? (matched?.link ? matched.link(ctx) : null);
    const target = serverTarget(resource) ?? (matched?.target ? matched.target(ctx) : null);
    // A forwarding rule only forwards to ITS OWN link: a server-named page wins
    // and is a detail-card row like any other.
    const forward = fromServer === null && matched?.forward === true && link !== null;

    return {
      id: idOf(r, i),
      notificationId: canonical?.notificationId ?? '',
      stateRevision: canonical?.stateRevision ?? null,
      category,
      iconVariant: ICON_VARIANTS.includes(rawVariant) ? rawVariant : matched?.variant ?? ICON_VARIANTS[i % 5] ?? 'teal',
      icon: str(r, ['icon'], '') || matched?.icon || 'notificationsOutline',
      ...(high ? { priority: 'high' as const } : {}),
      title,
      description,
      timeAgo: formatRelative(sentAt),
      read: canonical?.read ?? bool(r, ['is_read', 'read', 'seen']),
      dismissed: canonical?.dismissed ?? false,
      actorName: actor,
      actorLabel: matched?.actorLabel ?? 'Action by',
      actorImage: personImageUrl(actorRec),
      dateLabel,
      details: contextDetails(ctx),
      link,
      ...(forward ? { forward: true } : {}),
      target,
    };
  });
}

/**
 * The list envelope with its live `unread_count` kept — the notifications
 * store's `refresh()` source (§3.3): the rows re-floor the held items, the
 * count is the badge's server term.
 */
export interface NotificationListWithCount {
  notifications: CanonicalNotification[];
  /** The server's live unread total for the caller (every page, not this one). */
  unreadCount: number;
}

export function mapNotificationListWithCount(data: unknown): NotificationListWithCount {
  const rec = asRecord(data);
  const countRaw = pick<unknown>(rec, ['unread_count', 'unread'], null);
  const unreadCount =
    typeof countRaw === 'number' && Number.isFinite(countRaw)
      ? Math.max(0, Math.trunc(countRaw))
      : typeof countRaw === 'string' && /^[0-9]+$/.test(countRaw)
        ? Number(countRaw)
        : 0;
  return {
    notifications: mapSnapshotNotifications(toList(data)),
    unreadCount,
  };
}

/** "leave_application_approved" → "Leave application approved". */
function humanizeEvent(event: string): string {
  const words = event.replace(/[_-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
}

// ── Payslips / Payroll ───────────────────────────────────────────────────────
// Verified shapes (live + backend source, 2026-06-22):
//  - list:  { payslips: [{ id, status, pay_date, period_start, period_end, totals }], meta }
//  - detail:{ payslip: {...same header...} }
//  - lines: { payslip_id, earnings:[{title, amount}], deductions:[{title, amount}], totals }
// IMPORTANT — the header `totals` (on both the list rows AND the detail header) are
// ALWAYS 0, and *structurally* so — not a "backend will populate it later" gap. The
// backend sums them from the payroll-run columns `payrolls.earnings`/`payrolls.deductions`,
// which hold a CSV of item-TYPE keys ("basic_pay,overtime,…"), not amount blobs — its
// sum_envelope() can't parse a CSV, so it returns 0. The ONLY source of real per-employee
// money is /lines (summed from the payslips_earnings/payslips_deductions tables). So:
// the list view-model carries NO net (the list page enriches each row via
// payslipsService.totals → /lines); the detail renders the breakdown + real totals from
// /lines. PDF + YTD are not provided by the API.
export interface PayslipListItemVM {
  id: string;
  monthLabel: string;
  periodLabel: string;
  payDateLabel: string;
  status: string;
  statusLabel: string;
  paidAtMs: number;
}
export interface PayslipLineVM {
  label: string;
  amountLabel: string;
}
/** Real money figures for one payslip, derived from /lines (the only source). */
export interface PayslipTotalsVM {
  currency: string;
  net: number;
  gross: number;
  deductions: number;
  netLabel: string;
  grossLabel: string;
  deductionsLabel: string;
}
export interface PayslipDetailVM {
  id: string;
  monthLabel: string;
  periodLabel: string;
  payDateLabel: string;
  status: string;
  statusLabel: string;
  currency: string;
  earnings: PayslipLineVM[];
  deductions: PayslipLineVM[];
  grossLabel: string;
  deductionsLabel: string;
  netLabel: string;
}

function monthLabel(value: unknown): string {
  const d = toDateSafe(value);
  return d ? d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : '';
}
function periodLabel(start: unknown, end: unknown): string {
  const s = formatDate(start);
  const e = formatDate(end);
  return s && e ? `${s} – ${e}` : s || e;
}
function toDateSafe(value: unknown): Date | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Map a single payslip header row (used per-row by the paginated list service). */
export function mapPayslipListItem(p: unknown, i = 0): PayslipListItemVM {
  const r = asRecord(p);
  const status = str(r, ['status'], '');
  return {
    id: idOf(r, i),
    monthLabel: monthLabel(pick<unknown>(r, ['period_start', 'pay_date'], null)),
    periodLabel: periodLabel(r.period_start, r.period_end),
    payDateLabel: formatDate(r.pay_date),
    status,
    statusLabel: titleCase(status),
    paidAtMs: toMillis(r.pay_date),
  };
}

export function mapPayslipList(data: unknown): PayslipListItemVM[] {
  return toList(data).map((p, i) => mapPayslipListItem(p, i));
}

const mapPayLines = (data: unknown, key: string, currency: string): PayslipLineVM[] =>
  toList(asRecord(data)[key])
    .map((l) => {
      const r = asRecord(l);
      return {
        label: str(r, ['title', 'label', 'name', 'item_key'], ''),
        amount: Number(pick<number>(r, ['amount'], 0)) || 0,
      };
    })
    // Drop zero-value rows: a payslip carries a dozen earning/deduction TYPES, most
    // 0.00 for any given period, so listing them all is noise. The /lines `totals`
    // already sum every row, so the shown (non-zero) lines still add up to the total.
    .filter((l) => Math.abs(l.amount) >= 0.005)
    .map((l) => ({ label: l.label, amountLabel: formatMoney(l.amount, currency) }));

/**
 * Map the /lines `totals` block — the ONLY source of real money figures (the
 * payslip header totals are always 0; see the note above). Falls back to
 * gross − deductions if the server omits net_pay.
 */
export function mapPayslipTotals(lines: unknown): PayslipTotalsVM {
  const lt = asRecord(asRecord(lines).totals);
  const currency = str(lt, ['currency'], 'PHP');
  const gross = Number(pick<number>(lt, ['gross_earnings'], 0)) || 0;
  const deductions = Number(pick<number>(lt, ['total_deductions'], 0)) || 0;
  // The server reports net as max(0, gross − deductions) (clamped, never negative);
  // mirror that clamp in the fallback so a derived net can't diverge from the API's.
  const net =
    lt.net_pay != null ? Number(pick<number>(lt, ['net_pay'], 0)) || 0 : Math.max(0, gross - deductions);
  return {
    currency,
    gross,
    deductions,
    net,
    grossLabel: formatMoney(gross, currency),
    deductionsLabel: formatMoney(deductions, currency),
    netLabel: formatMoney(net, currency),
  };
}

/** Merge the payslip header (period/status) with its /lines breakdown + real totals. */
export function mapPayslipDetail(header: unknown, lines: unknown): PayslipDetailVM {
  const h = domainBody(header, ['payslip']);
  const totals = mapPayslipTotals(lines);
  return {
    id: idOf(h),
    monthLabel: monthLabel(pick<unknown>(h, ['period_start', 'pay_date'], null)),
    periodLabel: periodLabel(h.period_start, h.period_end),
    payDateLabel: formatDate(h.pay_date),
    status: str(h, ['status'], ''),
    statusLabel: titleCase(str(h, ['status'], '')),
    currency: totals.currency,
    earnings: mapPayLines(lines, 'earnings', totals.currency),
    deductions: mapPayLines(lines, 'deductions', totals.currency),
    grossLabel: totals.grossLabel,
    deductionsLabel: totals.deductionsLabel,
    netLabel: totals.netLabel,
  };
}

// ── Salary history (Salary Records page) ─────────────────────────────────────
// Shape confirmed from the backend projection (Salary_euapi::shape):
//   { salary_history: [{ id, effective_date, status, payroll_basis, salary_term,
//       monthly_salary, daily_rate, hourly_rate,
//       salary_grade: { id, name, level, step }, currency, remarks,
//       created_at, updated_at }], meta }
// `/me/salary/current` and `/me/salary/{id}` return the SAME record shape under
// the `salary` key, so one mapper serves all three endpoints.
//
// Two server behaviours the mapper has to encode:
//  - Only APPROVED records are ever returned (the server pins `status` and
//    ignores `?status=`), so `status` is informational, never a filter here.
//  - Rates default to 0 when unset. A 0 rate means "not on file", NOT "zero
//    pesos", so its label is left empty for the UI to render as an em dash —
//    the same rule ProfilePage's positiveNumberField() applies.
export interface SalaryRecordVM {
  id: string;
  /** Raw `YYYY-MM-DD`, kept for range queries / detail lookups. */
  effectiveDate: string;
  effectiveLabel: string;
  effectiveMs: number;
  status: string;
  statusLabel: string;
  payrollBasisLabel: string;
  salaryTermLabel: string;
  currency: string;
  monthly: number;
  monthlyLabel: string;
  daily: number;
  dailyLabel: string;
  hourly: number;
  hourlyLabel: string;
  /** e.g. "Grade IV · Level 4 · Step 2"; empty when the record is ungraded. */
  gradeLabel: string;
  remarks: string;
  updatedLabel: string;
}

/**
 * Money for a salary record, honouring whatever the tenant actually configured.
 *
 * The API sends `get_setting('default_currency_symbol') ?: 'PHP'`, so `currency`
 * is usually a SYMBOL ("₱"), not an ISO code. `formatMoney` hands it to
 * `Intl.NumberFormat({ style: 'currency' })`, which throws on a non-ISO value and
 * falls back to a bare number — that is why ProfilePage notes formatMoney "drops
 * it for a non-ISO currency". On a page that is nothing but money, print the
 * tenant's own symbol instead. Deliberately NOT mapping a symbol onto a guessed
 * code: "$" → USD/SGD/AUD is unknowable, and printing the wrong currency is worse
 * than printing none.
 *
 * Also the money formatter of the Current Cutoff Earnings report
 * (`services/cutoff-earnings-report.ts`), whose payload carries the same symbol.
 */
export function salaryMoney(amount: number, currency: string): string {
  const c = currency.trim();
  if (/^[A-Za-z]{3}$/.test(c)) return formatMoney(amount, c.toUpperCase());
  const n = amount.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return c ? `${c}${n}` : n;
}

/** A salary grade rendered as one line; '' when no grade is attached. */
function salaryGradeLabel(grade: Json): string {
  const name = str(grade, ['name', 'title'], '');
  const level = pick<unknown>(grade, ['level'], null);
  const step = pick<unknown>(grade, ['step'], null);
  const parts: string[] = [];
  if (name) parts.push(name);
  if (level != null && level !== '') parts.push(`Level ${String(level)}`);
  if (step != null && step !== '') parts.push(`Step ${String(step)}`);
  return parts.join(' · ');
}

/** Map one `salary_history` row (used per-row by the paginated list service). */
export function mapSalaryRecord(raw: unknown, i = 0): SalaryRecordVM {
  const r = asRecord(raw);
  const status = str(r, ['status'], '');
  const currency = str(r, ['currency'], 'PHP') || 'PHP';
  const effectiveDate = str(r, ['effective_date'], '');

  // 0 = not on file. Keep the number for logic, blank the label for the UI.
  const rate = (keys: string[]): { value: number; label: string } => {
    const value = Number(pick<number>(r, keys, 0)) || 0;
    return { value, label: value > 0 ? salaryMoney(value, currency) : '' };
  };
  const monthly = rate(['monthly_salary', 'monthly']);
  const daily = rate(['daily_rate']);
  const hourly = rate(['hourly_rate']);

  return {
    id: idOf(r, i),
    effectiveDate,
    effectiveLabel: formatDate(effectiveDate),
    effectiveMs: toMillis(effectiveDate),
    status,
    statusLabel: titleCase(status),
    payrollBasisLabel: titleCase(str(r, ['payroll_basis'], '')),
    salaryTermLabel: titleCase(str(r, ['salary_term'], '')),
    currency,
    monthly: monthly.value,
    monthlyLabel: monthly.label,
    daily: daily.value,
    dailyLabel: daily.label,
    hourly: hourly.value,
    hourlyLabel: hourly.label,
    gradeLabel: salaryGradeLabel(asRecord(r.salary_grade)),
    remarks: str(r, ['remarks'], '').trim(),
    updatedLabel: formatDate(pick<unknown>(r, ['updated_at', 'created_at'], null)),
  };
}

/** Map a `{ salary: {...} }` single-record payload (`/current`, `/{id}`). */
export function mapSalaryRecordBody(data: unknown): SalaryRecordVM | null {
  const rec = domainBody(data, ['salary']);
  return Object.keys(rec).length ? mapSalaryRecord(rec) : null;
}

// ── Loans (self-service borrowing) ───────────────────────────────────────────
// Verified live against rs8 (scripts/verify-loans.mjs):
//   { loans: [{ id, category, status, status_key, principal_amount,
//     interest_rate, months_to_pay, min_payment, monthly_payment, total_paid,
//     total_fees, current_balance, payroll_binding, date_applied,
//     start_payment, currency }], meta: { page, per_page, total } }
// `meta` carries NO total_pages. Dates arrive as space-separated datetimes
// ("2026-08-20 10:01:38"), which `toDate` already handles.

/** Half a centavo. Every server money field is `round(x, 2)`, so below this is zero. */
const LOAN_MONEY_EPSILON = 0.005;

/**
 * Status keys that mean "the loan was granted". The canonical set the API emits
 * is draft|pending|approved|cancelled (`get_loan_stage()` hard-codes it and
 * defaults anything else to `draft`), but migration 20260721185713 folded the
 * older vocabulary — ongoing, completed, delinquent, restructured — into
 * `approved`, and `docs/openapi.json` still ships a stale example showing
 * `status_key: "ongoing"`. Keeping the legacy words here means an un-migrated
 * tenant row still reads correctly.
 *
 * `completed` sits in this set deliberately: for ANY granted loan the BALANCE
 * decides whether it is still running, never the word. The migration says so
 * itself — a fully paid loan "stays approved but is excluded from further
 * deduction by the outstanding-balance filter".
 */
const LOAN_APPROVED_KEYS = new Set([
  'approved', 'ongoing', 'active', 'completed', 'delinquent', 'delinquency', 'restructured',
]);

export type LoanLifecycle = 'active' | 'completed' | 'pending' | 'cancelled';

/**
 * The lifecycle the app shows, derived from the raw status plus the balance.
 *
 * There is no "active" or "completed" STATUS on the server — only the four
 * canonical keys — so this is where the user-facing vocabulary comes from.
 * Filter on `status_key`; display `status`.
 *
 * There is deliberately no `unknown` member: an unrecognised or empty key falls
 * to `pending`, mirroring the server's own `draft` default. That guarantees the
 * humanised label the API sends and the lifecycle the app derives can never
 * contradict each other — and an `unknown` bucket would make the loan vanish
 * from every segment except "All", which is silent data loss.
 */
export function loanLifecycleOf(statusKey: unknown, currentBalance: number): LoanLifecycle {
  const key = String(statusKey ?? '').trim().toLowerCase();
  if (key === 'cancelled' || key === 'canceled' || key === 'rejected' || key === 'declined') {
    return 'cancelled';
  }
  if (LOAN_APPROVED_KEYS.has(key)) {
    return currentBalance >= LOAN_MONEY_EPSILON ? 'active' : 'completed';
  }
  return 'pending';
}

export const LOAN_LIFECYCLE_LABEL: Record<LoanLifecycle, string> = {
  active: 'Active',
  pending: 'Pending',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const clamp01 = (n: number): number => (Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0);

/**
 * Normalise the per-loan currency. The API sources it from
 * `get_setting('default_currency_symbol') ?: 'PHP'`, so a tenant row holding a
 * SYMBOL ("₱") rather than an ISO code is possible — and `formatMoney` hands it
 * to `Intl.NumberFormat({ style: 'currency' })`, which throws on a non-ISO value
 * and falls back to a bare number with NO currency sign at all. `salaryMoney`
 * (above) already solves exactly this, so loans route every figure through it.
 */
function loanCurrency(rec: Json, fallback = 'PHP'): string {
  const raw = str(rec, ['currency'], '').trim();
  return raw || fallback;
}

export interface LoanVM {
  id: string;
  loanId: number;
  /** `category` comes from a LEFT JOIN and can be empty. */
  category: string;
  /** Humanised status straight from the server. */
  status: string;
  /** Raw key — the thing to branch on. */
  statusKey: string;
  lifecycle: LoanLifecycle;
  lifecycleLabel: string;
  currency: string;
  principal: number;
  principalLabel: string;
  balance: number;
  balanceLabel: string;
  /** NOTE: includes interest, unlike `balance` which is principal-only. */
  totalPaid: number;
  totalPaidLabel: string;
  totalFees: number;
  totalFeesLabel: string;
  monthlyPayment: number;
  monthlyPaymentLabel: string;
  minPayment: number;
  minPaymentLabel: string;
  interestRate: number;
  interestRateLabel: string;
  monthsToPay: number;
  termLabel: string;
  payrollBinding: string;
  payrollBindingLabel: string;
  appliedLabel: string;
  appliedAtMs: number;
  startPaymentLabel: string;
  /**
   * Fraction of PRINCIPAL repaid, 0..1. Principal-denominated on purpose:
   * `current_balance` excludes interest while `total_paid` includes it, so the
   * two are not comparable and must never be subtracted from one another.
   */
  progress: number;
  progressPercent: number;
  /**
   * True once some principal has actually been repaid (`balance < principal`).
   *
   * When false the loan has had NOTHING repaid — but this payload cannot say
   * which of the two reasons applies, because both report `balance ===
   * principal`: payroll has not generated an amortization schedule yet, or the
   * schedule exists and no period has been paid. Verified live: loan #126 reads
   * as "not known" while having a real 2-period schedule. So callers may use
   * this to soften wording, and must NOT use it to claim a schedule is missing
   * — only the detail page, which loads `/schedule`, can tell them apart.
   */
  progressKnown: boolean;
}

export interface LoanDetailVM extends LoanVM {
  numberOfPayments: number;
  remarks: string;
}

const LOAN_BINDING_LABEL: Record<string, string> = {
  none: 'Not deducted from payroll',
  monthly: 'Monthly payroll deduction',
  semi_monthly: 'Semi-monthly payroll deduction',
  weekly: 'Weekly payroll deduction',
  bi_weekly: 'Bi-weekly payroll deduction',
};

/** Map one `loans[]` row. Shared by the list and the detail. */
export function mapLoan(value: unknown, index = 0): LoanVM {
  const r = asRecord(value);
  const currency = loanCurrency(r);
  const principal = numOf(r, ['principal_amount'], 0);
  const balance = Math.max(0, numOf(r, ['current_balance'], 0));
  const statusKey = str(r, ['status_key'], '').trim().toLowerCase();
  const lifecycle = loanLifecycleOf(statusKey, balance);
  const binding = str(r, ['payroll_binding'], '').trim().toLowerCase();
  const months = numOf(r, ['months_to_pay'], 0);
  const rate = numOf(r, ['interest_rate'], 0);

  // "Some principal has been repaid." A loan with no generated schedule and a
  // loan whose schedule has no paid periods BOTH report balance === principal,
  // so this cannot distinguish them — see the field's docblock.
  const progressKnown = principal > 0 && balance < principal - LOAN_MONEY_EPSILON;
  const progress = principal > 0 ? clamp01((principal - balance) / principal) : 0;

  return {
    id: idOf(r, index),
    loanId: numOf(r, ['id'], 0),
    category: str(r, ['category'], '').trim() || 'Loan',
    status: titleCase(str(r, ['status'], '')),
    statusKey,
    lifecycle,
    lifecycleLabel: LOAN_LIFECYCLE_LABEL[lifecycle],
    currency,
    principal,
    principalLabel: salaryMoney(principal, currency),
    balance,
    balanceLabel: salaryMoney(balance, currency),
    totalPaid: numOf(r, ['total_paid'], 0),
    totalPaidLabel: salaryMoney(numOf(r, ['total_paid'], 0), currency),
    totalFees: numOf(r, ['total_fees'], 0),
    totalFeesLabel: salaryMoney(numOf(r, ['total_fees'], 0), currency),
    monthlyPayment: numOf(r, ['monthly_payment'], 0),
    monthlyPaymentLabel: salaryMoney(numOf(r, ['monthly_payment'], 0), currency),
    minPayment: numOf(r, ['min_payment'], 0),
    minPaymentLabel: salaryMoney(numOf(r, ['min_payment'], 0), currency),
    interestRate: rate,
    interestRateLabel: rate > 0 ? `${rate}%` : 'No interest',
    monthsToPay: months,
    termLabel: months > 0 ? `${months} ${months === 1 ? 'month' : 'months'}` : '',
    payrollBinding: binding,
    payrollBindingLabel: LOAN_BINDING_LABEL[binding] ?? (binding ? titleCase(binding) : ''),
    appliedLabel: formatDate(pick<unknown>(r, ['date_applied'], null)),
    appliedAtMs: toMillis(pick<unknown>(r, ['date_applied'], null)),
    startPaymentLabel: formatDate(pick<unknown>(r, ['start_payment'], null)),
    progress,
    progressPercent: Math.round(progress * 100),
    progressKnown,
  };
}

/**
 * Map the `{ loans: [...] }` list, newest first.
 *
 * The sort is not cosmetic: `Loans_model::get_details()` ends at `GROUP BY
 * loans.id` with NO `ORDER BY` on the non-DataTables path, so the server's
 * "newest first" is not actually guaranteed.
 */
export function mapLoanList(data: unknown): LoanVM[] {
  return toList(data)
    .map((l, i) => mapLoan(l, i))
    .sort((a, b) => b.appliedAtMs - a.appliedAtMs || b.loanId - a.loanId);
}

/** Map the `{ loan: {...} }` detail payload. */
export function mapLoanDetail(data: unknown): LoanDetailVM {
  const r = domainBody(data, ['loan']);
  return {
    ...mapLoan(r),
    numberOfPayments: numOf(r, ['number_of_payments'], 0),
    remarks: str(r, ['remarks'], '').trim(),
  };
}

// -- Payments ---------------------------------------------------------------

const LOAN_PAYMENT_METHOD_LABEL: Record<string, string> = {
  payroll: 'Payroll deduction',
  cash: 'Cash',
  bank_transfer: 'Bank transfer',
  check: 'Check',
  other: 'Other',
};

const LOAN_PAYMENT_SOURCE_LABEL: Record<string, string> = {
  manual: 'Recorded manually',
  payroll: 'From payroll',
  import: 'Imported',
};

export interface LoanPaymentVM {
  id: string;
  dateLabel: string;
  paidAtMs: number;
  amount: number;
  amountLabel: string;
  lateInterest: number;
  lateInterestLabel: string;
  methodLabel: string;
  sourceLabel: string;
  remarks: string;
}

export interface LoanPaymentsVM {
  currency: string;
  /**
   * Sum of every payment on the loan. This can legitimately differ from the
   * list row's `total_paid`, which counts only payments linked to an
   * amortization period (`amortization_id IS NOT NULL`) — so the two are
   * labelled distinctly in the UI and never asserted equal.
   */
  totalPaid: number;
  totalPaidLabel: string;
  rows: LoanPaymentVM[];
}

/** Map `{ loan_id, payments: [...], total_paid, currency }`. */
export function mapLoanPayments(data: unknown): LoanPaymentsVM {
  const rec = asRecord(data);
  const currency = loanCurrency(rec);
  const total = numOf(rec, ['total_paid'], 0);
  const rows: LoanPaymentVM[] = toList(rec.payments ?? rec).map((p, i) => {
    const r = asRecord(p);
    const method = str(r, ['payment_method'], '').trim().toLowerCase();
    const source = str(r, ['source'], '').trim().toLowerCase();
    const late = numOf(r, ['late_interest'], 0);
    return {
      id: idOf(r, i),
      dateLabel: formatDate(pick<unknown>(r, ['date_paid'], null)),
      paidAtMs: toMillis(pick<unknown>(r, ['date_paid'], null)),
      amount: numOf(r, ['amount'], 0),
      amountLabel: salaryMoney(numOf(r, ['amount'], 0), currency),
      lateInterest: late,
      lateInterestLabel: late >= LOAN_MONEY_EPSILON ? salaryMoney(late, currency) : '',
      // Keep whatever the tenant actually wrote when it is outside the known
      // vocabulary — a tenant writing "gcash" should read "Gcash", not "Other".
      methodLabel: LOAN_PAYMENT_METHOD_LABEL[method] ?? (method ? titleCase(method) : ''),
      sourceLabel: LOAN_PAYMENT_SOURCE_LABEL[source] ?? (source ? titleCase(source) : ''),
      remarks: str(r, ['remarks'], '').trim(),
    };
  });
  rows.sort((a, b) => b.paidAtMs - a.paidAtMs);
  return { currency, totalPaid: total, totalPaidLabel: salaryMoney(total, currency), rows };
}

// -- Amortization schedule --------------------------------------------------

export interface LoanSchedulePeriodVM {
  key: string;
  periodNumber: number;
  dueLabel: string;
  dueAtMs: number;
  paymentLabel: string;
  principalLabel: string;
  interestLabel: string;
  endingBalance: number;
  endingBalanceLabel: string;
  isPaid: boolean;
  paidLabel: string;
  /** Unpaid and past due. */
  isOverdue: boolean;
}

export interface LoanScheduleVM {
  currency: string;
  periodsTotal: number;
  periodsPaid: number;
  totalPaymentLabel: string;
  totalPrincipalLabel: string;
  totalInterestLabel: string;
  /** Fraction of periods settled, 0..1. */
  progress: number;
  progressPercent: number;
  /**
   * Money still owed after the final period — non-zero when the loan carries a
   * `min_payment_override` (a pinned payment that does not fully amortise), so
   * the schedule "finishes" while the borrower still owes.
   */
  residual: number;
  residualLabel: string;
  rows: LoanSchedulePeriodVM[];
}

/**
 * Map `{ loan_id, schedule: [...], summary: {...} }`.
 *
 * Two things this must get right:
 *
 * 1. The rows can carry DUPLICATE `period_number`s.
 *    `Loan_amortization_model::get_details()` LEFT-JOINs `loan_payments` with no
 *    `GROUP BY`, and the backend documents the consequence in its own
 *    `get_schedule_summary()`: "a period may carry more than one payment row,
 *    and joining would repeat that period, inflating COUNT(*) and every SUM()".
 *    So the array is deduped here, and the counts are read from `summary` only —
 *    never from `rows.length`.
 *
 * 2. The currency lives in `summary`, NOT at the root (unlike `/payments`,
 *    which does expose a root `currency`). Reading the root would silently
 *    render every schedule figure in PHP regardless of the tenant setting.
 */
export function mapLoanSchedule(data: unknown, fallbackCurrency = 'PHP'): LoanScheduleVM {
  const rec = asRecord(data);
  const summary = asRecord(rec.summary);
  const currency = loanCurrency(summary, fallbackCurrency);

  const periodsTotal = numOf(summary, ['periods_total'], 0);
  const periodsPaid = numOf(summary, ['periods_paid'], 0);
  const todayMs = Date.now();

  const seen = new Map<string, LoanSchedulePeriodVM>();
  toList(rec.schedule ?? rec).forEach((s, i) => {
    const r = asRecord(s);
    const periodNumber = numOf(r, ['period_number'], 0);
    const key = periodNumber > 0 ? `p${periodNumber}` : `i${i}`;
    const isPaid = bool(r, ['is_paid'], false);
    const paidLabel = formatDate(pick<unknown>(r, ['paid_date'], null));

    const existing = seen.get(key);
    if (existing) {
      // Same period repeated by the join: keep the first row but let any of its
      // duplicates establish paid-ness.
      existing.isPaid = existing.isPaid || isPaid;
      if (!existing.paidLabel && paidLabel) existing.paidLabel = paidLabel;
      existing.isOverdue = !existing.isPaid && existing.dueAtMs > 0 && existing.dueAtMs < todayMs;
      return;
    }

    const dueAtMs = toMillis(pick<unknown>(r, ['due_date'], null));
    const ending = numOf(r, ['ending_balance'], 0);
    seen.set(key, {
      key,
      periodNumber: periodNumber > 0 ? periodNumber : i + 1,
      dueLabel: formatDate(pick<unknown>(r, ['due_date'], null)),
      dueAtMs,
      paymentLabel: salaryMoney(numOf(r, ['payment_amount'], 0), currency),
      principalLabel: salaryMoney(numOf(r, ['principal_amount'], 0), currency),
      interestLabel: salaryMoney(numOf(r, ['interest_amount'], 0), currency),
      endingBalance: ending,
      endingBalanceLabel: salaryMoney(ending, currency),
      isPaid,
      paidLabel,
      isOverdue: !isPaid && dueAtMs > 0 && dueAtMs < todayMs,
    });
  });

  const rows = [...seen.values()].sort((a, b) => a.periodNumber - b.periodNumber);
  const last = rows.length ? rows[rows.length - 1] : undefined;
  const residual = last && last.endingBalance >= LOAN_MONEY_EPSILON ? last.endingBalance : 0;
  const progress = periodsTotal > 0 ? clamp01(periodsPaid / periodsTotal) : 0;

  return {
    currency,
    periodsTotal,
    periodsPaid,
    totalPaymentLabel: salaryMoney(numOf(summary, ['total_payment'], 0), currency),
    totalPrincipalLabel: salaryMoney(numOf(summary, ['total_principal'], 0), currency),
    totalInterestLabel: salaryMoney(numOf(summary, ['total_interest'], 0), currency),
    progress,
    progressPercent: Math.round(progress * 100),
    residual,
    residualLabel: residual > 0 ? salaryMoney(residual, currency) : '',
    rows,
  };
}

// ── Leaves (Leave / Requests pages) ──────────────────────────────────────────
// Verified: { leaves: [{ id, leave_type_title, leave_type_color, status, reason,
//   start_date, end_date, total_days, total_hours, created_at, ... }], meta }.
/**
 * A leave application's state. `cancelled` is the APPLICANT's own withdrawal
 * and is deliberately separate from `rejected` (a decision made against them):
 * the two read differently to the person who filed the request, and the Leaves
 * page filters them apart.
 */
export type LeaveStatusVariant = 'approved' | 'pending' | 'for_review' | 'rejected' | 'cancelled';

/**
 * Still awaiting a decision — `pending` (nobody has acted) or `for_review` (a first
 * approver endorsed it and a final approver has yet to sign off).
 *
 * Exists so the "undecided" idea has ONE name. Before `for_review` was its own variant
 * it fell through `leaveStatusVariant()`'s catch-all to `pending`, which meant a dozen
 * `=== 'pending'` tests were quietly doing the right thing for the wrong reason. Widening
 * the union makes each of those a behaviour change the compiler CANNOT see — it only
 * flags exhaustive `Record<LeaveStatusVariant, …>` maps — so every one of them now calls
 * this instead. The withdraw button is the sharpest case: the server still allows
 * cancelling an endorsed leave, so a bare `=== 'pending'` there would take the control
 * away for a state the backend explicitly supports.
 */
export function isLeaveAwaitingDecision(variant: LeaveStatusVariant): boolean {
  return variant === 'pending' || variant === 'for_review';
}
export interface LeaveRequestVM {
  id: string;
  type: string;
  typeColor: string;
  status: string;
  statusVariant: LeaveStatusVariant;
  /** Who gave the FIRST approval, when dual approval endorsed this ('' otherwise). */
  endorsedByName: string;
  /** When it was endorsed, e.g. "Sep 13, 2026" ('' when it was not). */
  endorsedAtLabel: string;
  reason: string;
  startDate: string;
  endDate: string;
  dateRangeLabel: string;
  totalDays: number;
  /** `total_hours` as ERPat stored it (0 when absent). */
  totalHours: number;
  /** One date booked by the hour ({@link isHourlyLeave}) — say it in hours, not "0.5 days". */
  isHourly: boolean;
  /**
   * How many supporting documents the leave carries (`attachment_count`; 0 when
   * absent — a server older than HumanResource 1.95.0). The ONE home of the
   * count: the detail payload's list is what can be opened, this is what exists.
   */
  attachmentCount: number;
  createdAt: string;
  createdAtLabel: string;
  createdTimeLabel: string;
  month: string;
  day: string;
  weekday: string;
}

/**
 * Exported so the Calendar's own leave mapper resolves a status through the
 * SAME vocabulary the Leaves page uses. A second copy would drift, and the two
 * surfaces would then disagree about whether the same application is cancelled
 * or rejected.
 */
export function leaveStatusVariant(status: string): LeaveStatusVariant {
  const s = status.toLowerCase().trim().replace(/[\s-]+/g, '_');
  // FIRST, and before the `approv` test: under dual approval a first approver's
  // "approve" writes `for_review`, and the applicant must be told their request moved to
  // a second approver rather than that it was approved. Matched on the same two spellings
  // the approver surface accepts (`normalizeTeamLeaveStatus`), so the two vocabularies
  // cannot drift.
  if (s.startsWith('for_review') || s.startsWith('endors')) return 'for_review';
  if (s.startsWith('approv')) return 'approved';
  // Tested BEFORE the rejection spellings, and `cancel` covers the API's
  // single-l "canceled" as well as "cancelled".
  if (s.startsWith('cancel')) return 'cancelled';
  if (s.startsWith('reject') || s.startsWith('declin')) return 'rejected';
  return 'pending';
}

/**
 * How a LEAVE STATE is named to the person who filed it.
 *
 * Only `for_review` is overridden. The approver surface calls that state "Endorsed",
 * which is the approver's word for their own action; to the employee waiting on it the
 * useful fact is that a second signature is outstanding. Every other state title-cases
 * fine straight from the API.
 */
const APPLICANT_LEAVE_STATUS_LABELS: Partial<Record<LeaveStatusVariant, string>> = {
  for_review: 'Pending final approval',
};

export function mapLeaveRequests(data: unknown): LeaveRequestVM[] {
  return toList(data).map((l, i) => mapLeaveRequestRow(l, i));
}

/**
 * One `/me/leaves` row. Shared by the list and {@link mapLeaveDetail} so the
 * detail and the list read a leave identically — and so the detail maps its
 * single row without indexing a one-element array.
 */
function mapLeaveRequestRow(l: unknown, i: number): LeaveRequestVM {
  const r = asRecord(l);
  const start = str(r, ['start_date'], '');
  const end = str(r, ['end_date'], '');
  const days = pick<number>(r, ['total_days'], 0);
  const hoursRaw = Number(pick<unknown>(r, ['total_hours'], 0));
  const totalHours = Number.isFinite(hoursRaw) && hoursRaw > 0 ? hoursRaw : 0;
  const isHourly = isHourlyLeave(start, end, Number(days), totalHours);
  const created = pick<unknown>(r, ['created_at', 'date_created', 'submitted_at'], null);
  const d = toDateSafe(start);
  const rangeBase = start === end ? formatDate(start) : `${formatDate(start)} – ${formatDate(end)}`;
  const variant = leaveStatusVariant(str(r, ['status'], ''));
  return {
    id: idOf(r, i),
    type: str(r, ['leave_type_title', 'leave_type', 'type'], 'Leave'),
    typeColor: str(r, ['leave_type_color', 'color'], ''),
    status: APPLICANT_LEAVE_STATUS_LABELS[variant] ?? titleCase(str(r, ['status'], '')),
    statusVariant: variant,
    // Shipped by Leaves_euapi::shape_application(). Absent on an older deployment, in
    // which case the endorsement step simply does not render.
    endorsedByName: str(r, ['first_checker_name'], ''),
    endorsedAtLabel: formatDate(pick<unknown>(r, ['first_checked_at'], null)),
    reason: str(r, ['reason'], ''),
    startDate: start,
    endDate: end,
    dateRangeLabel: `${rangeBase}${leaveSpanSuffix(start, end, Number(days), totalHours)}`,
    totalDays: days,
    totalHours,
    isHourly,
    attachmentCount: leaveAttachmentCountOf(r),
    createdAt: str(r, ['created_at', 'date_created', 'submitted_at'], ''),
    createdAtLabel: formatDate(created),
    createdTimeLabel: formatTime(created),
    month: d ? d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase() : '',
    day: d ? String(d.getDate()).padStart(2, '0') : '',
    weekday: d ? d.toLocaleDateString('en-US', { weekday: 'short' }) : '',
  };
}

// ── Leave supporting documents (HumanResource ≥ 1.95.0) ──────────────────────
// Verified against HumanResource 1.95.0:
//   GET  me/leaves/{id}                          → { leave, attachments: [Attachment], attachment_capabilities }
//   GET  leaves/{id} · leaves/{id}/details       → the same, with `attachments` ABSENT when the caller may only see the count
//   GET  {me/}leaves/{id}/attachments/{file_id}  → { file: Link }   (a fresh link per open — never cached or stored)
// Bytes are never in a list or detail payload; every list row carries `attachment_count`.

/**
 * A supporting document on a leave. Carries no URL: a document opens through a
 * per-request link ({@link mapLeaveAttachmentLink}), asked for on every open.
 */
export interface LeaveAttachmentVM {
  /** The server's attachment id — what `…/attachments/{file_id}` addresses. Key rows by THIS, never by `name`. */
  id: string;
  /** May differ from the picked file's name after filing: S3 re-encodes images (`.webp`). */
  name: string;
  /** Lower-case, no dot; `''` when unknown. */
  extension: string;
  /** 0 = unknown. */
  sizeBytes: number;
  /** "177.8 KB", or `''` when the size is unknown — never "0 B". */
  sizeLabel: string;
  isImage: boolean;
  /** The server's verdict when it gave one; otherwise image-or-PDF (what the viewer can show). */
  previewable: boolean;
  storage: 's3' | 'local';
  /** The uploader's user id as a STRING, so it compares with the user store's id. */
  uploadedBy: string;
  /** `''` when the server could not read the name back — omit it, never invent one. */
  uploadedByName: string;
  /** The raw `uploaded_at` stamp (naive UTC), or `''`. */
  uploadedAt: string;
  /** e.g. "Oct 28, 2026" in the device's zone; `''` when unknown. */
  uploadedAtLabel: string;
  /** e.g. "11:12 AM" in the device's zone; `''` when unknown. */
  uploadedTimeLabel: string;
}

/**
 * The server's per-leave verdict on what THIS caller may do to its documents.
 * Used as sent: `canAdd` already folds in the five-document cap and the
 * decided state, so the app never re-derives the cap from a list length. The
 * wire carries no `undecided` flag.
 */
export interface LeaveAttachmentCapabilitiesVM {
  canView: boolean;
  canAdd: boolean;
  /** May remove the documents THEY uploaded (compare `uploadedBy` with your id). */
  canRemoveOwn: boolean;
  /** May remove any document on the leave (a first approver while it is undecided). */
  canRemoveAny: boolean;
  /** Documents that can still be added (0 at the cap). */
  remaining: number;
  maxFiles: number;
}

/**
 * One answer of `GET …/attachments/{file_id}[?download=1]`. Local storage
 * answers `{ url: null, expires_in: 0, web_only: true, disposition: '' }`: there
 * is nothing to open here, and the document opens in ERPat on the web. Use the
 * URL for the one open that asked for it — never keep, cache, persist or log it.
 */
export interface LeaveAttachmentLinkVM {
  /** A signed URL valid for `expiresIn` seconds, or `null` — a valid answer, never `''`. */
  url: string | null;
  expiresIn: number;
  webOnly: boolean;
  disposition: 'inline' | 'attachment' | '';
}

/**
 * `GET /v1/api/me/leaves/{id}`. The self route ALWAYS sends `attachments`
 * (maybe `[]`), so a missing key reads `[]`; the count lives on
 * `request.attachmentCount` (one home).
 */
export interface LeaveDetailVM {
  request: LeaveRequestVM;
  attachments: LeaveAttachmentVM[];
  capabilities: LeaveAttachmentCapabilitiesVM | null;
}

/** A row's `attachment_count` — through `numOf`, since the API sends some counts as strings ("2"). */
function leaveAttachmentCountOf(rec: Json): number {
  return nonNegativeCount(numOf(rec, ['attachment_count'], 0));
}

/** One Attachment. `index` names a row that carries no id (it never should). */
export function mapLeaveAttachment(raw: unknown, index = 0): LeaveAttachmentVM {
  const rec = asRecord(raw);
  const name = str(rec, ['name', 'file_name', 'original_name'], '').trim() || `Document ${index + 1}`;
  const extension = (str(rec, ['extension', 'ext'], '').trim().replace(/^\.+/, '') || extensionOf(name)).toLowerCase();
  const sizeBytes = nonNegativeCount(numOf(rec, ['size', 'file_size', 'size_bytes'], 0));
  const declaresImage = rec.is_image !== undefined && rec.is_image !== null;
  const isImage = declaresImage ? bool(rec, ['is_image']) : isImageExtension(extension);
  const declaresPreviewable = rec.previewable !== undefined && rec.previewable !== null;
  // The uploader is documented as a bare id; read an `{ id, name }` object too
  // rather than ever printing "[object Object]".
  const uploader = rec.uploaded_by !== null && typeof rec.uploaded_by === 'object' ? asRecord(rec.uploaded_by) : null;
  // `uploaded_at` is a naive UTC stamp: read as the instant it names (the
  // `activityStampMs` convention), so the labels are right in the DEVICE's zone;
  // `uploaded_at_local` read as device-local is the fallback, then any zoned stamp.
  const uploadedMs = activityStampMs(rec.uploaded_at, rec.uploaded_at_local) || toMillis(rec.uploaded_at);
  return {
    id: idOf(rec, index),
    name,
    extension,
    sizeBytes,
    sizeLabel: formatBytes(sizeBytes),
    isImage,
    previewable: declaresPreviewable ? bool(rec, ['previewable']) : isImage || extension === 'pdf',
    storage: str(rec, ['storage'], '').trim().toLowerCase() === 'local' ? 'local' : 's3',
    uploadedBy: (uploader ? str(uploader, ['id', 'user_id'], '') : str(rec, ['uploaded_by'], '')).trim(),
    uploadedByName: str(rec, ['uploaded_by_name'], '').trim() || (uploader ? str(uploader, ['name', 'full_name'], '').trim() : ''),
    uploadedAt: str(rec, ['uploaded_at'], ''),
    uploadedAtLabel: uploadedMs > 0 ? formatDate(new Date(uploadedMs)) : '',
    uploadedTimeLabel: uploadedMs > 0 ? formatTime(new Date(uploadedMs)) : '',
  };
}

/** A list of Attachments; `[]` for no list. Entries that are not objects are skipped. */
export function mapLeaveAttachments(raw: unknown): LeaveAttachmentVM[] {
  return toList<unknown>(raw)
    .filter((row) => row !== null && typeof row === 'object' && !Array.isArray(row))
    .map((row, i) => mapLeaveAttachment(row, i));
}

/** The `attachment_capabilities` block; `null` when absent or not an object. A stray `undecided` is ignored. */
export function mapLeaveAttachmentCapabilities(raw: unknown): LeaveAttachmentCapabilitiesVM | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = asRecord(raw);
  return {
    canView: bool(r, ['can_view']),
    canAdd: bool(r, ['can_add']),
    canRemoveOwn: bool(r, ['can_remove_own']),
    canRemoveAny: bool(r, ['can_remove_any']),
    remaining: nonNegativeCount(numOf(r, ['remaining'], 0)),
    maxFiles: nonNegativeCount(numOf(r, ['max_files'], 5)),
  };
}

/**
 * `GET …/attachments/{file_id}` → `{ file: Link }`. Unlike
 * {@link mapAdvisoryFileLink}, a missing, `null` or empty URL stays `null` —
 * "nothing to open" — and never collapses to `''`, which a caller could hand a
 * browser as a blank tab.
 */
export function mapLeaveAttachmentLink(data: unknown): LeaveAttachmentLinkVM {
  const rec = domainBody(data, ['file']);
  const disposition = str(rec, ['disposition'], '').trim().toLowerCase();
  return {
    url: uploadedFileUrl(pick<unknown>(rec, ['url'], null)) || null,
    expiresIn: nonNegativeCount(numOf(rec, ['expires_in'], 0)),
    webOnly: bool(rec, ['web_only']),
    disposition: disposition === 'inline' || disposition === 'attachment' ? disposition : '',
  };
}

/**
 * The documents that ride BESIDE a leave (`{ leave, attachments, … }`) — read
 * from the envelope first (`domainBody` unwraps past the sibling), then from
 * the record in case a deployment nests them there. `null` when neither carries
 * a list: the team routes omit the key when the caller may only see the count.
 */
function leaveAttachmentsBeside(outer: Json, record: Json): LeaveAttachmentVM[] | null {
  if (Array.isArray(outer.attachments)) return mapLeaveAttachments(outer.attachments);
  if (Array.isArray(record.attachments)) return mapLeaveAttachments(record.attachments);
  return null;
}

/** The `attachment_capabilities` beside a leave — the envelope first, then the record. */
function leaveAttachmentCapabilitiesBeside(outer: Json, record: Json): LeaveAttachmentCapabilitiesVM | null {
  return (
    mapLeaveAttachmentCapabilities(outer.attachment_capabilities) ??
    mapLeaveAttachmentCapabilities(record.attachment_capabilities)
  );
}

/** Map `GET /v1/api/me/leaves/{id}` → `{ leave, attachments, attachment_capabilities }` (or the row flat). */
export function mapLeaveDetail(data: unknown): LeaveDetailVM {
  const outer = asRecord(data);
  const leave = domainBody(data, ['leave']);
  return {
    request: mapLeaveRequestRow(leave, 0),
    attachments: leaveAttachmentsBeside(outer, leave) ?? [],
    capabilities: leaveAttachmentCapabilitiesBeside(outer, leave),
  };
}

// ── Team leaves (manager approval surface) ───────────────────────────────────
/**
 * A leave application's lifecycle state. `unset` absorbs an empty or unknown
 * spelling so a badge never renders blank, exactly as the attendance vocabulary
 * does — the two approval surfaces stay readable in the same way.
 */
export type TeamLeaveStatus =
  | 'pending'
  | 'approved'
  /**
   * Endorsed by a first approver, waiting on a final one. Leave has a
   * dual-approval workflow attendance does not: when the tenant's
   * `leave_dual_approval` setting is on, a first approver's "approved" writes
   * THIS and books no credit; only a final approver writes `approved`.
   */
  | 'for_review'
  | 'rejected'
  | 'cancelled'
  | 'unset';

/**
 * The statuses the approval endpoint ACCEPTS. Note there is no `pending`:
 * unlike attendance, leave has no revert — an approved leave has already
 * written a credit-deduction row — and sending it returns 422.
 */
export type TeamLeaveAction = 'approved' | 'rejected';

/** What the caller may do to a row: write the decision, or endorse it onward. */
export type TeamLeaveApproveAction = 'approve' | 'endorse';

/** Why the server refuses this caller this row ('' when it didn't say). */
export type TeamLeaveBlockReason =
  | 'already_decided'
  | 'awaiting_final_approval'
  | 'no_permission'
  | 'self_approval_denied'
  | '';

/**
 * The server's per-row verdict (`can_approve`) on whether THIS caller may act.
 *
 * Syntry holds no permit map, so this is the only thing that can distinguish a
 * row you may decide from one you may only read — and under dual approval it is
 * also what tells the button whether to say Approve or Endorse.
 */
export interface TeamLeaveApproval {
  allowed: boolean;
  action: TeamLeaveApproveAction | null;
  reason: TeamLeaveBlockReason;
  /**
   * The server says this caller's approve would be the FINAL-APPROVER BYPASS:
   * finalizing a leave no first approver has endorsed yet. `null` when the
   * payload predates the flag — see {@link leaveApprovalSkipsEndorsement}.
   */
  bypassesEndorsement: boolean | null;
}

const BLOCK_REASON_LABELS: Record<Exclude<TeamLeaveBlockReason, ''>, string> = {
  already_decided: 'Already decided',
  awaiting_final_approval: 'Waiting on a final approver',
  no_permission: 'You can review this, but not decide it',
  self_approval_denied: 'You can’t approve your own leave',
};

/** Human sentence for a refusal, or '' when the server gave no reason. */
export function teamLeaveBlockLabel(reason: TeamLeaveBlockReason): string {
  return reason ? BLOCK_REASON_LABELS[reason] : '';
}

/**
 * Would approving this application skip the endorsement stage?
 *
 * Under dual approval a final approver may approve a leave nobody has endorsed
 * yet. The server allows it on purpose, so this is NOT an authorization test —
 * it only decides whether the approver is asked to confirm first.
 *
 * The server's `can_approve.bypasses_endorsement` wins whenever it is present.
 * The fallback exists only for a backend that predates the flag, and derives the
 * same answer from what the row and the queue already say: an "approve" (not an
 * "endorse") on a still-pending row, while the tenant runs two stages.
 *
 * `meta` is typed structurally rather than as `TeamLeavesMeta`, which lives in
 * `services/erpat.ts` — that module imports this one.
 */
export function leaveApprovalSkipsEndorsement(
  application: TeamLeaveVM,
  meta: { dualApprovalEnabled: boolean },
): boolean {
  const { approval } = application;
  if (approval.bypassesEndorsement !== null) return approval.bypassesEndorsement;
  return (
    approval.allowed &&
    approval.action === 'approve' &&
    application.status === 'pending' &&
    meta.dualApprovalEnabled &&
    !application.endorsedByName
  );
}

export interface TeamLeaveVM {
  id: string;
  /** The applicant's USER id — lets a surface resolve a photo from the directory. */
  userId: string;
  employeeName: string;
  /** Absolute avatar URL, or '' — render with the initials fallback. */
  employeeImage: string;
  employeeInitials: string;
  jobTitle: string;
  departmentName: string;
  /** Leave type label, e.g. "Vacation Leave". */
  type: string;
  /** Tenant-configured hex for the type, or '' when the payload omits it. */
  typeColor: string;
  status: TeamLeaveStatus;
  statusLabel: string;
  reason: string;
  startDate: string;
  endDate: string;
  /** e.g. "Jul 01, 2026 – Jul 03, 2026 (3 days)". */
  dateRangeLabel: string;
  /** The same range WITHOUT the day count, for surfaces that show duration
   *  separately and would otherwise print it twice. */
  dateRangePlain: string;
  totalDays: number;
  totalHours: number;
  requiredCredits: number;
  isPaid: boolean;
  /** Local calendar date (YYYY-MM-DD) the leave starts — used to group by day. */
  dateKey: string;
  /** Human day label for the group header, e.g. "Jul 01, 2026". */
  dateLabel: string;
  /** Epoch ms for sorting (local-date based; 0 when unknown). */
  dateMillis: number;
  /** When the application was filed, e.g. "Jun 15, 2026" ('' when unknown). */
  createdAtLabel: string;
  /** Approver's display name ('' when not yet checked). */
  checkedByName: string;
  /**
   * Who gave the FIRST approval, under dual approval ('' when there was none).
   *
   * Kept meaningful after the final decision too — by then it is the audit trail,
   * which is exactly how the ERPat web modal treats its "Endorsed by" block.
   */
  endorsedByName: string;
  /** When it was endorsed, e.g. "Sep 13, 2026" ('' when it was not). */
  endorsedAtLabel: string;
  /** Server's verdict on what this caller may do to this row. */
  approval: TeamLeaveApproval;
  /**
   * The applicant's credit position, when the payload carries one. NULL today —
   * see {@link TeamLeaveCreditsVM} — and the detail modal hides the section.
   */
  credits: TeamLeaveCreditsVM | null;
  /** How many supporting documents the leave carries (`attachment_count`; 0 when absent). */
  attachmentCount: number;
  /**
   * The documents, when THIS caller may open them — `null` when the payload
   * withheld them (the team routes omit `attachments` for a caller who may only
   * see the count, and list rows never carry them): show the count-only note,
   * not an empty list. `[]` means "may view, none attached".
   */
  attachments: LeaveAttachmentVM[] | null;
  /** The server's verdict on what this caller may do to the documents; `null` when not sent. */
  attachmentCapabilities: LeaveAttachmentCapabilitiesVM | null;
  /**
   * True when a PATCH only ENDORSED the application rather than approving it
   * outright (`result.endorsed_only`) — dual approval booked no credit and it
   * now waits on a final approver. Always false on a list row.
   */
  endorsedOnly: boolean;
  /**
   * True when a PATCH approved the application WITHOUT an endorsement
   * (`result.bypassed_endorsement`) — a final approver skipped the first stage.
   * The server has recorded it in the audit log. Always false on a list row.
   */
  bypassedEndorsement: boolean;
}

const TEAM_LEAVE_STATUS_LABELS: Record<TeamLeaveStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  // "Endorsed" rather than the API's "for review": it pairs with the Endorse
  // action, and "For Review" would collide with the name of the tab this list
  // lives in.
  for_review: 'Endorsed',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  unset: 'No Status',
};

function normalizeTeamLeaveStatus(value: unknown): TeamLeaveStatus {
  const s = String(value ?? '').toLowerCase().trim().replace(/[\s-]+/g, '_');
  if (
    s === 'pending' || s === 'approved' || s === 'for_review' ||
    s === 'rejected' || s === 'cancelled'
  ) {
    return s;
  }
  // Spelling variants the backend has used across modules. `cancel` covers the
  // API's single-l "canceled" as well as "cancelled".
  if (s.startsWith('for_review') || s.startsWith('endors')) return 'for_review';
  if (s.startsWith('approv')) return 'approved';
  if (s.startsWith('reject') || s.startsWith('declin')) return 'rejected';
  if (s.startsWith('cancel')) return 'cancelled';
  if (s.startsWith('pend')) return 'pending';
  return 'unset';
}

/**
 * The applicant's leave-credit position, as shown on the ERPat web application
 * modal — the numbers an approver actually decides on.
 *
 * ⚠️ NOT AVAILABLE YET. Every credit route on the end-user API is `/me/`-scoped
 * (`/me/leave-credits`, `/me/leave-credits/balance/{year}`, `/me/leaves/balances`)
 * — the CALLER's own ledger, never the applicant's — and there is no
 * applicant-scoped equivalent (verified live: `/v1/api/leaves/{id}/credits`,
 * `/v1/api/leave-credits` and `/v1/api/users/{id}/leave-credits` all 404).
 *
 * The ask is for `GET /v1/api/leaves/{id}` to embed this block, since the
 * approval permit already authorises seeing that application. Until it does,
 * {@link mapTeamLeaveCredits} returns null and the modal hides the whole
 * section rather than rendering a wall of zeroes that look like real balances.
 */
export interface TeamLeaveCreditsVM {
  /** Calendar year the yearly figures cover, e.g. 2026. */
  year: number;
  /** Month label the monthly figures cover, e.g. "September". */
  monthLabel: string;
  /** That month as 1-12, or 0 when the payload didn't say. Marks where the
   *  year has actually reached, so the heatmap can tell "no leave taken" from
   *  "hasn't happened yet". */
  month: number;
  /** Usable Leave Credit — "Available for filing". */
  usable: number;
  /** Pending Request — "Leave type specific". */
  pending: number;
  /** Actual Balance — "Net credits left after usage". */
  actualBalance: number;
  /** Total Used — "Credits already deducted this year". */
  totalUsed: number;
  /** Pro-rated Earning — "Annual grant / 12, or monthly earned". */
  proratedEarning: number;
  /** Used This Month — "Approved days taken". */
  usedThisMonth: number;
  /** Days taken per calendar month, Jan..Dec — the activity heatmap. */
  monthly: number[];
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Map the applicant's credit block, when the payload carries one, else null.
 *
 * The shape comes from `GET /v1/api/leaves/{id}/details`, which answers
 * `{ leave, credit_overview, monthly_activity }` — the figures are a SIBLING of
 * the application, not a field on it, because they are expensive enough that the
 * list and the PATCH response deliberately omit them. So this is called with the
 * OUTER envelope, and returns null for any payload without them (a list row, a
 * plain `/leaves/{id}`), which is what makes the modal hide both sections rather
 * than render a wall of zeroes that would read as "no leave left".
 *
 * `credit_overview` names its keys as the UI labels them, not as the ledger does
 * (`actual_balance` is the ledger's `total_balance`), so no translation is
 * needed here. The older `credits` spelling is still read first: it costs one
 * `pick` and keeps a deployment that embeds the block on the row working.
 */
export function mapTeamLeaveCredits(value: unknown): TeamLeaveCreditsVM | null {
  const outer = asRecord(value);
  const raw = pick<unknown>(
    outer,
    ['credit_overview', 'credits', 'leave_credits', 'credit_summary'],
    null,
  );
  if (raw == null) return null;
  const r = asRecord(raw);

  /**
   * The twelve-month strip.
   *
   * `monthly_activity` is a sibling ORDERED array of twelve `{ month, total_days }`
   * objects — ordered because a JSON object keyed "1".."12" is not order-safe
   * across clients and the strip is read left to right. It is indexed by each
   * entry's own `month` rather than by position, so a short or shuffled array
   * cannot silently shift September's bar onto August.
   */
  const activity = pick<unknown>(outer, ['monthly_activity'], null);
  if (Array.isArray(activity)) {
    const byMonth = new Array<number>(12).fill(0);
    activity.forEach((entry, i) => {
      const e = asRecord(entry);
      const m = numOf(e, ['month'], i + 1);
      if (m >= 1 && m <= 12) byMonth[m - 1] = numOf(e, ['total_days']);
    });
    const monthName = str(r, ['month_name', 'month_label'], '');
    const nameIdx = MONTH_NAMES.findIndex((n) => n.toLowerCase() === monthName.toLowerCase());
    return {
      year: numOf(r, ['year'], new Date().getFullYear()),
      monthLabel: monthName,
      // Derived from the label: `credit_overview` names the month but does not
      // number it, and the heatmap needs the index to dim months the year has
      // not reached yet.
      month: nameIdx >= 0 ? nameIdx + 1 : 0,
      usable: numOf(r, ['usable_credit', 'usable', 'available']),
      pending: numOf(r, ['pending_request', 'pending', 'pending_days']),
      actualBalance: numOf(r, ['actual_balance', 'balance']),
      totalUsed: numOf(r, ['used_this_year', 'total_used', 'used']),
      proratedEarning: numOf(r, ['prorated_earning', 'prorated', 'earned']),
      usedThisMonth: numOf(r, ['used_this_month', 'month_used']),
      monthly: byMonth,
    };
  }

  // Legacy/embedded shape: the strip rides inside the block as `monthly`, either
  // a 12-slot array or a month-keyed object.
  const monthlyRaw = pick<unknown>(r, ['monthly', 'by_month', 'activity'], null);
  const monthly = Array.from({ length: 12 }, (_, i) => {
    if (Array.isArray(monthlyRaw)) return numOf(asRecord({ v: monthlyRaw[i] }), ['v']);
    if (monthlyRaw && typeof monthlyRaw === 'object') {
      const m = asRecord(monthlyRaw);
      return numOf(m, [String(i + 1), String(i + 1).padStart(2, '0'), MONTH_NAMES[i]!.toLowerCase()]);
    }
    return 0;
  });

  const monthIdx = numOf(r, ['month'], 0);
  const monthName = str(r, ['month_label', 'month_name'], '');
  const nameIdx = MONTH_NAMES.findIndex((n) => n.toLowerCase() === monthName.toLowerCase());
  return {
    year: numOf(r, ['year'], new Date().getFullYear()),
    monthLabel: monthName || (monthIdx >= 1 && monthIdx <= 12 ? MONTH_NAMES[monthIdx - 1]! : ''),
    month: monthIdx >= 1 && monthIdx <= 12 ? monthIdx : nameIdx >= 0 ? nameIdx + 1 : 0,
    usable: numOf(r, ['usable', 'usable_credit', 'available']),
    pending: numOf(r, ['pending', 'pending_request', 'pending_days']),
    actualBalance: numOf(r, ['actual_balance', 'balance']),
    totalUsed: numOf(r, ['total_used', 'used', 'used_this_year']),
    proratedEarning: numOf(r, ['prorated_earning', 'prorated', 'earned']),
    usedThisMonth: numOf(r, ['used_this_month', 'month_used']),
    monthly,
  };
}

/**
 * Read the row's `can_approve` verdict.
 *
 * ABSENT means the backend didn't say — an older deployment, or a payload that
 * predates the flag — so we fall back to today's optimistic behaviour (offer
 * the action, let the PATCH refuse) rather than hiding actions from every real
 * approver. When the key IS present it is honoured exactly.
 */
function readLeaveApproval(r: Json): TeamLeaveApproval {
  const raw = pick<unknown>(r, ['can_approve', 'canApprove'], null);
  if (raw == null) return { allowed: true, action: 'approve', reason: '', bypassesEndorsement: null };
  // A bare boolean is accepted too, in case the shape is simplified later.
  if (typeof raw === 'boolean') {
    return { allowed: raw, action: raw ? 'approve' : null, reason: '', bypassesEndorsement: null };
  }
  const rec = asRecord(raw);
  const allowed = bool(rec, ['allowed']);
  const action = str(rec, ['action'], '').toLowerCase().trim();
  const reason = str(rec, ['reason'], '').toLowerCase().trim();
  // Absent (an older backend) is NULL, not false: false would be a claim the
  // server never made, and it would suppress the fallback that still asks.
  const bypasses = pick<unknown>(rec, ['bypasses_endorsement'], null);
  return {
    allowed,
    action: action === 'approve' || action === 'endorse' ? action : null,
    reason:
      reason === 'already_decided' ||
      reason === 'awaiting_final_approval' ||
      reason === 'no_permission' ||
      reason === 'self_approval_denied'
        ? reason
        : '',
    bypassesEndorsement: bypasses == null ? null : bool(rec, ['bypasses_endorsement']),
  };
}

/** Coerce a numeric-ish field — the API sends some counts as strings ("3"). */
function numOf(rec: Json, keys: string[], fallback = 0): number {
  const v = pick<unknown>(rec, keys, fallback);
  const n = typeof v === 'number' ? v : Number(String(v ?? '').trim());
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Map one leave application from the manager-level team-leave surface into a
 * view model.
 *
 * The row is the same `EuapiLeaveApplication` shape `/me/leaves` returns —
 * flat `applicant_id` / `applicant_name` — but a team-scoped endpoint may also
 * nest the richer `employee` object the attendance surface uses (photo, job
 * title, department). Both are read, nested first, so this mapper works against
 * either shape without a change here.
 */
export function mapTeamLeaveRecord(value: unknown, idx = 0): TeamLeaveVM {
  // Accept a raw row (from the list's `toList`) and a single-resource payload
  // wrapped under the `leave` domain key (get / updateStatus) alike.
  const outer = asRecord(value);
  const r = domainBody(value, ['leave']);
  const emp = asRecord(pick(r, ['employee', 'applicant', 'user'], {}));

  /**
   * The PATCH response carries a `result` block beside the record. It is read
   * from the OUTER object first because `domainBody` unwraps past it, then from
   * the record itself in case the backend nests it there instead.
   *
   * This matters: the endpoint's `status` is NOT an echo of what was requested.
   * Under the tenant's dual-approval setting a first approver's "approved"
   * legitimately comes back as `for_review`, so the response — not the request —
   * is the source of truth for what actually happened.
   */
  const result = asRecord(pick(outer, ['result'], null) ?? pick(r, ['result'], {}));
  const effective = pick<unknown>(result, ['effective_status'], null);

  const name =
    str(emp, ['name', 'full_name'], '') ||
    str(r, ['applicant_name', 'employee_name', 'creator_name'], '');
  const start = str(r, ['start_date'], '');
  const end = str(r, ['end_date'], '');
  const days = numOf(r, ['total_days']);
  const dateKey = start.slice(0, 10);
  const rangeBase = start === end ? formatDate(start) : `${formatDate(start)} – ${formatDate(end)}`;
  // `result.effective_status` wins when the server sent one — it is the
  // authoritative outcome of a write; the row's own `status` is the fallback.
  const status = normalizeTeamLeaveStatus(effective ?? pick(r, ['status'], ''));

  return {
    id: idOf(r, idx),
    userId: str(emp, ['id'], '') || str(r, ['applicant_id', 'user_id', 'created_by'], ''),
    employeeName: name,
    // Same defensive read as the attendance mapper: the photo may ride the
    // nested employee object or the row itself, and the API documents neither.
    employeeImage:
      profileImageUrl(pick<unknown>(emp, EMPLOYEE_IMAGE_KEYS, null)) ||
      profileImageUrl(pick<unknown>(r, EMPLOYEE_IMAGE_KEYS, null)),
    employeeInitials: initialsOf(name) || '?',
    jobTitle: str(emp, ['job_title', 'title', 'position'], ''),
    departmentName:
      str(emp, ['department_name', 'department', 'team_name', 'team', 'unit_name', 'unit'], '') ||
      str(r, ['department_name', 'department', 'team_name', 'team', 'unit_name', 'unit'], ''),
    type: str(r, ['leave_type_title', 'leave_type', 'type'], 'Leave'),
    typeColor: str(r, ['leave_type_color', 'color'], ''),
    status,
    statusLabel: TEAM_LEAVE_STATUS_LABELS[status],
    reason: str(r, ['reason'], ''),
    startDate: start,
    endDate: end,
    dateRangeLabel: `${rangeBase}${leaveSpanSuffix(start, end, days, numOf(r, ['total_hours']))}`,
    dateRangePlain: rangeBase,
    totalDays: days,
    totalHours: numOf(r, ['total_hours']),
    requiredCredits: numOf(r, ['required_credits']),
    isPaid: bool(r, ['paid']),
    dateKey,
    dateLabel: dateKey ? formatDate(`${dateKey}T00:00:00`) : '',
    dateMillis: localDateMillis(start),
    createdAtLabel: formatDate(pick<unknown>(r, ['created_at', 'date_created', 'submitted_at'], null)),
    checkedByName: str(r, ['checker_name'], '') || str(asRecord(pick(r, ['checker'], {})), ['name'], ''),
    endorsedByName: str(r, ['first_checker_name'], ''),
    endorsedAtLabel: formatDate(pick<unknown>(r, ['first_checked_at'], null)),
    approval: readLeaveApproval(r),
    // Read from the record first, then the outer envelope — the backend may
    // hang the applicant's ledger beside the leave rather than inside it.
    credits: mapTeamLeaveCredits(r) ?? mapTeamLeaveCredits(outer),
    attachmentCount: leaveAttachmentCountOf(r),
    // The documents and their capabilities ride BESIDE the leave (`domainBody`
    // unwraps past them), so the outer envelope is read first, then the record.
    attachments: leaveAttachmentsBeside(outer, r),
    attachmentCapabilities: leaveAttachmentCapabilitiesBeside(outer, r),
    endorsedOnly: bool(result, ['endorsed_only']),
    bypassedEndorsement: bool(result, ['bypassed_endorsement']),
  };
}

/** Map a team-leave list payload (`{ leaves: [...] }`) to VMs. */
export function mapTeamLeaveList(data: unknown): TeamLeaveVM[] {
  return toList(data).map((row, i) => mapTeamLeaveRecord(row, i));
}

/**
 * A department the leave review queue can be narrowed to — one row of
 * `GET /v1/api/leaves/departments`. The server offers only departments holding at
 * least one employee the caller may review, so every option can return rows.
 */
export interface LeaveReviewDepartmentVM {
  /** Sent back as the queue's `department_id`. */
  id: number;
  title: string;
  /** The caller heads or belongs to this department. */
  isMine: boolean;
}

/**
 * Map `{ departments: [{ id, title, is_mine }] }` (or a bare array) in the server's
 * order. A row with no positive id cannot be sent back as a filter and a row with no
 * title cannot be shown, so both are dropped, as is a repeated id.
 */
export function mapLeaveReviewDepartments(data: unknown): LeaveReviewDepartmentVM[] {
  const seen = new Set<number>();
  const out: LeaveReviewDepartmentVM[] = [];
  for (const value of toList<unknown>(data)) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) continue;
    const rec = asRecord(value);
    const id = Number(str(rec, ['id', 'team_id', 'department_id']));
    const title = str(rec, ['title', 'name', 'department_name']).trim();
    if (!Number.isInteger(id) || id < 1 || title === '' || seen.has(id)) continue;
    seen.add(id);
    out.push({ id, title, isMine: bool(rec, ['is_mine', 'isMine']) });
  }
  return out;
}

// ── Advisories (Announcements page) ──────────────────────────────────────────
// Verified list: { advisories: [{ id, title, start_date, end_date, is_read,
//   is_pinned, date_created, files }], meta }. Detail adds `description` (HTML).
// Admin-style fields below are read defensively when the API includes them.

/**
 * One file attached to an advisory.
 *
 * Files carry no id on the wire — `index` (their position in the advisory's
 * list) is what `GET /v1/api/me/advisories/{id}/files/{index}` addresses when
 * a fresh link is needed. On an S3 tenant `url` is signed for the tenant's TTL
 * (60 s on the dev tenant), so it is only a first try: previews re-sign on a
 * failed load and Download always asks for a fresh link.
 */
export interface AdvisoryFileVM {
  index: number;
  name: string;
  /** Lower-case, no dot. `''` when unknown. */
  extension: string;
  /** 0 = unknown. */
  sizeBytes: number;
  /** "2.4 MB", or `''` when the size is unknown — never "0 B". */
  sizeLabel: string;
  isImage: boolean;
  /** `s3` = a signed URL that expires; `local` = a stable /storage URL. */
  storage: 's3' | 'local';
  /**
   * Inline preview URL. `''` when ERPat could not resolve it (an S3 file while
   * S3 is off) — the row still shows, so the index stays stable.
   */
  url: string;
}

function mapAdvisoryFiles(raw: unknown): AdvisoryFileVM[] {
  return toList<Json>(raw).map((f, i) => {
    const rec = asRecord(f);
    const url = uploadedFileUrl(pick<unknown>(rec, ['url', 'download_url', 'file_url', 'path'], ''));
    const name = str(rec, ['name', 'file_name', 'original_name'], '') || fileNameFromUrl(url) || `Attachment ${i + 1}`;
    const fromName = name.includes('.') ? (name.split('.').pop() ?? '') : '';
    const extension = (str(rec, ['extension', 'ext'], '') || fromName).replace(/^\./, '').toLowerCase();
    const sizeBytes = numOf(rec, ['size', 'file_size', 'size_bytes'], 0);
    const index = numOf(rec, ['index'], i);
    const declaresImage = rec.is_image !== undefined && rec.is_image !== null;
    return {
      index: Number.isInteger(index) && index >= 0 ? index : i,
      name,
      extension,
      sizeBytes: sizeBytes > 0 ? sizeBytes : 0,
      sizeLabel: formatBytes(sizeBytes),
      // Trust the server's answer when it gives one; the extension otherwise.
      isImage: declaresImage ? bool(rec, ['is_image']) : isImageExtension(extension),
      storage: str(rec, ['storage', 'service_type'], '') === 's3' ? 's3' : 'local',
      url,
    };
  });
}

/** The URL out of `GET …/advisories/{id}/files/{index}` → `{ file: { url, … } }`. `''` when absent. */
export function mapAdvisoryFileLink(data: unknown): string {
  const rec = asRecord(domainBody(data, ['file']));
  return uploadedFileUrl(pick<unknown>(rec, ['url', 'download_url'], ''));
}

export interface AdvisoryVM {
  id: string;
  title: string;
  body: string;
  bodyHtml: string;
  excerpt: string;
  createdAt: string;
  updatedAt: string;
  startAt: string;
  endAt: string;
  dateLabel: string;
  timeLabel: string;
  updatedDateLabel: string;
  updatedTimeLabel: string;
  createdByLabel: string;
  /**
   * User ids this advisory was addressed to, when the payload names them.
   *
   * EMPTY means "the payload said nothing about an audience" — which for an
   * advisory reads as an org-wide broadcast, NOT as "addressed to nobody".
   * `/me/advisories` never needs this (the server has already scoped that list);
   * it exists for the broad, audience-agnostic routes, where it is the only
   * thing distinguishing a notice meant for you from one that isn't.
   */
  audienceUserIds: string[];
  shareWithCount: number | null;
  viewersCount: number | null;
  views: number;
  readTracked: boolean;
  unread: boolean;
  pinned: boolean;
  startDateLabel: string;
  endDateLabel: string;
  /** Attachments, in upload order. `[]` when none (or an older ERPat that sends no `files`). */
  files: AdvisoryFileVM[];
}

/** A bare calendar date, no time and no zone — e.g. `start_date: "2026-04-09"`. */
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * ISO form of an API date, WITHOUT dragging a date-only value through a zone.
 *
 * `start_date`/`end_date` arrive as bare "YYYY-MM-DD". `new Date()` reads that
 * as UTC midnight and `toISOString()` hands back a stamp a device west of UTC
 * reads as the PREVIOUS day — "2026-04-01" filed a first-of-month advisory
 * under March, and a first-of-year one under the wrong year. A calendar date
 * names a day, not an instant, so it is passed through unchanged; values that
 * really do carry a time still normalise to a full ISO stamp.
 */
function isoDate(value: unknown): string {
  if (typeof value === 'string' && DATE_ONLY_RE.test(value.trim())) return value.trim();
  return toDate(value)?.toISOString() ?? '';
}

function labelOf(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return stripHtml(value);
  const rec = asRecord(value);
  const first = str(rec, ['first_name', 'firstname', 'given_name'], '');
  const last = str(rec, ['last_name', 'lastname', 'surname'], '');
  const full = [first, last].filter(Boolean).join(' ').trim();
  return full || str(rec, ['name', 'full_name', 'display_name', 'username', 'email', 'label', 'title'], '');
}

function countOf(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value.trim().match(/^\d+/)?.[0] ?? NaN);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (Array.isArray(value)) return value.length;
  const rec = asRecord(value);
  const count = pick<unknown>(rec, ['count', 'total', 'length', 'value'], null);
  if (count !== null) return countOf(count);
  return null;
}

function countFrom(rec: Json, keys: string[]): number | null {
  for (const key of keys) {
    if (rec[key] !== undefined && rec[key] !== null) return countOf(rec[key]);
  }
  return null;
}

export function mapAdvisory(data: unknown): AdvisoryVM {
  const r = asRecord(domainBody(data, ['advisory']));
  const id = str(r, ['id', 'advisory_id', 'announcement_id', 'entry_id', 'uuid', '_id', 'key'], idOf(r));
  const created = pick<unknown>(r, ['date_created', 'created_at', 'created', 'posted_at'], null);
  const updated = pick<unknown>(r, ['last_updated', 'updated_at', 'date_updated', 'modified_at'], null);
  const start = pick<unknown>(r, ['start_date', 'start_at', 'effective_date'], null);
  const end = pick<unknown>(r, ['end_date', 'end_at', 'expires_at', 'expiry_date'], null);
  const rawBody = str(r, ['description', 'body', 'content', 'message', 'details'], '');
  const body = stripHtml(rawBody);
  const realViews = countFrom(r, [
    'viewers',
    'viewer_count',
    'viewers_count',
    'viewer_total',
    'total_viewers',
    'views',
    'view_count',
    'views_count',
    'read_count',
    'readers_count',
    'read_by_count',
    'seen_count',
    'seen_by_count',
    'acknowledged_count',
    'acknowledgement_count',
    'total_views',
  ]);
  const readValue = pick<unknown>(r, ['is_read', 'read'], null);
  const unreadValue = pick<unknown>(r, ['is_unread', 'unread'], null);
  const readTracked = readValue !== null || unreadValue !== null;
  return {
    id,
    title: str(r, ['title', 'subject', 'announcement_title', 'advisory_title', 'name'], ''),
    body,
    bodyHtml: rawBody, // raw server HTML — sanitized at render (detail v-html)
    excerpt: body.slice(0, 160),
    createdAt: isoDate(created),
    updatedAt: isoDate(updated),
    startAt: isoDate(start),
    endAt: isoDate(end),
    dateLabel: formatDate(created),
    timeLabel: created ? formatTime(created) : '',
    updatedDateLabel: formatDate(updated),
    updatedTimeLabel: updated ? formatTime(updated) : '',
    createdByLabel: labelOf(pick<unknown>(r, ['created_by_name', 'createdByName', 'created_by_user', 'author_name', 'author', 'created_by', 'createdBy', 'creator', 'user'], '')),
    // Same key spellings `shareWithCount` counts, read for their ids instead.
    // Defensive by necessity: the broad routes are undocumented, so the field
    // may be ids, objects, a CSV, or absent entirely.
    audienceUserIds: uniqueList(
      parsePersonIds(
        pick(r, [
          'share_with',
          'shareWith',
          'shared_with',
          'sharedWith',
          'share_with_user_ids',
          'recipient_user_ids',
          'recipients',
          'audience_user_ids',
          'audience',
          'participants',
          'user_ids',
          'users',
        ], []),
      ),
    ).filter(Boolean),
    shareWithCount: countFrom(r, ['share_with_count', 'shareWithCount', 'shared_with_count', 'sharedWithCount', 'share_with', 'shareWith', 'shared_with', 'sharedWith', 'recipients_count', 'recipients', 'audience_count', 'audience', 'participants_count', 'participants']),
    viewersCount: realViews,
    views: realViews ?? 0,
    readTracked,
    unread: unreadValue !== null ? bool(r, ['is_unread', 'unread']) : readValue !== null ? !bool(r, ['is_read', 'read']) : false,
    pinned: bool(r, ['is_pinned', 'pinned']),
    startDateLabel: formatDate(start),
    endDateLabel: formatDate(end),
    files: mapAdvisoryFiles(pick<unknown>(r, ['files', 'attachments'], [])),
  };
}

export function mapAdvisories(data: unknown): AdvisoryVM[] {
  return toList(data).map((a) => mapAdvisory(a));
}

// ── Knowledge Base (help articles) ───────────────────────────────────────────
// Verified shape: { help_articles: [{ id, title, body, category }] }. The endpoint
// is list-only — there is NO detail route, and the full `body` (rich text/HTML or
// plain) ships inline with the list. Server query params are `search` (title+body
// substring) and `category_id`; the app filters client-side so browsing stays
// instant. The design's view counts / read-times / dates / "required" flags have
// no backing field, so they're intentionally omitted (don't fabricate them).
export interface KbArticleVM {
  id: string;
  title: string;
  /** Free-text category label (used to group + colour-code articles). */
  category: string;
  /** Raw server body (HTML or plain) — rendered on the detail page. */
  bodyHtml: string;
  /** Flattened single-line preview for list cards. */
  excerpt: string;
}

export function mapKbArticle(data: unknown): KbArticleVM {
  const r = asRecord(domainBody(data, ['help_article', 'article']));
  const rawBody = str(r, ['body', 'content', 'description'], '');
  return {
    id: idOf(r),
    title: str(r, ['title', 'subject', 'name'], ''),
    category: str(r, ['category', 'category_name', 'category_label'], '').trim(),
    bodyHtml: rawBody,
    excerpt: htmlToText(rawBody).slice(0, 160),
  };
}

export function mapKbArticles(data: unknown): KbArticleVM[] {
  return toList(data).map((a) => mapKbArticle(a));
}

// ── Employee Handbook (modules/Helpdesk → Handbook pillar) ───────────────────
// Distinct from the Knowledge Base above: that is the org-wide Help Center
// (`me/help-articles`, list-only, free-text category strings). This is the
// company HANDBOOK — a real server-side taxonomy (`handbook_categories`, 14
// compliance-aligned chapters carrying icon/description/sort) over published
// sections (`handbook_articles`), with a detail route and SQL paging.
//
// Verified live against dev tenant `rs8`:
//   GET me/handbook/categories    -> { categories: [{ id, title, slug, description,
//                                      icon, sort, total_articles }],
//                                      meta: { total_categories, total_articles,
//                                              last_updated_at } }
//   GET me/handbook/articles      -> { articles: [ …list shape, NO body… ],
//                                      meta: { page, per_page, total } }
//   GET me/handbook/articles/{id} -> { article: { …list shape, body, section_path } }
//
// PUBLISHED-ONLY by construction — the server hardcodes status='published', so a
// draft still under HR review can never reach this client.

/** One handbook chapter. `iconName` is a FontAwesome-4 class from the taxonomy. */
export interface HandbookCategoryVM {
  id: string;
  title: string;
  slug: string;
  description: string;
  /** e.g. `fa-gavel` — mapped to an ionicon in views/handbook/handbook.ts. */
  iconName: string;
  sort: number;
  /** Published sections filed under this chapter (counted by the server). */
  articleCount: number;
}

/** The landing payload: every chapter plus the whole-handbook summary. */
export interface HandbookSummaryVM {
  categories: HandbookCategoryVM[];
  totalCategories: number;
  totalArticles: number;
  /** Newest published/updated section timestamp; '' when the handbook is empty. */
  lastUpdatedAt: string;
}

export interface HandbookArticleVM {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  categoryId: string;
  categoryTitle: string;
  categorySlug: string;
  sort: number;
  /** Page range in the uploaded source document, when the section came from one. */
  sourcePages: string;
  totalViews: number;
  /** Server-computed from the body word count; 0 when the section has no body. */
  readingMinutes: number;
  publishedAt: string;
  updatedAt: string;
  /**
   * Sanitized HTML. Present ONLY on the detail endpoint — list rows omit it so a
   * page of LONGTEXT bodies never crosses the wire. Always '' on a list row.
   */
  bodyHtml: string;
  /** The section's path inside the source document (detail only). */
  sectionPath: string;
}

export function mapHandbookCategory(data: unknown, idx = 0): HandbookCategoryVM {
  const r = asRecord(domainBody(data, ['category']));
  return {
    id: idOf(r, idx),
    title: str(r, ['title', 'name'], ''),
    slug: str(r, ['slug'], ''),
    description: str(r, ['description', 'summary'], ''),
    iconName: str(r, ['icon'], ''),
    sort: Number(pick<unknown>(r, ['sort', 'order'], 0)) || 0,
    articleCount: Number(pick<unknown>(r, ['total_articles', 'articles_count', 'count'], 0)) || 0,
  };
}

export function mapHandbookSummary(data: unknown): HandbookSummaryVM {
  const rec = asRecord(data);
  const categories = toList(rec.categories ?? rec).map((c, i) => mapHandbookCategory(c, i));
  const meta = asRecord(rec.meta);
  return {
    categories,
    // Fall back to the list itself rather than 0 — a server that ever drops `meta`
    // should degrade to a correct count, not to a zeroed summary card.
    totalCategories:
      Number(pick<unknown>(meta, ['total_categories'], categories.length)) || categories.length,
    totalArticles:
      Number(pick<unknown>(meta, ['total_articles'], 0)) ||
      categories.reduce((sum, c) => sum + c.articleCount, 0),
    lastUpdatedAt: str(meta, ['last_updated_at', 'updated_at'], ''),
  };
}

export function mapHandbookArticle(data: unknown, idx = 0): HandbookArticleVM {
  const r = asRecord(domainBody(data, ['article', 'handbook_article']));
  const bodyHtml = str(r, ['body', 'content'], '');
  const excerpt = str(r, ['excerpt', 'summary'], '').trim();
  return {
    id: idOf(r, idx),
    title: str(r, ['title', 'name'], ''),
    slug: str(r, ['slug'], ''),
    // The server already derives an excerpt from the body when none is stored;
    // this second fallback only covers a payload from a future write path that
    // skipped both.
    excerpt: excerpt || htmlToText(bodyHtml).slice(0, 180),
    categoryId: str(r, ['category_id'], ''),
    categoryTitle: str(r, ['category_title', 'category'], ''),
    categorySlug: str(r, ['category_slug'], ''),
    sort: Number(pick<unknown>(r, ['sort', 'order'], 0)) || 0,
    sourcePages: str(r, ['source_pages'], ''),
    totalViews: Number(pick<unknown>(r, ['total_views', 'views'], 0)) || 0,
    readingMinutes: Number(pick<unknown>(r, ['reading_minutes', 'read_minutes'], 0)) || 0,
    publishedAt: str(r, ['published_at'], ''),
    updatedAt: str(r, ['updated_at'], ''),
    bodyHtml,
    sectionPath: str(r, ['section_path'], ''),
  };
}

export function mapHandbookArticles(data: unknown): HandbookArticleVM[] {
  return toList(data).map((a, i) => mapHandbookArticle(a, i));
}

// ── Support (tickets + channels) ─────────────────────────────────────────────
// Verified: support_channels [{ id, label, value, kind }];
//   tickets [{ id, reference, subject, category, priority,
//     status: new|open|client_replied|on_hold|closed,
//     assignee|agent:{ id, name, role, avatar_url }, requester, collaborators[],
//     my_role, can_comment, updated_at, created_at, unread,
//     status_label, ticket_type_id, ticket_type:{ id, title, label, duration_label, points },
//     due_date_local,
//     capabilities:{ my_role, can_comment, can_change_status, allowed_transitions[], can_assign_to_me,
//       can_edit, edit_fields{…}, can_attach, can_insert_template, can_insert_knowledge_base },
//     messages:[{ id, author:'me'|'agent'|'system', author_id, author_name, body(HTML or text),
//       sent_at, files:[{ index, name, extension, size, is_image, storage, url }] }] }].
//   Source of truth: modules/Helpdesk/controllers/Helpdesk_tickets_euapi.php.
export interface SupportChannelVM {
  id: string;
  label: string;
  value: string;
  kind: string;
}
export function mapSupportChannels(data: unknown): SupportChannelVM[] {
  return toList(data).map((c, i) => {
    const r = asRecord(c);
    return {
      id: str(r, ['id'], String(i)),
      label: str(r, ['label', 'name'], ''),
      value: str(r, ['value'], ''),
      kind: str(r, ['kind', 'type'], 'link'),
    };
  });
}

const TICKET_STATUSES: TicketStatus[] = ['open', 'pending', 'resolved', 'closed'];
const TICKET_RAW_STATUSES: TicketRawStatus[] = ['new', 'open', 'client_replied', 'on_hold', 'closed'];
const TICKET_PRIORITIES: TicketPriority[] = ['low', 'medium', 'high', 'urgent', 'critical'];
const TICKET_ROLES: TicketRole[] = ['assignee', 'requester', 'creator', 'collaborator', 'viewer'];

/**
 * Bucket the server's `status` into one of the four presentation buckets.
 *
 * ERPat's real enum is `new | open | client_replied | on_hold | closed` (see
 * `TicketRawStatus`) — `new`/`client_replied` read as "open", `on_hold` as
 * "pending", `closed` as "closed", and nothing maps to "resolved" on the live
 * backend. The synonym lists stay so a payload from another source still lands
 * somewhere sensible; anything unrecognized falls back to `open`.
 */
function normalizeTicketStatus(raw: string): TicketStatus {
  const s = raw.toLowerCase().replace(/[\s_-]+/g, '');
  if (['resolved', 'done', 'completed', 'solved', 'fixed'].includes(s)) return 'resolved';
  if (['closed', 'cancelled', 'canceled', 'archived', 'rejected'].includes(s)) return 'closed';
  if (['pending', 'inprogress', 'processing', 'waiting', 'onhold', 'assigned', 'reopened'].includes(s)) return 'pending';
  return TICKET_STATUSES.includes(s as TicketStatus) ? (s as TicketStatus) : 'open';
}

/** The server's status value, kept verbatim when it is one of the real five. */
function rawTicketStatus(raw: string): TicketRawStatus {
  const s = raw.toLowerCase().trim() as TicketRawStatus;
  return TICKET_RAW_STATUSES.includes(s) ? s : 'open';
}

/**
 * A message's attachments (`files[]`: `{ index, name, extension, size, is_image,
 * storage, url }` — the advisory shape). `index` addresses the per-file re-sign
 * route, so it is kept even when `url` is '' (an S3 file while S3 is off).
 */
function mapTicketFiles(raw: unknown): TicketFile[] {
  return toList<Json>(raw).map((f, i) => {
    const rec = asRecord(f);
    const url = uploadedFileUrl(pick<unknown>(rec, ['url', 'download_url'], ''));
    const name = str(rec, ['name', 'file_name'], '') || fileNameFromUrl(url) || `Attachment ${i + 1}`;
    const fromName = name.includes('.') ? (name.split('.').pop() ?? '') : '';
    const extension = (str(rec, ['extension', 'ext'], '') || fromName).replace(/^\./, '').toLowerCase();
    const sizeBytes = numOf(rec, ['size', 'file_size'], 0);
    const index = numOf(rec, ['index'], i);
    const declaresImage = rec.is_image !== undefined && rec.is_image !== null;
    return {
      index: Number.isInteger(index) && index >= 0 ? index : i,
      name,
      extension,
      sizeBytes: sizeBytes > 0 ? sizeBytes : 0,
      isImage: declaresImage ? bool(rec, ['is_image']) : isImageExtension(extension),
      storage: str(rec, ['storage', 'service_type'], '') === 's3' ? 's3' : 'local',
      url,
    };
  });
}

function mapTicketMessage(m: unknown, i: number): TicketMessage {
  const r = asRecord(m);
  const author = str(r, ['author'], 'agent');
  // Read the timestamp ONCE so the relative label and the sortable instant can
  // never disagree about which field they came from.
  const at = pick<unknown>(r, ['sent_at', 'created_at'], null);
  return {
    id: idOf(r, i),
    author: author === 'me' ? 'me' : author === 'system' ? 'system' : 'agent',
    authorId: str(r, ['author_id', 'created_by'], ''),
    authorName: str(r, ['author_name', 'name'], ''),
    // A web reply is HTML (the rich editor), an app reply is plain text: keep the
    // LINES either way — a one-line strip turned every multi-line reply into one run.
    body: htmlToLines(pick(r, ['body', 'message', 'content'], '')),
    files: mapTicketFiles(pick<unknown>(r, ['files', 'attachments'], [])),
    timeLabel: formatRelative(at),
    sentAt: toMillis(at),
  };
}

/** A person on a ticket (`{ id, name, role, avatar_url }`), or null when absent. */
function mapTicketPerson(value: unknown): TicketPerson | null {
  if (!value || typeof value !== 'object') return null;
  const r = asRecord(value);
  const name = str(r, ['name', 'full_name'], '');
  const id = str(r, ['id', 'user_id'], '');
  if (!id && !name) return null;
  return {
    id,
    name,
    role: str(r, ['role', 'role_title'], ''),
    avatarUrl: profileImageUrl(pick<unknown>(r, ['avatar_url', 'avatar', 'image'], '')),
    initials: initialsOf(name) || '?',
  };
}

/**
 * The server's capabilities block. Defaults are deliberately CONSERVATIVE in
 * the direction that keeps today's behaviour: a backend that predates the
 * block yields "can comment, nothing else", so the composer stays and no
 * action button appears that the server would reject.
 */
function mapTicketCapabilities(value: unknown, fallbackRole: string, fallbackCanComment: boolean): TicketCapabilities {
  const r = asRecord(value);
  const role = str(r, ['my_role'], fallbackRole) as TicketRole;
  const transitions = toList<unknown>(pick(r, ['allowed_transitions'], []))
    .map((t) => rawTicketStatus(String(t)))
    .filter((t, idx, all) => all.indexOf(t) === idx);
  const canComment = bool(r, ['can_comment'], fallbackCanComment);
  const canEdit = bool(r, ['can_edit'], false);
  // An older backend has no edit_fields: nothing is editable (canEdit is false too).
  const f = asRecord(r.edit_fields);
  const editFields: TicketEditFields = {
    title: canEdit && bool(f, ['title'], false),
    ticketType: canEdit && bool(f, ['ticket_type_id', 'ticket_type'], false),
    requestedBy: canEdit && bool(f, ['requested_by'], false),
    collaborators: canEdit && bool(f, ['collaborators'], false),
    assignedTo: canEdit && bool(f, ['assigned_to'], false),
    dueDate: canEdit && bool(f, ['due_date'], false),
  };
  return {
    myRole: TICKET_ROLES.includes(role) ? role : 'viewer',
    canComment,
    canChangeStatus: bool(r, ['can_change_status'], false) && transitions.length > 0,
    allowedTransitions: transitions,
    canAssignToMe: bool(r, ['can_assign_to_me'], false),
    canEdit,
    editFields,
    // Older backends accepted no files at all, so the default is no.
    canAttach: canComment && bool(r, ['can_attach'], false),
    canInsertTemplate: bool(r, ['can_insert_template'], false),
    canInsertKnowledgeBase: bool(r, ['can_insert_knowledge_base'], false),
  };
}

/** A ticket's `ticket_type` block, or null when the ticket has no type. */
function mapTicketTypeInfo(value: unknown): TicketTypeInfo | null {
  if (!value || typeof value !== 'object') return null;
  const r = asRecord(value);
  const id = str(r, ['id'], '');
  const title = str(r, ['title', 'name'], '');
  if (!id && !title) return null;
  return {
    id,
    title,
    label: str(r, ['label'], '') || title,
    durationLabel: str(r, ['duration_label'], ''),
    points: numOf(r, ['points'], 0),
  };
}

export function mapTickets(data: unknown): Ticket[] {
  return toList(data).map((t, i) => mapTicket(t, i));
}

export function mapTicket(data: unknown, i = 0): Ticket {
  const r = asRecord(domainBody(data, ['ticket']));
  // `assignee` is the current name; `agent` the pre-capabilities alias.
  const assignee = mapTicketPerson(r.assignee) ?? mapTicketPerson(r.agent);
  const agentRec = asRecord(r.agent);
  const rawStatusText = str(r, ['status'], 'open');
  const priority = str(r, ['priority'], 'medium').toLowerCase();
  // List rows carry my_role / can_comment at the top level; the detail carries
  // the full capabilities block. Feed the top-level pair in as fallbacks so a
  // list row still resolves a role and a composer decision.
  const capabilities = mapTicketCapabilities(
    r.capabilities,
    str(r, ['my_role'], 'requester'),
    bool(r, ['can_comment'], true),
  );
  return {
    id: idOf(r, i),
    reference: str(r, ['reference', 'ref'], ''),
    // Subjects can come back as rich-text HTML (e.g. "<p>ID replacement</p>");
    // strip tags so list/detail show plain text, not raw markup.
    subject: stripHtml(str(r, ['subject', 'title'], '')) || str(r, ['subject', 'title'], '').trim(),
    category: str(r, ['category'], ''),
    ticketTypeId: (() => {
      const typeId = str(r, ['ticket_type_id'], '') || str(asRecord(r.ticket_type), ['id'], '');
      return typeId === '0' ? '' : typeId;
    })(),
    ticketType: mapTicketTypeInfo(r.ticket_type),
    priority: (TICKET_PRIORITIES.includes(priority as TicketPriority) ? priority : 'medium') as TicketPriority,
    status: normalizeTicketStatus(rawStatusText),
    rawStatus: rawTicketStatus(rawStatusText),
    statusLabel: ticketStatusLabel(rawTicketStatus(rawStatusText), str(r, ['status_label'], '')),
    dueDateLocal: str(r, ['due_date_local'], ''),
    agent: {
      id: assignee?.id ?? '',
      name: assignee?.name ?? '',
      role: assignee?.role ?? '',
      initials: assignee?.initials ?? '',
      online: bool(agentRec, ['online']),
    },
    assignee,
    requester: mapTicketPerson(r.requester),
    collaborators: toList<unknown>(pick(r, ['collaborators'], []))
      .map(mapTicketPerson)
      .filter((p): p is TicketPerson => p !== null),
    capabilities,
    updatedLabel: formatRelative(pick<unknown>(r, ['updated_at', 'updated'], null)),
    createdLabel: formatDate(pick<unknown>(r, ['created_at', 'created', 'date_created'], null)),
    unread: bool(r, ['unread', 'is_unread']),
    messages: toList(pick(r, ['messages', 'thread'], [])).map(mapTicketMessage),
  };
}

/**
 * The `{ message, status, capabilities }` reply envelope from
 * POST /me/tickets/{id}/messages — enough to append the reply and patch the
 * ticket's status/capabilities in place without a refetch.
 */
export interface TicketReplyResultVM {
  message: TicketMessage;
  rawStatus: TicketRawStatus | null;
  status: TicketStatus | null;
  statusLabel: string | null;
  capabilities: TicketCapabilities | null;
}
export function mapTicketReplyResult(data: unknown): TicketReplyResultVM {
  const r = asRecord(data);
  const statusText = str(r, ['status'], '');
  return {
    message: mapTicketMessage(r.message, 0),
    rawStatus: statusText ? rawTicketStatus(statusText) : null,
    status: statusText ? normalizeTicketStatus(statusText) : null,
    statusLabel: statusText ? ticketStatusLabel(rawTicketStatus(statusText), str(r, ['status_label'], '')) : null,
    capabilities: r.capabilities && typeof r.capabilities === 'object'
      ? mapTicketCapabilities(r.capabilities, 'viewer', true)
      : null,
  };
}

/** GET /me/tickets/form-options → what the New Ticket form offers. */
export function mapTicketFormOptions(data: unknown): TicketFormOptions {
  const r = asRecord(data);
  const types: TicketTypeOption[] = toList<Json>(pick(r, ['ticket_types'], [])).map((t, i) => {
    const rec = asRecord(t);
    const title = str(rec, ['title', 'name'], '');
    return {
      id: str(rec, ['id'], String(i)),
      title,
      label: str(rec, ['label'], '') || title,
      description: str(rec, ['description'], ''),
      durationSeconds: numOf(rec, ['duration_seconds'], 0),
      durationLabel: str(rec, ['duration_label'], ''),
      points: numOf(rec, ['points'], 0),
    };
  });
  const formats = toList<unknown>(pick(r, ['accepted_file_formats'], []))
    .map((x) => String(x).trim().replace(/^\./, '').toLowerCase())
    .filter(Boolean);
  return {
    ticketTypes: types,
    hideTicketTitle: bool(r, ['hide_ticket_title'], false),
    acceptedFileFormats: formats,
    canSetCollaborators: bool(asRecord(r.fields), ['collaborators'], false),
    canCreate: bool(r, ['can_create'], true),
  };
}

/** GET /me/tickets/people → the picker's page of people. */
export function mapTicketPeople(data: unknown): TicketPerson[] {
  return toList<unknown>(pick(asRecord(data), ['people'], []))
    .map(mapTicketPerson)
    .filter((p): p is TicketPerson => p !== null);
}

/** GET /me/tickets/{id}/mentionables → who a reply may @mention. */
export function mapTicketMentionables(data: unknown): TicketMentionable[] {
  return toList<unknown>(pick(asRecord(data), ['members'], [])).flatMap((m) => {
    const person = mapTicketPerson(m);
    if (!person) return [];
    const token = str(asRecord(m), ['token'], '') || `@[${person.name} :${person.id}]`;
    return [{ ...person, token }];
  });
}

/** GET /me/tickets/{id}/templates → the "Insert template" choices. */
export function mapTicketTemplates(data: unknown): TicketTemplateOption[] {
  return toList<Json>(pick(asRecord(data), ['templates'], [])).map((t, i) => {
    const rec = asRecord(t);
    return {
      id: str(rec, ['id'], String(i)),
      title: str(rec, ['title'], ''),
      ticketType: str(rec, ['ticket_type'], ''),
      isPrivate: bool(rec, ['private'], false),
      html: str(rec, ['description_html', 'description'], ''),
    };
  });
}

/** GET /me/tickets/knowledge-base → the "Insert knowledge base" choices. */
export function mapTicketKbOptions(data: unknown): TicketKbOption[] {
  return toList<Json>(pick(asRecord(data), ['articles'], [])).map((a, i) => {
    const rec = asRecord(a);
    return {
      id: str(rec, ['id'], String(i)),
      title: str(rec, ['title'], ''),
      category: str(rec, ['category'], ''),
      html: str(rec, ['description_html', 'description'], ''),
    };
  });
}

/** The URL out of `GET …/tickets/{id}/messages/{messageId}/files/{index}`. '' when absent. */
export function mapTicketFileLink(data: unknown): string {
  const rec = asRecord(domainBody(data, ['file']));
  return uploadedFileUrl(pick<unknown>(rec, ['url', 'download_url'], ''));
}

type TextLikeValue = string | number;
function toTextLike(value: unknown): TextLikeValue | null {
  if (typeof value === 'string' || typeof value === 'number') return value;
  return null;
}

function uniqueList(values: string[]): string[] {
  const set = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    const clean = value.trim();
    if (!clean || set.has(clean)) continue;
    set.add(clean);
    out.push(clean);
  }
  return out;
}

function toPersonId(raw: unknown): string {
  if (typeof raw === 'string' || typeof raw === 'number') return String(raw);
  if (!raw || typeof raw !== 'object') return '';
  const rec = asRecord(raw);
  return str(rec, ['id', 'user_id', 'uuid', '_id', 'member_id', 'person_id'], '').trim();
}

function toPersonName(raw: unknown): string {
  if (typeof raw === 'string') return raw.trim();
  if (typeof raw === 'number') return String(raw);
  if (!raw || typeof raw !== 'object') return '';
  const rec = asRecord(raw);
  const full = str(rec, ['name', 'full_name', 'display_name', 'label'], '').trim();
  if (full) return full;
  const first = str(rec, ['first_name'], '').trim();
  const last = str(rec, ['last_name'], '').trim();
  return `${first} ${last}`.trim();
}

function parsePersonIds(raw: unknown): string[] {
  const values = toList<TextLikeValue>(raw);
  if (!values.length && raw != null && typeof raw !== 'object') {
    const fallback = toTextLike(raw);
    return fallback ? [String(fallback)] : [];
  }
  return uniqueList(values.map(toPersonId));
}

function parsePersonNames(raw: unknown): string[] {
  const values = toList<TextLikeValue>(raw);
  if (!values.length && typeof raw === 'string') {
    return raw.trim() ? [raw.trim()] : [];
  }
  return uniqueList(values.map((value) => (typeof value === 'string' ? value.trim() : toPersonName(value))).filter(Boolean));
}

function parseDisciplinaryFiles(raw: unknown): DisciplinaryFileVM[] {
  return toList<Json>(raw).map((f, i) => {
    const rec = asRecord(f);
    return {
      id: str(rec, ['id', 'uuid', '_id', 'name'], String(i)),
      name: str(rec, ['file_name', 'name', 'label'], `Attachment ${i + 1}`),
      // Resolved against the API origin like every other attachment mapper —
      // a relative `/storage/...` path used to be passed through raw here and
      // rendered as a dead link.
      url: uploadedFileUrl(pick<unknown>(rec, ['url', 'download_url', 'path'], '')),
    };
  });
}

/**
 * A comment's own attachments (`messages[].files`, `{ file_name, url }`), resolved against
 * the API origin like the case's own. The server sends no file id, so each is keyed by its
 * message and place (`msg-<message>-<n>`), apart from the case's own files, which are keyed
 * by their server id, uuid or name (their index only when the server sends none).
 */
function parseDisciplinaryMessageFiles(raw: unknown, messageId: string): DisciplinaryFileVM[] {
  return toList<unknown>(raw).map((file, i) => {
    const rec = asRecord(file);
    return {
      id: `msg-${messageId}-${i}`,
      name: complianceText(rec.file_name) || complianceText(rec.name) || `Attachment ${i + 1}`,
      url: uploadedFileUrl(rec.url),
    };
  });
}

function parseDisciplinaryMessages(data: Json, fallbackText: string): DisciplinaryMessageVM[] {
  const rawMessages = toList<Json>(pick(data, ['messages', 'thread', 'history', 'replies'], []));
  const mapped = rawMessages.map((m, i): DisciplinaryMessageVM => {
    const rec = asRecord(m);
    // Read the timestamp ONCE — the relative label and the sortable instant
    // must come from the same field.
    const at = pick<unknown>(rec, ['sent_at', 'created_at', 'timestamp'], null);
    const id = idOf(rec, i);
    return {
      id,
      author: str(rec, ['author', 'sender', 'from'], 'System'),
      authorName: str(rec, ['author_name', 'sender_name', 'created_by_name', 'name'], 'System'),
      body: str(rec, ['body', 'message', 'text', 'content', 'description'], ''),
      timeLabel: formatRelative(at),
      sentAt: toMillis(at),
      // Compliance 1.6.0's per-message flags. Only an explicit `is_public: false` hides a
      // note from the subject; a server that sends none shows every note it sends.
      authorId: complianceIdText(rec.author_id),
      isMine: complianceFlag(rec.is_mine),
      isPublic: rec.is_public !== false,
      isSystem: complianceFlag(rec.is_system),
      isAcknowledgement: complianceFlag(rec.is_acknowledgement),
      files: parseDisciplinaryMessageFiles(rec.files, id),
    };
  });
  if (mapped.length) return mapped;
  const body = stripHtml(fallbackText);
  if (!body) return [];
  // No real notes: synthesise one from the case description, dated by the case
  // itself so it still lands under a correct day heading.
  //
  // Two `pick`s, not one list: `pick` stops at the first key that is neither
  // undefined nor null, and the euapi emits `created_at: ''` for an undated
  // case — so `['created_at', 'date_occurred']` would return that empty string
  // and the `date_occurred` fallback could never be reached. Take the first
  // NON-EMPTY of the two instead.
  const caseAt =
    pick<unknown>(data, ['created_at'], null) || pick<unknown>(data, ['date_occurred'], null);
  return [
    {
      id: 'description',
      author: 'System',
      authorName: 'Case details',
      body,
      timeLabel: formatRelative(caseAt),
      sentAt: toMillis(caseAt),
      // Made by this mapper, not written by a person: the system's, public, nothing attached.
      authorId: '',
      isMine: false,
      isPublic: true,
      isSystem: true,
      isAcknowledgement: false,
      files: [],
    },
  ];
}

export interface DisciplinaryFileVM {
  id: string;
  name: string;
  url: string;
}

export interface DisciplinaryMessageVM {
  id: string;
  author: string;
  authorName: string;
  body: string;
  /**
   * Relative label, e.g. "2h ago" / "Sep 12" — clockless past a week, which is
   * the wrong trade for an audit record. NOT rendered any more: the notes list
   * renders {@link sentAt} through `withDaySeparators` instead.
   *
   * It survives as that helper's fallback — though note the fallback is nearly
   * vacuous, since this and {@link sentAt} come from the same value through the
   * same `toDate`: when the server could not date the note, BOTH are blank and
   * the view shows a placeholder.
   */
  timeLabel: string;
  /** Sortable instant, or 0 when the server sent nothing parseable. */
  sentAt: number;
  /** The writer's user id ('' when the server sent none, or for the "Case details" note). */
  authorId: string;
  isMine: boolean;
  /** Seen by everyone on the case; false: compliance officers only. A missing flag reads as public. */
  isPublic: boolean;
  /** Written by the system (the case opened, a status changed). */
  isSystem: boolean;
  isAcknowledgement: boolean;
  /**
   * The comment's own attachments (`messages[].files`, `{ file_name, url }`). The server
   * sends no file id, so each is keyed by its message and place (`msg-<message>-<n>`),
   * apart from the case's own files (keyed by their server id, uuid or name; their index
   * only when the server sends none).
   */
  files: DisciplinaryFileVM[];
}

export interface DisciplinaryCaseVM {
  id: string;
  status: string;
  categoryId: string;
  categoryName: string;
  description: string;
  dateOccurred: string;
  dateLabel: string;
  createdAt: string;
  updatedAt: string;
  createdLabel: string;
  updatedLabel: string;
  subjectUserIds: string[];
  participantUserIds: string[];
  complianceOfficerUserIds: string[];
  subjectUserNames: string[];
  participantUserNames: string[];
  complianceOfficerUserNames: string[];
  files: DisciplinaryFileVM[];
  messages: DisciplinaryMessageVM[];
}

export function mapDisciplinary(data: unknown, i = 0): DisciplinaryCaseVM {
  const r = domainBody(data, ['disciplinary']);
  const status = str(r, ['status'], 'open').toLowerCase();
  const description = str(r, ['description', 'details', 'notes', 'body'], '');
  const createdAt = str(r, ['created_at', 'created'], '');
  const updatedAt = str(r, ['updated_at', 'updated'], '');
  return {
    id: idOf(r, i),
    status,
    categoryId: str(r, ['category_id'], ''),
    categoryName: str(r, ['category_name', 'category', 'type'], 'Disciplinary'),
    description: stripHtml(description),
    dateOccurred: str(r, ['date_occurred', 'date', 'occurred_at'], ''),
    dateLabel: formatDate(pick<unknown>(r, ['date_occurred', 'date', 'occurred_at'], null)),
    createdAt,
    updatedAt,
    createdLabel: formatRelative(pick<unknown>(r, ['created_at', 'created'], null)),
    updatedLabel: formatRelative(pick<unknown>(r, ['updated_at', 'updated'], null)),
    subjectUserIds: uniqueList(
      parsePersonIds(
        pick(r, ['subject_user_ids', 'subject_users', 'subject_user', 'subjects'], []),
      ),
    ),
    participantUserIds: uniqueList(
      parsePersonIds(
        pick(r, ['participant_user_ids', 'participants_user_ids', 'participants', 'participant_user_ids'], []),
      ),
    ),
    complianceOfficerUserIds: uniqueList(
      parsePersonIds(
        pick(r, ['compliance_officer_user_ids', 'compliance_officers', 'officers', 'compliance_officer_ids'], []),
      ),
    ),
    subjectUserNames: uniqueList(
      parsePersonNames(pick(r, ['subject_user_names', 'subject_users', 'subject_names'], [])),
    ),
    participantUserNames: uniqueList(
      parsePersonNames(pick(r, ['participant_names', 'participants', 'participant_user_names'], [])),
    ),
    complianceOfficerUserNames: uniqueList(
      parsePersonNames(pick(r, ['compliance_officer_names', 'compliance_officers', 'officer_names'], [])),
    ),
    files: parseDisciplinaryFiles(pick(r, ['files', 'attachments'], [])),
    messages: parseDisciplinaryMessages(r, description),
  };
}

/**
 * One case as `GET /me/disciplinary/{id}` (and the answer to a reply or an acknowledgement)
 * sends it: the row, plus what only the detail carries — ERPat Compliance 1.6.0,
 * modules/Compliance/specs/employee-self-service-implementation-plan.md §3.2. A list row
 * never has these, which is why they are not on {@link DisciplinaryCaseVM}.
 */
export interface DisciplinaryCaseDetailVM extends DisciplinaryCaseVM {
  /** What the server lets the caller do; every flag false when it sent no `capabilities`. */
  capabilities: DisciplinaryCapabilities;
  /** True when the server sent a `capabilities` block (Compliance 1.6.0+); false on an older server. */
  capabilitiesFromServer: boolean;
  /** When the caller acknowledged the case ('' when they have not, or the server sent none). */
  myAcknowledgedAt: string;
  myAcknowledgedLabel: string;
  /** The corrective actions linked to the case that the caller owns or that are about them. */
  correctiveActions: CorrectiveActionVM[];
}

/** `{ disciplinary: Detail }` (or the bare case) → the case. Throws when the server sent none. */
export function mapDisciplinaryDetail(data: unknown): DisciplinaryCaseDetailVM {
  const r = domainBody(data, ['disciplinary']);
  if (!complianceIdText(r.id)) throw new Error('The server did not return the case.');
  const caps = r.capabilities;
  return {
    ...mapDisciplinary(r),
    capabilities: mapDisciplinaryCapabilities(caps),
    capabilitiesFromServer: !!caps && typeof caps === 'object' && !Array.isArray(caps),
    myAcknowledgedAt: complianceText(r.my_acknowledged_at),
    myAcknowledgedLabel: complianceDateLabel(r.my_acknowledged_at),
    correctiveActions: toList<unknown>(r.corrective_actions).map((row, i) => mapCorrectiveActionRow(row, i)),
  };
}

// ── Notes ────────────────────────────────────────────────────────────────────
export interface NoteVM {
  id: string;
  title: string;
  /** Server-side category — what the web module's Notes/Scribbles/Journal tabs switch on. */
  type: NoteType;
  body: string;
  tags: string[];
  createdAtMs: number;
  createdAtLabel: string;
  /** Last edit, or 0 when the note has never been edited. */
  updatedAtMs: number;
  /** Relative label for {@link updatedAtMs}, or '' when never edited. */
  updatedAtLabel: string;
  pinned: boolean;
  archived: boolean;
  /**
   * Files attached in ERPat. Read on the LIST row as well as the detail: the
   * web module shows a FILES column on its list, so the row is a plausible
   * carrier even when the detail payload omits them.
   */
  attachments: NoteAttachmentVM[];
  /**
   * Whether this note carries a re-editable drawing scene.
   *
   * The listing deliberately does NOT carry the scenes themselves — one drawing
   * holds up to 400 shapes and a page returns 25 notes — so this flag is how a
   * list knows which scribbles are worth fetching in full.
   */
  hasScribbleScene: boolean;
}
/**
 * A note's labels, as people should read them.
 *
 * ERPat's `labels` CSV mixes forms: titles written by Syntry and registry IDS
 * applied in the web ("Journal,143"). Ids are resolved back to their title
 * where the tenant's mapping is known, and dropped otherwise — a bare number is
 * not a label, and showing it (as "143") is worse than showing nothing.
 */
function readableLabels(value: unknown): string[] {
  return splitLabels(value)
    .map((tag) => tag.trim())
    .filter(Boolean)
    .map((tag) => (/^\d+$/.test(tag) ? labelTitleFor(tag) : tag))
    .filter(Boolean);
}

/**
 * The registry ids {@link readableLabels} had to DROP — ids this tenant has no
 * mapping for.
 *
 * Hiding them is a presentation decision; losing them is not. An edit writes
 * the labels back wholesale, so a note whose only ERPat label is an unmapped id
 * would be stripped of it by a title-only change. Callers that save must carry
 * these through untouched.
 */
function unresolvedLabelIds(value: unknown): string[] {
  return splitLabels(value)
    .map((tag) => tag.trim())
    .filter((tag) => /^\d+$/.test(tag) && !labelTitleFor(tag));
}

/**
 * The note categories ERPat stores natively on `notes`.`type` — the same three
 * the web module tabs between (Notes / Scribbles / Journal). Fixed at creation:
 * `PATCH /me/notes/{id}` rejects `type` with a 422 rather than ignoring it.
 *
 * There is deliberately no `meeting` here; that kind is a Syntry-side label on
 * top of a `note` (see NotesPage.kindOf).
 */
export type NoteType = 'note' | 'scribble' | 'journal';

const NOTE_TYPES: readonly string[] = ['note', 'scribble', 'journal'];

/**
 * A note's server-side category, defaulting to 'note'.
 *
 * Anything unrecognised (or absent, on a backend predating the column) reads as
 * a plain note rather than throwing — the same fallback the API itself applies.
 */
function noteTypeOf(rec: Json): NoteType {
  const raw = str(rec, ['type', 'note_type'], '').trim().toLowerCase();
  return NOTE_TYPES.includes(raw) ? (raw as NoteType) : 'note';
}

/**
 * When the note was last edited, or 0 / '' when it never has been.
 *
 * ERPat sends `updated_at_utc` (null until the first edit) alongside an already
 * localised `updated_at_local`; the local one is read for display so it lines up
 * with `created_at`, which the API also converts before sending.
 */
function noteUpdatedAt(rec: Json): { updatedAtMs: number; updatedAtLabel: string } {
  const updated = pick<unknown>(rec, ['updated_at_local', 'updated_at'], null);
  const updatedAtMs = toMillis(updated);
  return { updatedAtMs, updatedAtLabel: updatedAtMs ? formatRelative(updated) : '' };
}

export function mapNotes(data: unknown): NoteVM[] {
  return toList(data).map((n, i) => mapNote(n, i));
}

/**
 * Map one note. The end-user API stores the body under `description` (often
 * HTML) and tags under `labels` — a comma-separated string, not an array — so
 * we strip the markup and split the labels. A single-note response wraps the
 * row under `note`; list rows are already flat.
 */
export function mapNote(data: unknown, index = 0): NoteVM {
  const r = domainBody(data, ['note']);
  const created = pick<unknown>(r, ['created_at', 'date_created'], null);
  return {
    id: idOf(r, index),
    title: str(r, ['title', 'subject'], ''),
    type: noteTypeOf(r),
    body: stripHtml(pick(r, ['description', 'body', 'content', 'text'], '')),
    tags: readableLabels(pick<unknown>(r, ['labels', 'tags'], '')),
    createdAtMs: toMillis(created),
    createdAtLabel: formatRelative(created),
    ...noteUpdatedAt(r),
    pinned: bool(r, ['pinned', 'is_pinned']),
    archived: bool(r, ['archived', 'is_archived']),
    attachments: mapNoteAttachments(r),
    hasScribbleScene: bool(r, ['has_scribble_scene']),
  };
}

/**
 * Detail view model for a single note (GET /me/notes/{id} — the "view" API).
 *
 * Unlike {@link NoteVM} (list rows), the detail keeps the **raw** body so the
 * modal can render the note's rich text (HTML composed in the web app) instead
 * of a flattened preview — sanitize it before binding with `v-html`. It also
 * carries an absolute date alongside the relative label.
 */
export interface NoteDetailVM {
  id: string;
  title: string;
  /** Server-side category. Fixed at creation — PATCH rejects a change with 422. */
  type: NoteType;
  /** Raw body (may be HTML or plain text) — sanitize before rendering. */
  body: string;
  tags: string[];
  /**
   * Registry ids on this note that no known mapping resolves to a title.
   * Deliberately absent from `tags` (a bare number is not a readable label) but
   * kept here so the edit modal can write them back instead of destroying them.
   */
  unresolvedLabelIds: string[];
  createdAtMs: number;
  /** Relative label, e.g. "2 days ago". */
  createdAtLabel: string;
  /** Absolute date, e.g. "Jun 12, 2026". */
  createdAtDate: string;
  /** Last edit, or 0 when the note has never been edited. */
  updatedAtMs: number;
  /** Relative label for {@link updatedAtMs}, or '' when never edited. */
  updatedAtLabel: string;
  pinned: boolean;
  archived: boolean;
  /**
   * Files attached in ERPat, read defensively across every plausible key.
   *
   * As of 2026-09-02 the endpoint does send these: a `files[]` of
   * `{ file_name, file_size, is_image, url }`, resolved server-side from the
   * same storage the web Notes editor writes. They remain READ-only — creating
   * an attachment from a client still has no endpoint.
   */
  attachments: NoteAttachmentVM[];
  /** Whether a re-editable drawing scene exists — see {@link NoteVM.hasScribbleScene}. */
  hasScribbleScene: boolean;
  /**
   * The re-editable drawing behind a scribble, or null.
   *
   * Only ever present on a scribble, and only when one was drawn on a pad: a
   * photographed whiteboard or a pasted screenshot is a picture with no shapes
   * behind it, and reads as null here.
   */
  scribbleScene: SketchScene | null;
}

/**
 * Map a single-note "view" response into {@link NoteDetailVM}. Shares the
 * key spellings + label splitting with {@link mapNote}, but preserves the raw
 * body (the modal renders it as sanitized rich text).
 */
export function mapNoteDetail(data: unknown): NoteDetailVM {
  const r = domainBody(data, ['note']);
  const created = pick<unknown>(r, ['created_at', 'date_created'], null);
  return {
    id: idOf(r),
    title: str(r, ['title', 'subject'], ''),
    type: noteTypeOf(r),
    body: str(r, ['description', 'body', 'content', 'text'], ''),
    tags: readableLabels(pick<unknown>(r, ['labels', 'tags'], '')),
    unresolvedLabelIds: unresolvedLabelIds(pick<unknown>(r, ['labels', 'tags'], '')),
    createdAtMs: toMillis(created),
    createdAtLabel: formatRelative(created),
    createdAtDate: formatDate(created),
    ...noteUpdatedAt(r),
    pinned: bool(r, ['pinned', 'is_pinned']),
    archived: bool(r, ['archived', 'is_archived']),
    attachments: mapNoteAttachments(r),
    hasScribbleScene: bool(r, ['has_scribble_scene']),
    // Re-validated on the way in, exactly as the API re-validates on the way
    // out: a row written before the validator existed yields null rather than
    // something the pad would replay into a broken drawing.
    scribbleScene: parseScene(pick<unknown>(r, ['scribble_data'], null)),
  };
}

/** Normalise the API's `labels` (comma-separated string) or a tags array into a clean string[]. */
function splitLabels(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((t) => String(t).trim()).filter(Boolean);
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
  }
  return [];
}

// ── Timeline (activity feed / profile posts) ──────────────────────────────────
// Verified shape (GET /me/timeline → { posts: [...], meta }):
//   { id, description, created_by, created_by_user, author_image, total_replies,
//     is_public, files: [{file_name,file_size,url}], reactions: {total,mine,breakdown},
//     created_at }.
// The euapi resolves the author avatar + attachment URLs server-side and folds a
// per-post reaction summary in, so the feed renders avatars, file chips and a like
// button without extra round-trips.

/** One attachment on a post (resolved to a servable URL server-side). */
export interface TimelineFileVM {
  name: string;
  url: string;
  /** Bytes when the server sent `file_size`; 0 = unknown. */
  size: number;
}

/** Compact reaction summary for a post — the like count plus the caller's own state. */
export interface TimelineReactionsVM {
  /** Running total of all reactions on the post. */
  total: number;
  /** The caller's own reaction key ('' when they haven't reacted). */
  mine: string;
  /** Convenience flag: the caller has reacted. */
  reacted: boolean;
}

export interface TimelinePostVM {
  id: string;
  body: string;
  /** Numeric author id (`created_by`), as a string — used to spot the caller's own posts. */
  authorId: string;
  authorName: string;
  authorInitials: string;
  /** Absolute author avatar URL, or '' — render initials when empty. */
  authorImage: string;
  /**
   * The author has left the company (server-decided: an inactive or deleted staff
   * account). {@link authorName} is "Resigned Employee #<job id>" and
   * {@link authorImage} is ''; render the neutral placeholder, never open a profile.
   */
  authorSeparated: boolean;
  /** Relative label, e.g. "2h ago". */
  timeLabel: string;
  /** Absolute date, e.g. "Jun 12, 2026". */
  dateLabel: string;
  createdAtMs: number;
  replies: number;
  isPublic: boolean;
  files: TimelineFileVM[];
  reactions: TimelineReactionsVM;
}

/** Parse the euapi `files` array (or a legacy serialized shape) into file VMs. */
function mapTimelineFiles(raw: unknown): TimelineFileVM[] {
  return toList<Json>(raw)
    .map((f, i) => {
      const rec = asRecord(f);
      const size = pick<unknown>(rec, ['file_size', 'size'], 0);
      return {
        name: str(rec, ['file_name', 'name', 'label'], `Attachment ${i + 1}`),
        // Resolved against the API origin like every other attachment mapper —
        // a relative `/storage/...` path used to be passed through raw here.
        url: uploadedFileUrl(pick<unknown>(rec, ['url', 'download_url', 'path'], '')),
        size: typeof size === 'number' && Number.isFinite(size) ? size : 0,
      };
    })
    .filter((f) => f.name);
}

/** Parse the euapi `reactions` block ({total, mine, breakdown}) into a reactions VM. */
function mapTimelineReactions(raw: unknown): TimelineReactionsVM {
  const rec = asRecord(raw);
  const mine = str(rec, ['mine', 'current_user_reaction'], '');
  return {
    total: pick<number>(rec, ['total', 'total_reactions'], 0),
    mine,
    reacted: mine !== '',
  };
}

/** Map one timeline post. A single-post response wraps the row under `post`; list rows are flat. */
export function mapTimelinePost(data: unknown, index = 0): TimelinePostVM {
  const r = domainBody(data, ['post']);
  const authorName = str(r, ['created_by_user', 'author_name', 'author', 'name'], '');
  const created = pick<unknown>(r, ['created_at', 'date_created', 'posted_at'], null);
  const authorSeparated = bool(r, ['author_separated']);
  return {
    id: idOf(r, index),
    body: str(r, ['description', 'body', 'content', 'message', 'text'], ''),
    authorId: str(r, ['created_by', 'author_id', 'user_id'], ''),
    authorName,
    authorInitials: str(r, ['initials'], '') || initialsOf(authorName),
    authorImage: authorSeparated
      ? ''
      : profileImageUrl(pick<unknown>(r, ['author_image', 'author_avatar', 'avatar', 'image'], null)),
    authorSeparated,
    timeLabel: formatRelative(created),
    dateLabel: formatDate(created),
    createdAtMs: toMillis(created),
    replies: pick<number>(r, ['total_replies', 'replies_count', 'comment_count', 'replies'], 0),
    isPublic: bool(r, ['is_public', 'public'], false),
    files: mapTimelineFiles(pick<unknown>(r, ['files', 'attachments'], [])),
    reactions: mapTimelineReactions(pick<unknown>(r, ['reactions'], {})),
  };
}

export function mapTimelinePosts(data: unknown): TimelinePostVM[] {
  return toList(data).map((p, i) => mapTimelinePost(p, i));
}

// A comment (reply) on a post — the euapi returns these under `comments` in the
// single-post view, each shaped like a lightweight post (no reactions/files).
export interface TimelineCommentVM {
  id: string;
  body: string;
  authorId: string;
  authorName: string;
  authorInitials: string;
  authorImage: string;
  /** See {@link TimelinePostVM.authorSeparated}. */
  authorSeparated: boolean;
  timeLabel: string;
  createdAtMs: number;
}

export function mapTimelineComment(data: unknown, index = 0): TimelineCommentVM {
  const r = asRecord(data);
  const authorName = str(r, ['created_by_user', 'author_name', 'author', 'name'], '');
  const created = pick<unknown>(r, ['created_at', 'date_created', 'posted_at'], null);
  const authorSeparated = bool(r, ['author_separated']);
  return {
    id: idOf(r, index),
    body: str(r, ['description', 'body', 'content', 'message', 'text'], ''),
    authorId: str(r, ['created_by', 'author_id', 'user_id'], ''),
    authorName,
    authorInitials: str(r, ['initials'], '') || initialsOf(authorName),
    authorImage: authorSeparated
      ? ''
      : profileImageUrl(pick<unknown>(r, ['author_image', 'author_avatar', 'avatar', 'image'], null)),
    authorSeparated,
    timeLabel: formatRelative(created),
    createdAtMs: toMillis(created),
  };
}

/** Map the POST /me/timeline/{id}/reactions response ({ reactions: {...} }). */
export function mapTimelineReactionResponse(data: unknown): TimelineReactionsVM {
  return mapTimelineReactions(domainBody(data, ['reactions']));
}

/** The single-post view: the post + its mapped comments. */
export interface TimelinePostDetailVM {
  post: TimelinePostVM;
  comments: TimelineCommentVM[];
}

export function mapTimelinePostDetail(data: unknown): TimelinePostDetailVM {
  const body = domainBody(data, []);
  return {
    post: mapTimelinePost(data),
    comments: toList<Json>(pick(body, ['comments'], [])).map((c, i) => mapTimelineComment(c, i)),
  };
}

// ── Departments (org directory) ───────────────────────────────────────────────
// Built from GET /me/teams (id + title only) and GET /me/teams/{id}/members
// (id, first_name, last_name, image, job_title). The end-user API exposes neither
// an explicit unit *type* (team vs group) nor a designated *head*, so both are
// derived client-side: the type from keywords in the title, the head from the
// first member whose job title reads like a lead role. `location` is read
// defensively so it lights up if the backend ever adds it; today it resolves ''.
export type DepartmentKind = 'team' | 'group';

export interface DepartmentMemberVM {
  id: string;
  name: string;
  initials: string;
  /** Absolute avatar URL, or '' — render with the initials fallback. */
  image: string;
  jobTitle: string;
  /** Work email, or '' when the API doesn't expose it. */
  email: string;
  /** True for the member heuristically identified as the unit's head. */
  isHead: boolean;
}

export interface DepartmentVM {
  id: string;
  title: string;
  kind: DepartmentKind;
  /** Capitalised `kind` for the chip ('Team' | 'Group'). */
  kindLabel: string;
  /** Office/site label, or '' when the API doesn't provide one. */
  location: string;
  /** Free-text description, or '' — shown in the detail modal when present. */
  description: string;
  /** Formatted creation date, or '' when the API doesn't provide one. */
  createdAtLabel: string;
  /** The heuristically-detected head, or null when none stands out. */
  head: DepartmentMemberVM | null;
  /** Every member (the head included), in the order returned. */
  members: DepartmentMemberVM[];
  memberCount: number;
}

/** Job titles that read like the person leads the unit. */
const HEAD_TITLE_RE = /\b(head|lead|manager|supervisor|director|chief|officer|president|owner|founder|principal|coordinator)\b/i;
/** Titles naming a cross-functional group rather than a standing team. */
const GROUP_TITLE_RE = /\b(group|squad|guild|committee|council|circle|leaders?|board|chapter)\b/i;

function classifyDepartment(title: string): DepartmentKind {
  return GROUP_TITLE_RE.test(title) ? 'group' : 'team';
}

function mapDepartmentMember(value: unknown, index: number): DepartmentMemberVM {
  const r = asRecord(value);
  const name =
    `${str(r, ['first_name'], '')} ${str(r, ['last_name'], '')}`.replace(/\s+/g, ' ').trim() ||
    str(r, ['name', 'full_name', 'display_name'], '');
  return {
    id: idOf(r, index),
    name,
    initials: str(r, ['initials'], '') || initialsOf(name),
    image: profileImageUrl(pick<unknown>(r, ['image', 'avatar', 'photo', 'image_url', 'avatar_url'], null)),
    jobTitle: str(r, ['job_title', 'position', 'title', 'role'], ''),
    email: str(r, ['email', 'work_email', 'email_address'], ''),
    isHead: false,
  };
}

/**
 * Assemble one department/team/group card model from its `/me/teams` row and the
 * `/me/teams/{id}/members` list. The first member whose job title matches
 * {@link HEAD_TITLE_RE} is flagged as the head (and surfaced as `head`).
 */
export function mapDepartment(team: unknown, membersData: unknown, index = 0): DepartmentVM {
  const r = asRecord(team);
  const title = str(r, ['title', 'name', 'label'], `Team ${index + 1}`);
  const members = toList(membersData).map(mapDepartmentMember);

  const headIndex = members.findIndex((m) => HEAD_TITLE_RE.test(m.jobTitle));
  let head: DepartmentMemberVM | null = null;
  if (headIndex >= 0) {
    members[headIndex] = { ...members[headIndex]!, isHead: true };
    head = members[headIndex]!;
  }

  const kind = classifyDepartment(title);
  return {
    id: idOf(r, index),
    title,
    kind,
    kindLabel: titleCase(kind),
    location: str(r, ['location', 'office', 'site', 'city', 'address', 'branch'], ''),
    description: stripHtml(pick(r, ['description', 'about', 'summary', 'notes'], '')),
    createdAtLabel: formatDate(pick<unknown>(r, ['created_at', 'date_created', 'created'], null)),
    head,
    members,
    memberCount: members.length,
  };
}

// ── App Dash (server-configured mobile dashboard layout) ─────────────────────
/**
 * One widget instance on a server-configured dashboard page.
 *
 * `span` is the authoritative width signal, resolved server-side against the
 * page's `columns` and the widget registry's half-width capability flag. `size`
 * is presentational and is deliberately kept CONSISTENT with `span` by the API
 * (span 1 <=> size 'small') purely so app builds older than `span` still lay out
 * correctly. New code must read `span`, never `size`.
 *
 * `title` and `subtitle` are still IGNORED at runtime — the registry components
 * are self-labelling. They are kept on the VM so a future renderer can honour
 * them without an API or mapper change.
 */
export interface AppDashboardWidgetVM {
  id: string;
  /** Registry type key, e.g. "attendance_stats" — always a non-empty string. */
  type: string;
  size: string;
  /**
   * Column span, 1 (half width) or 2 (full width). RESOLVED SERVER-SIDE — the
   * device does no width logic and holds no copy of the widget registry. Anything
   * other than 1 on the wire (including an older backend that sends no span at
   * all) maps to 2, so a missing field can only ever produce the safe, full-width
   * layout.
   */
  span: 1 | 2;
  title: string;
  subtitle: string;
  /**
   * How the widget is placed on the dashboard.
   *
   * `inline` (the default, and everything an older backend can produce) puts it
   * in the widget flow, where `span` decides its width. `overlay` floats it
   * ABOVE the dashboard, pinned to an edge and out of the scroll flow — the
   * placement the App Dash manager previews as a banner sitting over the page.
   */
  placement: AppDashboardPlacement;
  /** Which edge an `overlay` is pinned to. Meaningless when `placement` is inline. */
  overlayAnchor: 'top' | 'bottom';
  /** The viewer may close this overlay. Meaningless when `placement` is inline. */
  dismissible: boolean;
  /** Free-form per-instance configuration (read by the configurable widgets). */
  props: Record<string, unknown>;
}

export interface AppDashboardPageVM {
  id: string;
  key: string;
  title: string;
  subtitle: string;
  /** 1 or 2. Anything else on the wire clamps to 1 (single-column). */
  columns: 1 | 2;
  widgets: AppDashboardWidgetVM[];
}

export interface AppDashboardVM {
  name: string;
  key: string;
  schemaVersion: number;
  /**
   * The tenant's chosen primary brand color, as the raw server enum string
   * (`default` | `red` | `blue` | `green` | `orange` | `yellow`). Kept as a
   * plain string here so this module stays free of theme imports — the call
   * site normalizes it via `normalizeBrandColorKey()` in `@/theme/brand-color`,
   * which collapses anything unknown (including an older backend that sends no
   * theme at all) to `default`. The server also sends a `primary_color` hex; it
   * is informational only and deliberately NOT read — the client styles
   * exclusively from its own precomputed tables and never injects a
   * server-supplied color string into CSS.
   */
  themePrimary: string;
  pages: AppDashboardPageVM[];
}

export type AppDashboardPlacement = 'inline' | 'overlay';

/**
 * Placement keys the App Dash editor has been seen to (or plausibly will) send.
 *
 * The editor is backend-side and this field is newer than the app, so it is read
 * through every spelling it could arrive under, on the widget instance AND on
 * its free-form config — the manager writes some capabilities to the instance
 * and some into the widget's own settings blob, and which one a given build uses
 * is not pinned by the OpenAPI.
 */
const PLACEMENT_KEYS = [
  'placement',
  'display',
  'display_mode',
  'render_mode',
  'mode',
  'layout',
  'position',
  'variant',
];
/** Boolean spellings of the same capability (`{ overlay: true }`). */
const OVERLAY_FLAG_KEYS = ['overlay', 'is_overlay', 'as_overlay', 'floating', 'pinned', 'sticky'];
/** Anything in this family means "float it over the page", whichever word the editor used. */
const OVERLAY_WORDS = /^(overlay|overlaid|float(ing)?|sticky|fixed|pinned|hover)$/i;

/** True for `true`, `1`, `"1"`, `"true"`, `"yes"`, `"on"` — the shapes PHP/JSON mix freely. */
function truthy(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') return /^(1|true|yes|on)$/i.test(value.trim());
  return false;
}

/**
 * Resolve a widget's placement from the instance and its config.
 *
 * Fail-safe by construction: ONLY an explicit overlay signal produces `overlay`.
 * An older backend, an unknown word, or a missing field all fall through to
 * `inline`, which is the flow every widget has always rendered in.
 */
function readPlacement(rec: Json, config: Json): AppDashboardPlacement {
  for (const source of [rec, config]) {
    const named = pick<unknown>(source, PLACEMENT_KEYS, '');
    if (typeof named === 'string' && OVERLAY_WORDS.test(named.trim())) return 'overlay';
    if (OVERLAY_FLAG_KEYS.some((key) => truthy(source[key]))) return 'overlay';
  }
  // `size` doubles as the placement slot in the editor's older shape
  // (`full` | `half` | `overlay`), so it is the last thing checked.
  const size = str(rec, ['size'], '');
  return OVERLAY_WORDS.test(size.trim()) ? 'overlay' : 'inline';
}

/** Which edge an overlay hugs. Bottom is the editor's default and the app's. */
function readOverlayAnchor(rec: Json, config: Json): 'top' | 'bottom' {
  const keys = ['overlay_position', 'overlay_anchor', 'anchor', 'align', 'vertical_align', 'edge'];
  for (const source of [rec, config]) {
    const value = str(source, keys, '');
    if (/^top$/i.test(value.trim())) return 'top';
    if (/^bottom$/i.test(value.trim())) return 'bottom';
  }
  return 'bottom';
}

/** Map one raw widget instance; `null` when it has no usable string `type`. */
function mapAppDashboardWidget(value: unknown, index: number): AppDashboardWidgetVM | null {
  const r = asRecord(value);
  const type = pick<unknown>(r, ['type', 'widget_type'], null);
  if (typeof type !== 'string' || !type.trim()) return null;
  const props = asRecord(pick<unknown>(r, ['props', 'config', 'options'], null));
  return {
    id: idOf(r, index),
    type: type.trim(),
    size: str(r, ['size'], ''),
    // Only an explicit 1 halves a widget; everything else — 2, absent, a string,
    // junk — is full width. Failing safe here is what lets an app build talk to a
    // backend that predates the field.
    span: Number(pick<unknown>(r, ['span'], 2)) === 1 ? 1 : 2,
    title: str(r, ['title'], ''),
    subtitle: str(r, ['subtitle', 'sub_title'], ''),
    placement: readPlacement(r, props),
    overlayAnchor: readOverlayAnchor(r, props),
    dismissible: [r, props].some((source) =>
      ['dismissible', 'closable', 'can_close', 'can_dismiss'].some((key) => truthy(source[key])),
    ),
    props,
  };
}

function mapAppDashboardPage(value: unknown, index: number): AppDashboardPageVM {
  const r = asRecord(value);
  return {
    id: idOf(r, index),
    key: str(r, ['key', 'page_key', 'slug'], ''),
    title: str(r, ['title', 'name'], ''),
    subtitle: str(r, ['subtitle', 'sub_title'], ''),
    columns: Number(pick<unknown>(r, ['columns'], 1)) === 2 ? 2 : 1,
    widgets: toList(pick(r, ['widgets', 'items'], []))
      .map((w, i) => mapAppDashboardWidget(w, i))
      .filter((w): w is AppDashboardWidgetVM => w !== null),
  };
}

/**
 * GET /v1/api/me/app-dashboard?app=syntry → `{ dashboard: {...} | null }` —
 * the tenant's PUBLISHED mobile dashboard layout for this app. `dashboard` is
 * `null` (mapped to `null` here) when the tenant has no published dashboard,
 * which callers treat as "use the built-in home layout".
 */
export function mapAppDashboard(data: unknown): AppDashboardVM | null {
  // The payload sits under the `dashboard` domain key; tolerate an already-flat
  // body too. A null / non-object dashboard means "none published" → null out.
  const outer = asRecord(data);
  const raw = 'dashboard' in outer ? outer.dashboard : data;
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const rec = raw as Json;

  const sv = pick<unknown>(rec, ['schema_version', 'schemaVersion'], 1);
  const schemaVersion =
    typeof sv === 'number' && Number.isFinite(sv) ? sv : Number.parseInt(String(sv), 10) || 1;

  // `theme: { primary, primary_color }` is additive — an older backend omits it
  // entirely, which falls through to 'default' (the bundled teal).
  const theme = asRecord(pick<unknown>(rec, ['theme'], {}));

  return {
    name: str(rec, ['name', 'title'], ''),
    key: str(rec, ['dashboard_key', 'key'], ''),
    schemaVersion,
    themePrimary: str(theme, ['primary', 'primary_key', 'key'], 'default') || 'default',
    pages: toList(pick(rec, ['pages'], [])).map(mapAppDashboardPage),
  };
}

// ── Banner widget (per-instance App Dash config) ──────────────────────────────
/**
 * One rendition of a banner image, already resolved to an absolute URL.
 *
 * `width` is the file's own pixel width, which is what lets the device pick a
 * rendition for its resolution (viewport width × DPR); 0 means the server
 * didn't say and the file can only serve as the plain fallback. `media` is a
 * CSS media query for an art-directed crop, '' for the default one.
 */
export interface BannerImageVM {
  url: string;
  width: number;
  media: string;
}

/**
 * The `banner` widget's instance configuration, authored in ERPat (web app /
 * App Dash manager) and delivered on the widget instance's free-form `props`.
 */
export interface BannerWidgetConfigVM {
  images: BannerImageVM[];
  alt: string;
  /** `'16:9'`, `'16/9'` or a decimal string; '' to let the image set its height. */
  ratio: string;
  /** Where a tap goes: an in-app path (`/announcements`) or an external URL. '' = not tappable. */
  link: string;
  headline: string;
  caption: string;
}

/** Every key ERPat has been seen to (or plausibly will) use for a banner image list. */
const BANNER_LIST_KEYS = ['images', 'sources', 'variants', 'renditions', 'banners', 'slides'];
/** Single-image spellings, checked when no list is present. */
const BANNER_SINGLE_KEYS = [
  'image',
  'image_url',
  'banner',
  'banner_url',
  'url',
  'src',
  'photo',
  'file',
  'file_path',
  'path',
];
/** Per-entry URL spellings inside a list item that is an object rather than a string. */
const BANNER_ENTRY_URL_KEYS = ['url', 'image', 'image_url', 'src', 'path', 'file', 'file_path'];

/**
 * Named size buckets → the pixel width they stand for.
 *
 * ERPat's uploader labels renditions by name, not by number, so `image_mobile`
 * has to become a `w` descriptor somewhere. These are the conventional widths
 * for each bucket; an explicit `width` on the entry always wins over them, and
 * a bucket is only used when the filename carries no size either.
 */
const BANNER_BUCKET_WIDTHS: { keys: string[]; width: number }[] = [
  { keys: ['image_mobile', 'mobile', 'image_sm', 'small'], width: 480 },
  { keys: ['image_tablet', 'tablet', 'image_md', 'medium'], width: 1024 },
  { keys: ['image_desktop', 'desktop', 'image_lg', 'large'], width: 1600 },
  { keys: ['image_xl', 'xlarge', 'image_retina', 'retina'], width: 2048 },
];

/**
 * Pixel width read out of the file name — `banner-960.jpg`, `hero_1280.webp`,
 * `promo-1920x1080.png`, `strip-1440w.jpg`.
 *
 * A convenience, not a contract: an admin who uploads renditions named this way
 * gets resolution switching without the App Dash form carrying a width field.
 * Bounded to plausible image widths so a date or an id in the name (`2026`,
 * `banner-7.jpg`) is not mistaken for one.
 */
function bannerWidthFromName(url: string): number {
  const name = url.split(/[?#]/)[0] ?? '';
  const match =
    /(\d{3,5})x\d{3,5}(?=\.[a-z0-9]+$)/i.exec(name) ??
    /(\d{3,5})w(?=[-_.])/i.exec(name) ??
    /[-_](\d{3,5})(?=\.[a-z0-9]+$)/i.exec(name);
  const width = Number.parseInt(match?.[1] ?? '', 10);
  return Number.isFinite(width) && width >= 120 && width <= 8192 ? width : 0;
}

/** Map one list entry (a bare URL string, or an object carrying url/width/media). */
function mapBannerImage(value: unknown): BannerImageVM | null {
  if (typeof value === 'string') {
    const url = uploadedFileUrl(value);
    return url ? { url, width: bannerWidthFromName(url), media: '' } : null;
  }
  const rec = asRecord(value);
  const url = uploadedFileUrl(pick<unknown>(rec, BANNER_ENTRY_URL_KEYS, null));
  if (!url) return null;
  const declared = Number(pick<unknown>(rec, ['width', 'w', 'image_width', 'min_width'], 0));
  const width =
    Number.isFinite(declared) && declared > 0 ? Math.round(declared) : bannerWidthFromName(url);
  return { url, width, media: str(rec, ['media', 'media_query', 'breakpoint'], '') };
}

/**
 * A link is only followed if it is an http(s) URL or an in-app path. Anything
 * else — `javascript:`, `data:`, a protocol-relative `//host` that would leave
 * the app silently — is dropped to '' and the banner simply isn't tappable.
 */
function bannerLink(value: unknown): string {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  return raw.startsWith('/') && !raw.startsWith('//') ? raw : '';
}

/**
 * Read a `banner` widget instance's config off its free-form `props`.
 *
 * Defensive in the same way every mapper here is: the App Dash editor is
 * backend-side and its field names are not pinned by the OpenAPI, so each value
 * is read through the spellings it plausibly arrives under. An unconfigured or
 * unrecognisable instance maps to zero images, which the widget renders as
 * nothing at all rather than as a broken frame.
 */
export function mapBannerWidgetConfig(props: unknown): BannerWidgetConfigVM {
  const rec = asRecord(props);

  const images: BannerImageVM[] = [];
  const seen = new Set<string>();
  const push = (image: BannerImageVM | null): void => {
    if (!image) return;
    const key = `${image.media}|${image.url}`;
    if (seen.has(key)) return;
    seen.add(key);
    images.push(image);
  };

  for (const entry of toList(pick<unknown>(rec, BANNER_LIST_KEYS, []))) push(mapBannerImage(entry));

  // Named buckets are additive: a config may pair a single `image` with an
  // `image_mobile` crop, and both belong in the same srcset.
  for (const bucket of BANNER_BUCKET_WIDTHS) {
    const value = pick<unknown>(rec, bucket.keys, null);
    if (value == null) continue;
    const image = mapBannerImage(value);
    if (image) push({ ...image, width: image.width || bucket.width });
  }

  if (images.length === 0) push(mapBannerImage(pick<unknown>(rec, BANNER_SINGLE_KEYS, null)));

  const ratioRaw = pick<unknown>(rec, ['ratio', 'aspect', 'aspect_ratio'], '');
  const ratio =
    typeof ratioRaw === 'number' && Number.isFinite(ratioRaw) && ratioRaw > 0
      ? String(ratioRaw)
      : String(ratioRaw ?? '').trim();

  return {
    images,
    alt: str(rec, ['alt', 'alt_text', 'image_alt'], ''),
    ratio,
    link: bannerLink(
      pick<unknown>(rec, ['link', 'href', 'link_url', 'target_url', 'action_url', 'cta_url'], ''),
    ),
    headline: str(rec, ['headline', 'heading', 'title'], ''),
    caption: str(rec, ['caption', 'text', 'description', 'subtitle'], ''),
  };
}

// ── helpers ──────────────────────────────────────────────────────────────────
function titleCase(s: string): string {
  return s
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

// ── Forms (answerable Marketing forms — GET /v1/api/forms[/{uuid}]) ───────────
/**
 * The custom-field types the answering UI knows how to render. Anything else is
 * treated as a plain text input (the server accepts text fields as-is).
 */
export type FormFieldType =
  | 'text'
  | 'textarea'
  | 'email'
  | 'number'
  | 'date'
  | 'select'
  | 'multi_select'
  | 'files'
  | 'label'
  | 'external_link';

export interface FormFieldVM {
  /** custom-field id (string) — the key the submit `answers` map is keyed by. */
  id: string;
  type: FormFieldType;
  title: string;
  placeholder: string;
  required: boolean;
  /** Choices for select / multi_select; empty for every other type. */
  options: string[];
  label: string;
  sort: number;
}

export interface FormSummaryVM {
  id: string;
  uuid: string;
  sysId: string;
  title: string;
  description: string;
  status: string;
  isOpen: boolean;
  isPublic: boolean;
  embedAvailable: boolean;
  createdByLabel: string;
  createdDateLabel: string;
  createdTimeLabel: string;
  updatedDateLabel: string;
  updatedTimeLabel: string;
  createdLabel: string;
}

export interface FormSubmissionStateVM {
  limited: boolean;
  max: number | null;
  count: number;
  remaining: number | null;
  canSubmit: boolean;
}

export interface FormDetailVM extends FormSummaryVM {
  acceptsGlobalAttachment: boolean;
  allowedFileTypes: string[];
  submission: FormSubmissionStateVM;
  fields: FormFieldVM[];
}

const FIELD_TYPES: readonly FormFieldType[] = [
  'text',
  'textarea',
  'email',
  'number',
  'date',
  'select',
  'multi_select',
  'files',
  'label',
  'external_link',
];

const numOr = (rec: Json, keys: string[], fallback: number): number => {
  const v = pick<unknown>(rec, keys, fallback);
  const n = typeof v === 'number' ? v : Number.parseInt(String(v), 10);
  return Number.isFinite(n) ? n : fallback;
};

// ── Celebrations (Home widgets: birthdays + work anniversaries) ────────────
/**
 * One teammate celebrating this month.
 *
 * PRIVACY: there is deliberately no birth year and no age here, because the
 * endpoint never sends them — `/v1/api/me/celebrations/*` exposes a month/day
 * label plus sortable month/day ints and nothing more, matching the Profile
 * API's blanking of `dob` for non-self. Do not add an `age` field.
 */
export interface CelebrantVM {
  id: string;
  /** "Ana Santos". */
  name: string;
  /** First name only — used by the Greet toast and aria-labels. */
  firstName: string;
  initials: string;
  /** Absolute avatar URL, or '' → the row renders initials instead. */
  image: string;
  jobTitle: string;
  /** Month + day only, e.g. "Aug 12". */
  dateLabel: string;
  /** Tenant-local calendar month (1–12) / day (1–31), for sorting. */
  month: number;
  day: number;
  /** Celebrating today — computed server-side in tenant-local time. */
  isToday: boolean;
}

export interface AnniversaryCelebrantVM extends CelebrantVM {
  /** Completed years of service. */
  years: number;
  /** "5 years" / "1 year" — '' when the payload omits or zeroes it. */
  yearsLabel: string;
}

/** A celebrations payload: the rows plus the counts and canned greetings. */
export interface CelebrationsVM<T extends CelebrantVM = CelebrantVM> {
  celebrants: T[];
  /** Celebrants this month BEFORE any server-side limit — drives the chip. */
  total: number;
  /** Tenant-local month the payload describes (1–12). */
  month: number;
  /**
   * The tenant's canned greeting strings, served by the API rather than kept
   * client-side so an edited `config/greetings.php` can't drift from the app.
   */
  greetings: string[];
}

const MONTH_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

function mapCelebrant(value: unknown, index: number): CelebrantVM {
  const r = asRecord(value);
  const name =
    `${str(r, ['first_name'], '')} ${str(r, ['last_name'], '')}`.replace(/\s+/g, ' ').trim() ||
    str(r, ['name', 'full_name', 'display_name'], '');

  const month = numOr(r, ['month', 'celebration_month', 'birth_month'], 0);
  const day = numOr(r, ['day', 'celebration_day', 'birth_day'], 0);

  // Prefer the server's preformatted label; only derive one if it's missing.
  // Deliberately NOT formatDate() — that renders a year, which is exactly what
  // this feature must never show.
  let dateLabel = str(r, ['date_label', 'birthday_label', 'anniversary_label', 'label'], '');
  if (!dateLabel && month >= 1 && month <= 12 && day >= 1) {
    dateLabel = `${MONTH_SHORT[month - 1]} ${day}`;
  }

  return {
    id: str(r, ['user_id', 'id', 'uuid'], String(index)),
    name,
    firstName: str(r, ['first_name'], '') || name.split(/\s+/)[0] || '',
    initials: str(r, ['initials'], '') || initialsOf(name) || '?',
    image: profileImageUrl(pick<unknown>(r, ['avatar_url', 'image', 'avatar', 'photo', 'image_url'], null)),
    jobTitle: str(r, ['job_title', 'position', 'title', 'role'], ''),
    dateLabel,
    month,
    day,
    isToday: bool(r, ['is_today', 'today'], false),
  };
}

/**
 * Read the celebrant rows explicitly rather than via `toList()`: the payload
 * carries `greetings` (also an array), and toList() returns the first non-meta
 * array it finds, so key order would silently decide the result.
 */
function celebrantRows(data: unknown): unknown[] {
  const rec = asRecord(data);
  const rows = rec['celebrants'] ?? rec['birthdays'] ?? rec['anniversaries'];
  if (Array.isArray(rows)) return rows;
  return Array.isArray(data) ? data : [];
}

function celebrationsEnvelope(data: unknown): Omit<CelebrationsVM, 'celebrants'> {
  const rec = asRecord(data);
  const greetings = pick<unknown>(rec, ['greetings'], []);
  return {
    total: numOr(rec, ['total'], celebrantRows(data).length),
    month: numOr(rec, ['month'], 0),
    greetings: Array.isArray(greetings) ? greetings.map((g) => String(g)).filter(Boolean) : [],
  };
}

/** GET /me/celebrations/birthdays → `{ celebrants, total, month, greetings }`. */
export function mapBirthdayCelebrants(data: unknown): CelebrationsVM {
  return { ...celebrationsEnvelope(data), celebrants: celebrantRows(data).map(mapCelebrant) };
}

/** GET /me/celebrations/anniversaries → adds years of service per row. */
export function mapAnniversaryCelebrants(data: unknown): CelebrationsVM<AnniversaryCelebrantVM> {
  const celebrants = celebrantRows(data).map((row, i) => {
    const years = numOr(asRecord(row), ['years', 'years_of_service', 'service_years'], 0);
    return {
      ...mapCelebrant(row, i),
      years,
      yearsLabel: years > 0 ? `${years} year${years === 1 ? '' : 's'}` : '',
    };
  });
  return { ...celebrationsEnvelope(data), celebrants };
}

/** A string list from either an array payload or a comma-separated string. */
function stringList(v: unknown): string[] {
  if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean);
  if (typeof v === 'string') return v.split(',').map((x) => x.trim()).filter(Boolean);
  return [];
}

function formPersonLabel(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number') return stripHtml(String(value));
  const rec = asRecord(value);
  const first = str(rec, ['first_name', 'firstname', 'given_name'], '');
  const last = str(rec, ['last_name', 'lastname', 'surname'], '');
  const full = [first, last].filter(Boolean).join(' ').trim();
  return full || str(rec, ['name', 'full_name', 'display_name', 'username', 'email'], '');
}

function hasFormEmbed(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return Boolean(normalized) && !['0', 'false', 'no', 'off', '-'].includes(normalized);
  }
  if (Array.isArray(value)) return value.length > 0;
  return Object.keys(asRecord(value)).length > 0;
}

function mapFormField(raw: unknown, idx = 0): FormFieldVM {
  const r = asRecord(raw);
  const rawType = str(r, ['type', 'field_type'], 'text');
  const type = (FIELD_TYPES as readonly string[]).includes(rawType)
    ? (rawType as FormFieldType)
    : 'text';
  return {
    id: idOf(r, idx),
    type,
    title: str(r, ['title', 'label', 'name']),
    placeholder: str(r, ['placeholder']),
    required: bool(r, ['required']),
    options: stringList(pick<unknown>(r, ['options'], [])),
    label: str(r, ['label']),
    sort: numOr(r, ['sort'], idx),
  };
}

function mapFormSubmissionState(raw: unknown): FormSubmissionStateVM {
  const r = asRecord(raw);
  const limited = bool(r, ['limited']);
  const maxRaw = pick<unknown>(r, ['max'], null);
  const remRaw = pick<unknown>(r, ['remaining'], null);
  return {
    limited,
    max: maxRaw == null ? null : numOr(r, ['max'], 0),
    count: numOr(r, ['count'], 0),
    remaining: remRaw == null ? null : numOr(r, ['remaining'], 0),
    // Absent (list summary) → assume answerable while the form is open.
    canSubmit: bool(r, ['can_submit', 'canSubmit'], true),
  };
}

export function mapFormSummary(raw: unknown, idx = 0): FormSummaryVM {
  const r = asRecord(raw);
  const status = str(r, ['status']);
  const uuid = str(r, ['uuid']);
  const created = pick<unknown>(r, ['created_at', 'date_created', 'created'], '');
  const updated = pick<unknown>(r, ['last_updated', 'updated_at', 'date_updated', 'modified_at'], '');
  const isPublic = bool(r, ['is_public', 'public']);
  const embedRaw = pick<unknown>(
    r,
    ['embed', 'embed_code', 'embed_url', 'public_embed_url', 'is_embedded', 'is_embed', 'embed_enabled'],
    null,
  );
  return {
    id: idOf(r, idx),
    uuid,
    sysId: str(r, ['sysid', 'sys_id', 'uuid'], uuid),
    title: str(r, ['title'], 'Untitled form'),
    description: stripHtml(str(r, ['description'])),
    status,
    isOpen: bool(r, ['is_open', 'isOpen'], status === 'open'),
    isPublic,
    // NOT derived from `isPublic`. The documented `EuapiForm` schema (list and
    // detail alike) has no embed-related field at all, so a spec-conformant
    // response always leaves `embedRaw` null — and every public form was
    // therefore shown as embeddable with zero evidence an embed exists.
    // `hasFormEmbed(null)` is `false`, so an unconfirmed form now reads as "No
    // embed" until a real field (`embed`/`embed_code`/… above) shows up.
    embedAvailable: hasFormEmbed(embedRaw),
    createdByLabel: formPersonLabel(pick<unknown>(r, ['created_by_user', 'created_by_name', 'createdByName', 'created_by', 'createdBy', 'author', 'creator'], '')),
    createdDateLabel: formatDate(created),
    createdTimeLabel: formatTime(created),
    updatedDateLabel: formatDate(updated),
    updatedTimeLabel: formatTime(updated),
    createdLabel: formatRelative(created),
  };
}

/** GET /v1/api/forms → `{ forms: [...] }` mapped to summaries. */
export function mapForms(data: unknown): FormSummaryVM[] {
  return toList(data).map((row, i) => mapFormSummary(row, i));
}

/** GET /v1/api/forms/{uuid} → `{ form: { ...summary, fields, submission } }`. */
export function mapFormDetail(data: unknown): FormDetailVM {
  const form = domainBody(data, ['form']);
  const summary = mapFormSummary(form);
  const fields = toList(pick<unknown>(form, ['fields'], []))
    .map((f, i) => mapFormField(f, i))
    .sort((a, b) => a.sort - b.sort);
  return {
    ...summary,
    acceptsGlobalAttachment: bool(form, ['accepts_global_attachment']),
    allowedFileTypes: stringList(pick<unknown>(form, ['allowed_file_types'], [])),
    submission: mapFormSubmissionState(pick<unknown>(form, ['submission'], {})),
    fields,
  };
}

// ── Surveys (in-app survey/consent prompts — GET /v1/api/me/surveys) ──────────
/**
 * The question input types the popup knows how to render. An unknown value
 * degrades to 'text' — a garbled question must never break the prompt.
 */
export type SurveyInputType =
  | 'agreement'
  | 'rating'
  | 'scale'
  | 'single_choice'
  | 'multi_choice'
  | 'text';

export type SurveyEnforcement = 'optional' | 'required';
export type SurveyType = 'rating' | 'consent' | 'poll' | 'feedback';

export interface SurveyQuestionVM {
  /** Question id — the key the respond `answers` map is keyed by. */
  id: string;
  label: string;
  inputType: SurveyInputType;
  /** Choices for scale / single_choice / multi_choice; empty otherwise. */
  options: string[];
  required: boolean;
  sort: number;
}

export interface SurveyCategoryVM {
  title: string;
  icon: string;
  color: string;
}

export interface SurveyPromptVM {
  /** Opaque respond token, e.g. "platform:7" | "tenant:3" — echo back verbatim. */
  token: string;
  title: string;
  /** Raw authored rich HTML — callers MUST pass it through sanitizeHtml() before v-html. */
  bodyHtml: string;
  surveyType: SurveyType;
  enforcement: SurveyEnforcement;
  /** enforcement === 'required' — a required survey blocks until answered. */
  required: boolean;
  version: number;
  snoozeDays: number;
  /** true = the platform operator asks ("ERPat asks"); false = the user's own company. */
  isPlatform: boolean;
  /** Consent presentation: Agree stays disabled until the body is scrolled to its end. */
  scrollGate: boolean;
  category: SurveyCategoryVM | null;
  questions: SurveyQuestionVM[];
}

const SURVEY_INPUT_TYPES: readonly SurveyInputType[] = [
  'agreement',
  'rating',
  'scale',
  'single_choice',
  'multi_choice',
  'text',
];
const SURVEY_TYPES: readonly SurveyType[] = ['rating', 'consent', 'poll', 'feedback'];

function mapSurveyQuestion(raw: unknown, idx = 0): SurveyQuestionVM {
  const r = asRecord(raw);
  const rawType = str(r, ['input_type', 'type'], 'text');
  return {
    id: idOf(r, idx),
    label: str(r, ['label', 'title', 'question']),
    inputType: (SURVEY_INPUT_TYPES as readonly string[]).includes(rawType)
      ? (rawType as SurveyInputType)
      : 'text',
    options: stringList(pick<unknown>(r, ['options', 'choices'], [])),
    required: bool(r, ['is_required', 'required']),
    sort: numOr(r, ['sort_order', 'sort'], idx),
  };
}

export function mapSurveyPrompt(raw: unknown, scrollGate = true): SurveyPromptVM {
  const r = asRecord(raw);
  const rawSurveyType = str(r, ['survey_type', 'type'], 'feedback');
  const surveyType = (SURVEY_TYPES as readonly string[]).includes(rawSurveyType)
    ? (rawSurveyType as SurveyType)
    : 'feedback';
  // 'required' on an explicit enforcement match OR the contract's redundant
  // `required` boolean — so a missing/garbled enforcement string alone cannot
  // downgrade a blocking survey. When BOTH are absent/garbled it degrades to
  // OPTIONAL, because bad data must never hard-block the whole app behind a modal.
  const required = str(r, ['enforcement'], 'optional') === 'required' || bool(r, ['required']);
  const catRec = asRecord(pick<unknown>(r, ['category'], null));
  const catTitle = str(catRec, ['title']);
  return {
    token: str(r, ['token', 'key'], ''),
    title: str(r, ['title'], 'Quick survey'),
    bodyHtml: str(r, ['body', 'description', 'content']),
    surveyType,
    enforcement: required ? 'required' : 'optional',
    required,
    version: numOr(r, ['version'], 1),
    snoozeDays: numOr(r, ['snooze_days', 'snoozeDays'], 7),
    isPlatform: bool(r, ['is_platform', 'isPlatform']),
    scrollGate,
    category: catTitle
      ? { title: catTitle, icon: str(catRec, ['icon']), color: str(catRec, ['color']) }
      : null,
    questions: toList(pick<unknown>(r, ['questions'], []))
      .map((q, i) => mapSurveyQuestion(q, i))
      .sort((a, b) => a.sort - b.sort),
  };
}

/**
 * GET /v1/api/me/surveys → `{ surveys: [...], meta: { total, scroll_to_agree } }`.
 * Server order (most-urgent first, required before optional) is preserved — do NOT
 * re-sort. Token-less rows are dropped (there is nothing to respond to). The
 * queue-level scroll_to_agree policy is stamped onto each VM.
 */
export function mapSurveyQueue(data: unknown): SurveyPromptVM[] {
  const rec = asRecord(data);
  const meta = asRecord(pick<unknown>(rec, ['meta'], {}));
  const scrollGate = bool(meta, ['scroll_to_agree', 'scrollToAgree'], true);
  return toList(pick<unknown>(rec, ['surveys'], data))
    .map((row) => mapSurveyPrompt(row, scrollGate))
    .filter((s) => s.token !== '');
}

// ── Mood Tracker (Home widget: "Kamusta ka?") ──────────────────────────────
/**
 * One mood an employee can pick.
 *
 * The set is CONFIGURED PER TENANT, so the widget renders whatever this list
 * contains — it must never hardcode buttons. `color` is a token family name
 * (green | teal | blue | purple | orange | red | muted), not a hex, so the pill
 * tint resolves against the app's own theme in both light and dark.
 */
export interface MoodOptionVM {
  id: string;
  key: string;
  label: string;
  emoji: string;
  color: string;
  score: number;
  sortOrder: number;
}

/** One logged mood. Option fields are denormalized by the API. */
export interface MoodEntryVM {
  id: string;
  optionId: string;
  key: string;
  label: string;
  emoji: string;
  color: string;
  note: string;
  /** Preformatted tenant-local time, e.g. "9:12 AM". */
  timeLabel: string;
  /** Tenant-local calendar day, "Y-m-d". */
  dateLocal: string;
}

/** GET /me/mood — everything the widget needs to paint, in one read. */
export interface MoodTodayVM {
  prompt: string;
  options: MoodOptionVM[];
  current: MoodEntryVM | null;
  entries: MoodEntryVM[];
  count: number;
  canSubmit: boolean;
  retryAfterSeconds: number;
  cooldownSeconds: number;
  /**
   * The tenant's "Let employees add a note" switch (`notes.enabled`). When false
   * the server DROPS a POSTed note rather than refusing it, so the note popup
   * must not be offered — the text would be silently lost.
   */
  notesEnabled: boolean;
  /**
   * Longest note the server accepts (`notes.max_length`), counted in Unicode
   * code points as typed (a plain emoji is one; a skin-tone or ZWJ emoji is two
   * or more). The textarea's own `maxlength` counts UTF-16 units, which are
   * never fewer than code points, so it can only ever be stricter than the server.
   */
  noteMaxLength: number;
  /** The tenant-local date this payload describes — never the device's. */
  date: string;
}

/** Token families the pills may tint with; anything else falls back to teal. */
const MOOD_COLORS = ['green', 'teal', 'blue', 'purple', 'orange', 'red', 'muted'];

const moodColor = (raw: string): string => (MOOD_COLORS.includes(raw) ? raw : 'teal');

export function mapMoodOption(raw: unknown, idx = 0): MoodOptionVM {
  const rec = asRecord(raw);
  return {
    id: idOf(rec, idx),
    key: str(rec, ['key', 'mood_key']),
    label: str(rec, ['label', 'name', 'title']),
    emoji: str(rec, ['emoji', 'icon']),
    color: moodColor(str(rec, ['color'], 'teal')),
    score: numOr(rec, ['score'], 0),
    sortOrder: numOr(rec, ['sort_order', 'sortOrder'], idx),
  };
}

export function mapMoodEntry(raw: unknown, idx = 0): MoodEntryVM {
  const rec = asRecord(raw);
  const key = str(rec, ['key', 'mood_key']);
  return {
    id: idOf(rec, idx),
    optionId: str(rec, ['option_id', 'optionId', 'mood_option_id']),
    key,
    // The API already falls back to the key when an option was renamed away;
    // repeat it here so a partial payload still renders something readable.
    label: str(rec, ['label', 'name'], key),
    emoji: str(rec, ['emoji']),
    color: moodColor(str(rec, ['color'], 'teal')),
    note: str(rec, ['note']),
    // Prefer the server's preformatted label: it is already in the TENANT's
    // timezone, which the device cannot infer.
    timeLabel: str(rec, ['time_label', 'timeLabel']),
    dateLocal: str(rec, ['date_local', 'dateLocal', 'local_date']),
  };
}

/** GET /me/mood → the widget's whole state. */
export function mapMoodToday(data: unknown): MoodTodayVM {
  const rec = asRecord(data);
  const today = asRecord(pick(rec, ['today'], {}));
  const current = pick<unknown>(today, ['current'], null);
  // Absent on a backend older than MoodTracker 1.1.0, which accepted notes by
  // default — so the fallbacks keep the popup on at the original 500 limit.
  const notes = asRecord(pick(rec, ['notes'], {}));

  return {
    prompt: str(rec, ['prompt'], 'Kamusta ka?'),
    options: toList(pick<unknown>(rec, ['options'], [])).map(mapMoodOption),
    current: current ? mapMoodEntry(current) : null,
    entries: toList(pick<unknown>(today, ['entries'], [])).map(mapMoodEntry),
    count: numOr(today, ['count'], 0),
    canSubmit: bool(rec, ['can_submit', 'canSubmit'], true),
    retryAfterSeconds: numOr(rec, ['retry_after_seconds', 'retryAfterSeconds'], 0),
    cooldownSeconds: numOr(rec, ['cooldown_seconds', 'cooldownSeconds'], 60),
    notesEnabled: bool(notes, ['enabled'], true),
    noteMaxLength: numOr(notes, ['max_length', 'maxLength'], 500),
    date: str(today, ['date']),
  };
}

/** GET /me/mood/history → the caller's own past entries. */
export function mapMoodHistory(data: unknown): { entries: MoodEntryVM[]; total: number } {
  const rec = asRecord(data);
  const meta = asRecord(pick(rec, ['meta'], {}));
  // Explicit key read, never toList(data): the payload carries two collections
  // and a bare list-coercion could latch onto the wrong one.
  const entries = toList(pick<unknown>(rec, ['entries'], [])).map(mapMoodEntry);
  return { entries, total: numOr(meta, ['total'], entries.length) };
}

// ── Activity (the caller's own system-log trail) ───────────────────────────
/**
 * Verified against application/controllers/v1/api/account/Activity.php:
 * `GET /v1/api/me/activity` → `{ activity: [row], meta }`, and
 * `GET /v1/api/me/activity/summary` → `{ summary }`. Titles and remarks arrive as
 * plain text (the server strips the HTML the web stores); the raw record
 * snapshots are never sent.
 */
export type ActivitySeverity = 'info' | 'warning' | 'critical';

/** Drives the row glyph only — colour is reserved for `severity`. */
export type ActivityKind = 'signin' | 'attendance' | 'leave' | 'security' | 'create' | 'update' | 'delete' | 'other';

export interface ActivityEntryVM {
  id: string;
  /** Plain-text sentence, e.g. "Dela Cruz, Juan Clocked in at ERPat". */
  title: string;
  /** Catalog description of the event type; '' when uncatalogued. */
  description: string;
  /** Raw action key (`update`, `new_login`). */
  action: string;
  actionLabel: string;
  /** Raw component key — the value the list's `module` filter takes. */
  module: string;
  moduleLabel: string;
  category: string;
  severity: ActivitySeverity;
  kind: ActivityKind;
  remarks: string;
  /** "Chrome 151 · Windows 10", "Automated task", … ('' when unknown). */
  deviceLabel: string;
  userAgent: string;
  ipAddress: string;
  /** The instant, epoch ms (0 when unparseable). */
  atMs: number;
  /** Device-local `YYYY-MM-DD` — the day group the row renders under. */
  dayKey: string;
  timeLabel: string;
  dateLabel: string;
}

export interface ActivityModuleVM {
  key: string;
  label: string;
  count: number;
  recentCount: number;
  lastAtMs: number;
}

export interface ActivitySummaryVM {
  total: number;
  recentDays: number;
  recentCount: number;
  firstAtMs: number;
  lastAtMs: number;
  severity: Record<ActivitySeverity, number>;
  /** Sorted by count, descending (server order). */
  modules: ActivityModuleVM[];
}

const ACTIVITY_SEVERITIES: readonly ActivitySeverity[] = ['info', 'warning', 'critical'];

/**
 * The instant a stamp pair describes. The server sends naive `Y-m-d H:i:s`
 * strings; the UTC one, pinned with `Z`, is an unambiguous instant (and an ISO
 * shape every engine parses), so relative times and day grouping are right in
 * the DEVICE's zone. Falls back to the tenant-local stamp read as device-local —
 * the convention the other mappers use.
 */
function activityStampMs(utc: unknown, local: unknown): number {
  const text = typeof utc === 'string' ? utc.trim() : '';
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(text)) {
    const ms = new Date(`${text.replace(' ', 'T')}Z`).getTime();
    if (Number.isFinite(ms)) return ms;
  }
  return toMillis(local);
}

function activityKindOf(module: string, action: string): ActivityKind {
  const m = module.toLowerCase();
  const a = action.toLowerCase();
  if (m === 'user_activity' || /log_?in|log_?out|sign_?in|sign_?out/.test(a)) return 'signin';
  // The verb outranks the domain for removals: a deleted attendance row reads as a deletion.
  if (/delete|remove|archive|revoke|purge/.test(a)) return 'delete';
  if (/password|mfa|security|role|permission|lock|ban|access/.test(`${m} ${a}`)) return 'security';
  if (/attendance|biotime|timelog|kiosk/.test(m)) return 'attendance';
  if (m.includes('leave')) return 'leave';
  if (/create|add|assign|import|insert|upload|install|new/.test(a)) return 'create';
  if (/update|edit|status|approv|reject|change|set|save|sync|run/.test(a)) return 'update';
  return 'other';
}

/**
 * Where an entry came from. `cli` and `Unknown` are what the server writes when
 * there was no browser at all (a command-line run, a cron job or automation).
 */
function activityDeviceLabel(ua: string): string {
  const text = ua.trim();
  if (/^cli$/i.test(text)) return 'Automated task';
  if (/^unknown$/i.test(text)) return 'Unknown device';
  return describeUserAgent(text);
}

export function mapActivityEntry(raw: unknown, idx = 0): ActivityEntryVM {
  const rec = asRecord(raw);
  const device = asRecord(pick(rec, ['device'], {}));
  const action = str(rec, ['action', 'field_name']);
  const module = str(rec, ['module', 'module_name']);
  const severity = str(rec, ['severity'], 'info') as ActivitySeverity;
  const atMs = activityStampMs(pick(rec, ['occurred_at_utc'], ''), pick(rec, ['occurred_at_local'], null));
  const userAgent = str(device, ['user_agent'], str(rec, ['http_agent']));
  return {
    id: idOf(rec, idx),
    title: str(rec, ['title']),
    description: str(rec, ['description']),
    action,
    actionLabel: str(rec, ['action_label'], action),
    module,
    moduleLabel: str(rec, ['module_label'], module),
    category: str(rec, ['category']),
    severity: ACTIVITY_SEVERITIES.includes(severity) ? severity : 'info',
    kind: activityKindOf(module, action),
    remarks: str(rec, ['remarks']),
    deviceLabel: activityDeviceLabel(userAgent),
    userAgent,
    ipAddress: str(device, ['ip_address'], str(rec, ['ip_address'])),
    atMs,
    dayKey: atMs ? localDateKey(atMs) : '',
    timeLabel: atMs ? formatTime(atMs) : '',
    dateLabel: atMs ? formatDate(atMs) : '',
  };
}

export function mapActivitySummary(data: unknown): ActivitySummaryVM {
  const rec = domainBody(data, ['summary']);
  const severity = asRecord(pick(rec, ['severity'], {}));
  return {
    total: numOr(rec, ['total'], 0),
    recentDays: numOr(rec, ['recent_days'], 7),
    recentCount: numOr(rec, ['recent_count'], 0),
    firstAtMs: activityStampMs(pick(rec, ['first_activity_at_utc'], ''), pick(rec, ['first_activity_at_local'], null)),
    lastAtMs: activityStampMs(pick(rec, ['last_activity_at_utc'], ''), pick(rec, ['last_activity_at_local'], null)),
    severity: {
      info: numOr(severity, ['info'], 0),
      warning: numOr(severity, ['warning'], 0),
      critical: numOr(severity, ['critical'], 0),
    },
    modules: toList(pick<unknown>(rec, ['modules'], []))
      .map((m) => {
        const row = asRecord(m);
        const key = str(row, ['key']);
        return {
          key,
          label: str(row, ['label'], key),
          count: numOr(row, ['count'], 0),
          recentCount: numOr(row, ['recent_count'], 0),
          lastAtMs: activityStampMs(pick(row, ['last_activity_at_utc'], ''), pick(row, ['last_activity_at_local'], null)),
        };
      })
      .filter((m) => m.key !== ''),
  };
}

// ── Assets (Safekeep) ───────────────────────────────────────────────────────
/**
 * Mappers for ERPat's `modules/Assets/` End-User API.
 *
 * The shapes here are confirmed against `docs/openapi.json` (which documents all
 * twelve asset operations) AND `modules/Assets/controllers/Assets_euapi.php`, so
 * the key lists are shorter than elsewhere in this file. They stay defensive
 * about *absence* — a deleted category arrives as `{}`, `ticket_id` is null
 * whenever Helpdesk is unavailable, and `status` has `''` in its own enum.
 */

/** MySQL zero-dates reach the wire as `0000-00-00`; they mean "unset". */
const ZERO_DATE_RE = /^0{4}-0{2}-0{2}/;

/**
 * Format a date for display, falling back to the raw text rather than vanishing.
 *
 * `formatDate` answers `''` for anything it cannot parse, which would silently
 * delete a value the server did send. A zero-date is the one case that really is
 * "nothing", so it is dropped explicitly.
 */
/**
 * A date label that never silently deletes a value the server did send.
 *
 * `formatDate()` answers '' for anything it cannot parse, so a legacy
 * `0000-00-00` and a genuinely absent date would look identical. This guards the
 * zero-date explicitly and falls back to echoing the raw string rather than
 * dropping it. Shared by Assets and Projects — both surface raw DATE columns
 * (`start_date`, `deadline`, `due_date`) that carry zero-dates in older rows.
 */
function safeDateLabel(value: unknown): string {
  if (value == null) return '';
  const raw = typeof value === 'string' ? value.trim() : value;
  if (raw === '') return '';
  if (typeof raw === 'string' && ZERO_DATE_RE.test(raw)) return '';
  return formatDate(raw) || (typeof raw === 'string' ? raw : '');
}

/** A raw-value truthiness test, for `meta` entries (`bool()` reads a record). */
function metaTruthy(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') return ['1', 'true', 'yes', 'on', 'y'].includes(value.toLowerCase().trim());
  return Boolean(value);
}

/** `''`/null/undefined are "no answer"; `false` and `0` are answers and survive. */
function metaIsBlank(value: unknown): boolean {
  return value == null || (typeof value === 'string' && value.trim() === '');
}

/**
 * Render one `meta` value according to its schema field `type`.
 *
 * The vocabulary is exactly what the backend's compiler emits —
 * `Asset_fieldsets_model::map_element_to_type()` returns only
 * `text | number | date | select | bool`, folding `checkbox` and `radio` into
 * `select`. So there is no `checkbox` type to handle, and `select` is text.
 *
 * `bool` needs three writers' worth of tolerance: the web form stores a false
 * bool as `null`, the client API as `0`, and the Excel importer writes the raw
 * cell with no coercion at all (`"0"`, `"No"`). `null` never reaches here — it is
 * filtered as "not recorded" by `metaIsBlank` — so the remaining job is to read
 * `0`/`"0"`/`"No"` as false rather than as a non-empty truthy string.
 */
function formatMetaValue(value: unknown, type: string): string {
  const t = (type || 'text').toLowerCase();
  if (t === 'bool' || t === 'boolean') return metaTruthy(value) ? 'Yes' : 'No';
  if (t === 'date' || t === 'datetime') return safeDateLabel(value);
  return String(value).trim();
}

/** `ram_gb` → "Ram gb" — a readable label for a key the schema does not name. */
function humaniseMetaKey(key: string): string {
  const words = key.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : key;
}

function assetSchemaFields(raw: unknown): AssetFieldSchemaField[] {
  const schema = asRecord(raw);
  return toList<unknown>(pick<unknown>(schema, ['fields'], []))
    .map((f) => {
      const r = asRecord(f);
      return {
        key: str(r, ['key'], ''),
        label: str(r, ['label'], ''),
        type: str(r, ['type'], 'text'),
        required: bool(r, ['required']),
        primary: bool(r, ['primary']),
      };
    })
    .filter((f) => f.key !== '');
}

export function mapAssetCategory(data: unknown, i = 0): AssetCategoryVM {
  const r = asRecord(domainBody(data, ['asset_category', 'category']));
  return {
    id: idOf(r, i),
    name: str(r, ['name', 'title'], ''),
    slug: str(r, ['slug'], ''),
    group: str(r, ['group'], ''),
    icon: str(r, ['icon'], ''),
    // Absent means active: the endpoint returns only active types by default.
    isActive: bool(r, ['is_active'], true),
    fields: assetSchemaFields(pick<unknown>(r, ['field_schema'], {})),
  };
}

export function mapAssetCategories(data: unknown): AssetCategoryVM[] {
  return toList(data).map((c, i) => mapAssetCategory(c, i));
}

/** The embedded category reference. Every field is '' when it was deleted (`{}`). */
function mapAssetCategoryRef(raw: unknown): AssetCategoryRef {
  const r = asRecord(raw);
  return {
    id: str(r, ['id'], ''),
    slug: str(r, ['slug'], ''),
    name: str(r, ['name', 'title'], ''),
    group: str(r, ['group'], ''),
    icon: str(r, ['icon'], ''),
  };
}

/**
 * Includes `'lost'`, which the OpenAPI enum omits but the projection really
 * writes (`Asset_custody_projection::from_custody()`, `closure_reason === 'lost'`).
 */
const ASSET_LIFECYCLES: AssetLifecycle[] = [
  'assigned',
  'acknowledged',
  'return_requested',
  'returned',
  'lost',
];

/**
 * The raw token, and whether it was one we recognise.
 *
 * The distinction matters: an EMPTY status is a documented fall-through we can
 * reason about, while an UNRECOGNISED non-empty token is news from a newer
 * backend — and guessing at it is how a lost asset got shown as "Acknowledged".
 */
function assetLifecycleOf(r: Json): { lifecycle: AssetLifecycle; recognised: boolean } {
  const raw = str(r, ['status'], '').toLowerCase().trim();
  if (raw === '') return { lifecycle: '', recognised: true };
  if ((ASSET_LIFECYCLES as string[]).includes(raw)) return { lifecycle: raw as AssetLifecycle, recognised: true };
  return { lifecycle: '', recognised: false };
}

/**
 * Fold the server's lifecycle + flags into the single headline pill.
 *
 * FIRST MATCH WINS and the order is the contract:
 *   returned → return_requested → return_due → awaiting → active → unknown
 *
 * `has_open_issue` is deliberately NOT consulted. A fault is a separate badge:
 * an asset can be overdue AND faulty, and an acknowledged asset with a fault is
 * still acknowledged — collapsing them into one pill hides whichever lost.
 *
 * `''` is a real member of the server's status enum, so the last two branches
 * exist: fall back to the stored `acknowledged` flag, and only claim `unknown`
 * when even that says nothing. Showing "Confirm receipt" for a state the server
 * could not compute would be a worse guess than showing the tenant's own label.
 */
export function toAssetDisplayStatus(r: Json): AssetDisplayStatus {
  const { lifecycle, recognised } = assetLifecycleOf(r);

  // An unrecognised token is honest news, not a fall-through. Claiming a state we
  // could not read would attach a call to action to a guess.
  if (!recognised) return 'unknown';

  if (str(r, ['returned_at'], '') !== '' || lifecycle === 'returned') return 'returned';
  if (lifecycle === 'lost') return 'lost';

  // `return_requested` outranks `is_overdue` deliberately. Asking for collection
  // leaves the custody OPEN, so the projection keeps `due_at` and `is_overdue`
  // stays true — ranking overdue first would nag someone to do the very thing
  // they already did.
  if (lifecycle === 'return_requested') return 'return_requested';
  if (bool(r, ['is_overdue'])) return 'return_due';

  if (lifecycle === 'assigned') return 'awaiting';
  if (lifecycle === 'acknowledged') return 'active';
  if (bool(r, ['acknowledged'])) return 'active';
  return 'unknown';
}

function mapAssetOpenIssue(raw: unknown): AssetOpenIssueVM | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = asRecord(raw);
  const ticketId = numOr(r, ['ticket_id'], 0);
  return {
    id: str(r, ['id'], ''),
    category: str(r, ['category'], ''),
    description: str(r, ['description'], ''),
    ticketId: ticketId > 0 ? String(ticketId) : '',
    ticketReference: str(r, ['ticket_reference'], ''),
    ticketStatus: str(r, ['ticket_status'], ''),
    submittedLabel: formatRelative(pick<unknown>(r, ['submitted_at'], null)),
  };
}

function mapAssetRow(r: Json, i: number): AssetVM {
  const returnedAt = str(r, ['returned_at'], '');
  const primary = asRecord(pick<unknown>(r, ['primary'], {}));
  const hasOpenIssue = bool(r, ['has_open_issue']);
  const issue = mapAssetOpenIssue(pick<unknown>(r, ['issue'], null));
  return {
    id: idOf(r, i),
    tag: str(r, ['asset_tag'], ''),
    name: str(r, ['name', 'title'], ''),
    category: mapAssetCategoryRef(pick<unknown>(r, ['category'], {})),
    lifecycle: assetLifecycleOf(r).lifecycle,
    statusLabel: str(r, ['status_label'], ''),
    display: toAssetDisplayStatus(r),
    isOverdue: bool(r, ['is_overdue']),
    hasOpenIssue,
    // The server documents these two as always agreeing; trust the flag for the
    // badge and the object for the detail, so a null issue can never render an
    // empty card.
    issue: hasOpenIssue ? issue : null,
    acknowledged: bool(r, ['acknowledged']),
    condition: str(r, ['condition'], ''),
    location: str(r, ['location'], ''),
    issuedAt: str(r, ['issued_at'], ''),
    dueAt: str(r, ['due_at'], ''),
    returnedAt,
    issuedLabel: safeDateLabel(pick<unknown>(r, ['issued_at'], null)),
    dueLabel: safeDateLabel(pick<unknown>(r, ['due_at'], null)),
    returnedLabel: safeDateLabel(pick<unknown>(r, ['returned_at'], null)),
    primaryLabel: str(primary, ['label'], ''),
    primaryValue: str(primary, ['value'], ''),
    meta: asRecord(pick<unknown>(r, ['meta'], {})),
    current: returnedAt === '',
  };
}

export function mapAsset(data: unknown, i = 0): AssetVM {
  return mapAssetRow(asRecord(domainBody(data, ['asset'])), i);
}

export function mapAssets(data: unknown): AssetVM[] {
  return toList(data).map((a, i) => mapAssetRow(asRecord(a), i));
}

function mapAssetAccessories(raw: unknown): AssetAccessoryVM[] {
  return toList<unknown>(raw).map((a, i) => {
    const r = asRecord(a);
    return {
      id: idOf(r, i),
      name: str(r, ['name'], ''),
      note: str(r, ['note'], ''),
    };
  });
}

function mapAssetActivity(raw: unknown): AssetActivityVM[] {
  return toList<unknown>(raw).map((a) => {
    const r = asRecord(a);
    return {
      kind: str(r, ['kind'], 'note'),
      title: str(r, ['title'], ''),
      body: str(r, ['body'], ''),
      occurredLabel: formatRelative(pick<unknown>(r, ['occurred_at'], null)),
    };
  });
}

/**
 * Build the detail "Details" rows from the category's field schema + `meta`.
 *
 * Walks the schema IN ORDER, then appends any `meta` key the schema does not
 * name. `field_schema` is a compiled cache on the category row, so it can lag an
 * asset's actual `meta`; dropping the difference would hide real data. A schema
 * of `{fields: []}` is normal (no fieldset bound) and yields only that tail.
 *
 * `false` and `0` are ANSWERS and are kept — skipping every falsy value is how
 * "Odometer: 0" silently disappears. Only null/undefined/'' are dropped.
 */
export function buildAssetSpecs(fields: AssetFieldSchemaField[], meta: Json): Array<[string, string]> {
  const rows: Array<[string, string]> = [];
  const seen = new Set<string>();

  for (const field of fields) {
    seen.add(field.key);
    const value = meta[field.key];
    if (metaIsBlank(value)) continue;
    const text = formatMetaValue(value, field.type);
    if (text === '') continue;
    rows.push([field.label || humaniseMetaKey(field.key), text]);
  }

  for (const [key, value] of Object.entries(meta)) {
    if (seen.has(key) || metaIsBlank(value)) continue;
    // No schema entry means no declared type — render it as text.
    const text = formatMetaValue(value, 'text');
    if (text === '') continue;
    rows.push([humaniseMetaKey(key), text]);
  }

  return rows;
}

/**
 * One asset in full. `fields` is the schema for this asset's category, looked up
 * from the catalog by the caller — the detail endpoint embeds only the category
 * REFERENCE, not its schema.
 */
export function mapAssetDetail(data: unknown, fields: AssetFieldSchemaField[] = []): AssetDetailVM {
  const r = asRecord(domainBody(data, ['asset']));
  const base = mapAssetRow(r, 0);
  const endorsement = asRecord(pick<unknown>(r, ['endorsement'], {}));
  return {
    ...base,
    notes: str(r, ['notes', 'description'], ''),
    endorsedBy: str(endorsement, ['by'], ''),
    endorsementReference: str(endorsement, ['reference'], ''),
    accessories: mapAssetAccessories(pick<unknown>(r, ['accessories'], [])),
    activity: mapAssetActivity(pick<unknown>(r, ['activity'], [])),
    specs: buildAssetSpecs(fields, base.meta as Json),
  };
}

// ── Asset issues ────────────────────────────────────────────────────────────

function mapAssetIssueRow(r: Json, i: number): AssetIssueVM {
  const ticketId = numOr(r, ['ticket_id'], 0);
  return {
    issueId: str(r, ['issue_id'], '') || idOf(r, i),
    eventId: str(r, ['event_id', 'id'], ''),
    category: str(r, ['category'], ''),
    categoryCode: str(r, ['category_code', 'category'], ''),
    severity: str(r, ['severity'], 'medium').toLowerCase(),
    status: str(r, ['status'], '').toLowerCase(),
    description: str(r, ['description'], ''),
    ticketId: ticketId > 0 ? String(ticketId) : '',
    ticketReference: str(r, ['ticket_reference'], ''),
    ticketStatus: str(r, ['ticket_status'], ''),
    reportedLabel: formatRelative(pick<unknown>(r, ['reported_at'], null)),
    resolvedLabel: formatRelative(pick<unknown>(r, ['resolved_at'], null)),
    // Defaults ON: the backend's own default is 1, and assuming a fix needs no
    // confirmation would quietly close the loop on the holder's behalf.
    verificationRequired: bool(r, ['verification_required'], true),
  };
}

export function mapAssetIssues(data: unknown): AssetIssueVM[] {
  return toList(data).map((x, i) => mapAssetIssueRow(asRecord(x), i));
}

export function mapAssetIssue(data: unknown, i = 0): AssetIssueVM {
  return mapAssetIssueRow(asRecord(domainBody(data, ['issue'])), i);
}

/**
 * The echo from `POST /me/assets/{id}/issues`.
 *
 * Note `id` there is the **ledger event** id and `issue_id` the triageable
 * record — the two are different rows, and the response carries both.
 */
export function mapReportIssueResult(data: unknown): ReportIssueResultVM {
  const r = asRecord(domainBody(data, ['issue']));
  const ticketId = numOr(r, ['ticket_id'], 0);
  return {
    issueId: str(r, ['issue_id'], ''),
    eventId: str(r, ['id'], ''),
    category: str(r, ['category'], ''),
    categoryCode: str(r, ['category_code', 'category'], ''),
    severity: str(r, ['severity'], '').toLowerCase(),
    status: str(r, ['status'], '').toLowerCase(),
    ticketId: ticketId > 0 ? String(ticketId) : '',
    ticketReference: str(r, ['ticket_reference'], ''),
  };
}

// ── Endorsement documents ───────────────────────────────────────────────────

const ENDORSEMENT_ROLES: EndorsementRole[] = ['assignee', 'primary_caretaker', 'accountable'];

function endorsementRoleOf(r: Json): EndorsementRole {
  const raw = str(r, ['my_role'], '').toLowerCase().trim();
  return (ENDORSEMENT_ROLES as string[]).includes(raw) ? (raw as EndorsementRole) : '';
}

function mapEndorsementRow(r: Json, i: number): AssetEndorsementVM {
  const target = asRecord(pick<unknown>(r, ['target'], {}));
  return {
    id: idOf(r, i),
    reference: str(r, ['reference_no', 'reference'], ''),
    documentType: str(r, ['document_type'], '').toLowerCase(),
    status: str(r, ['status'], '').toLowerCase(),
    targetType: str(target, ['type'], '').toLowerCase(),
    targetName: str(target, ['name'], ''),
    myRole: endorsementRoleOf(r),
    purpose: str(r, ['purpose'], ''),
    isTemporary: bool(r, ['is_temporary']),
    releasedLabel: safeDateLabel(pick<unknown>(r, ['released_at'], null)),
    acceptanceDueLabel: safeDateLabel(pick<unknown>(r, ['acceptance_due_at'], null)),
    plannedDueLabel: safeDateLabel(pick<unknown>(r, ['planned_due_at'], null)),
    itemCount: numOr(r, ['item_count'], 0),
    pendingCount: numOr(r, ['pending_count'], 0),
    acceptedCount: numOr(r, ['accepted_count'], 0),
    canAccept: bool(r, ['can_accept']),
    canReject: bool(r, ['can_reject']),
  };
}

export function mapEndorsements(data: unknown): AssetEndorsementVM[] {
  return toList(data).map((e, i) => mapEndorsementRow(asRecord(e), i));
}

function mapCustodyItems(raw: unknown): AssetCustodyItemVM[] {
  return toList<unknown>(raw).map((x, i) => {
    const r = asRecord(x);
    const asset = asRecord(pick<unknown>(r, ['asset'], {}));
    return {
      custodyId: str(r, ['custody_id'], '') || String(i),
      assetId: str(asset, ['id'], ''),
      assetTag: str(asset, ['asset_tag'], ''),
      assetName: str(asset, ['name', 'title'], ''),
      status: str(r, ['status'], '').toLowerCase(),
      accessoriesOut: str(r, ['accessories_out'], ''),
      dueLabel: safeDateLabel(pick<unknown>(r, ['due_at'], null)),
      acceptedLabel: safeDateLabel(pick<unknown>(r, ['accepted_at'], null)),
      rejectedLabel: safeDateLabel(pick<unknown>(r, ['rejected_at'], null)),
    };
  });
}

export function mapEndorsementDetail(data: unknown): AssetEndorsementDetailVM {
  const r = asRecord(domainBody(data, ['endorsement', 'asset_endorsement']));
  const terms = asRecord(pick<unknown>(r, ['terms'], {}));
  const issuer = asRecord(pick<unknown>(r, ['issuer'], {}));
  return {
    ...mapEndorsementRow(r, 0),
    termsText: str(terms, ['text'], ''),
    termsSource: str(terms, ['source'], ''),
    requiresSignature: bool(terms, ['requires_signature']),
    issuerName: str(issuer, ['name'], ''),
    items: mapCustodyItems(pick<unknown>(r, ['items'], [])),
  };
}

// ── Caretaker assets ────────────────────────────────────────────────────────

export function mapCaretakerAssets(data: unknown): CaretakerAssetVM[] {
  return toList(data).map((x, i) => {
    const r = asRecord(x);
    const target = asRecord(pick<unknown>(r, ['target'], {}));
    return {
      custodyId: str(r, ['custody_id'], '') || String(i),
      assetId: str(r, ['asset_id'], ''),
      assetTag: str(r, ['asset_tag'], ''),
      name: str(r, ['name', 'title'], ''),
      targetType: str(target, ['type'], '').toLowerCase(),
      targetName: str(target, ['name'], ''),
      myRole: endorsementRoleOf(r),
      status: str(r, ['status'], '').toLowerCase(),
      acceptedLabel: safeDateLabel(pick<unknown>(r, ['accepted_at'], null)),
      dueLabel: safeDateLabel(pick<unknown>(r, ['due_at'], null)),
    };
  });
}

// ── Projects ───────────────────────────────────────────────────────────────
// Shapes are confirmed against modules/Projects/traits/ProjectsApiTrait.php AND
// the generated public/v1/api/openapi.json, so the key lists here are shorter
// than elsewhere in this file. They stay defensive about ABSENCE, and about the
// zero-dates that legacy rows carry in the raw DATE columns.
//
// Note the naming: everything is `mapProject*`. "Task" in this app means the
// PERSONAL to-do — a project task is a different entity from a different module.

/** Server-owned project statuses -> a human label. */
const PROJECT_STATUS_LABELS: Record<string, string> = {
  open: 'Open',
  completed: 'Completed',
  hold: 'On hold',
  // Single-l "canceled" is the literal ENUM value ERPat stores; the label is not.
  canceled: 'Cancelled',
};

function projectStatusLabel(status: string): string {
  if (!status) return '';
  return PROJECT_STATUS_LABELS[status] ?? status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
}

/** A `YYYY-MM-DD` that is genuinely a date — not '' and not a zero-date. */
function isRealDate(value: string): boolean {
  return value !== '' && !ZERO_DATE_RE.test(value);
}

/** Today as `YYYY-MM-DD` in the DEVICE's local calendar. */
function localTodayKey(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * Which due bucket a task falls into.
 *
 * Compared as `YYYY-MM-DD` STRINGS against the device's local date, never via
 * Date arithmetic: the server sends a bare date with no timezone, so `new
 * Date('2026-09-12')` lands on UTC midnight and would shift the bucket by a day
 * for anyone west of UTC — "due today" would read as "overdue" all morning.
 */
function taskDueBucket(deadline: string): TaskDueBucket {
  if (!isRealDate(deadline)) return 'undated';
  const today = localTodayKey();
  if (deadline < today) return 'overdue';
  if (deadline === today) return 'today';

  // "Soon" is the next seven days, still computed on the date only.
  const d = new Date(`${today}T00:00:00`);
  d.setDate(d.getDate() + 7);
  const p = (n: number) => String(n).padStart(2, '0');
  const horizon = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  return deadline <= horizon ? 'soon' : 'later';
}

function mapProjectLabels(value: unknown): ProjectLabel[] {
  if (!Array.isArray(value)) return [];
  return value.map((raw, i) => {
    const r = asRecord(raw);
    return { id: idOf(r, i), title: str(r, ['title', 'name']), color: str(r, ['color']) };
  });
}

function mapProjectUserRef(value: unknown): ProjectUserRef | null {
  if (value == null) return null;
  const r = asRecord(value);
  const id = str(r, ['id', 'user_id'], '');
  if (!id || id === '0') return null;
  return { id, name: str(r, ['name']), avatar: str(r, ['avatar', 'image']) };
}

function mapProjectUserRefs(value: unknown): ProjectUserRef[] {
  if (!Array.isArray(value)) return [];
  return value.map((v) => mapProjectUserRef(v)).filter((u): u is ProjectUserRef => u !== null);
}

function mapProjectPoints(value: unknown): ProjectPoints {
  const r = asRecord(value);
  return { total: numOr(r, ['total'], 0), completed: numOr(r, ['completed'], 0) };
}

function mapProjectRow(r: Json): ProjectVM {
  const status = str(r, ['status']).toLowerCase();
  const deadline = str(r, ['deadline']);
  const clientRec = asRecord(pick<unknown>(r, ['client'], {}));

  return {
    id: idOf(r),
    title: str(r, ['title', 'name']),
    status,
    statusLabel: projectStatusLabel(status),
    startDate: str(r, ['start_date']),
    deadline,
    deadlineLabel: safeDateLabel(deadline),
    // Only an OPEN project can be overdue. One that completed after its deadline
    // is finished, and flagging it red forever is noise.
    isOverdue: status === 'open' && isRealDate(deadline) && deadline < localTodayKey(),
    progress: numOr(r, ['progress'], 0),
    points: mapProjectPoints(pick<unknown>(r, ['points'], {})),
    labels: mapProjectLabels(pick<unknown>(r, ['labels'], [])),
    isStarred: bool(r, ['is_starred']),
    clientName: str(clientRec, ['company_name', 'name']),
  };
}

export function mapProjects(data: unknown): ProjectVM[] {
  return toList(data).map((p) => mapProjectRow(asRecord(p)));
}

export function mapProjectDetail(data: unknown): ProjectDetailVM {
  const r = asRecord(domainBody(data, ['project']));
  return {
    ...mapProjectRow(r),
    description: str(r, ['description']),
    isLeader: bool(r, ['is_leader']),
  };
}

function mapProjectTaskStatus(value: unknown): ProjectTaskStatusVM {
  const r = asRecord(value);
  return {
    id: str(r, ['id'], ''),
    // '' for tenant-authored statuses — never branch on it, key off `id`.
    key: str(r, ['key']),
    name: str(r, ['name', 'title']),
    color: str(r, ['color']),
    sort: numOr(r, ['sort'], 0),
  };
}

export function mapProjectTaskStatuses(data: unknown): ProjectTaskStatusVM[] {
  return toList(data)
    .map((s) => mapProjectTaskStatus(s))
    .sort((a, b) => a.sort - b.sort);
}

function mapProjectTaskRow(r: Json): ProjectTaskVM {
  const project = asRecord(pick<unknown>(r, ['project'], {}));
  const milestone = pick<unknown>(r, ['milestone'], null);
  const milestoneRec = milestone == null ? null : asRecord(milestone);
  const deadline = str(r, ['deadline']);

  return {
    id: idOf(r),
    title: str(r, ['title', 'name']),
    projectId: str(project, ['id'], ''),
    projectTitle: str(project, ['title']),
    milestoneId: milestoneRec ? str(milestoneRec, ['id'], '') : '',
    milestoneTitle: milestoneRec ? str(milestoneRec, ['title']) : '',
    status: mapProjectTaskStatus(pick<unknown>(r, ['status'], {})),
    assignee: mapProjectUserRef(pick<unknown>(r, ['assignee'], null)),
    collaborators: mapProjectUserRefs(pick<unknown>(r, ['collaborators'], [])),
    priority: numOr(r, ['priority'], 0),
    points: numOr(r, ['points'], 0),
    startDate: str(r, ['start_date']),
    deadline,
    deadlineLabel: safeDateLabel(deadline),
    dueBucket: taskDueBucket(deadline),
    labels: mapProjectLabels(pick<unknown>(r, ['labels'], [])),
    isMine: bool(r, ['is_mine']),
    parentTaskId: str(r, ['parent_task_id'], '0'),
    // Absent on a server that predates the write tier -> false, so the app shows
    // no action rather than offering one that would 404.
    canChangeStatus: bool(r, ['can_change_status']),
    canComment: bool(r, ['can_comment']),
    canLogTime: bool(r, ['can_log_time']),
  };
}

export function mapProjectTasks(data: unknown): ProjectTaskVM[] {
  return toList(data).map((t) => mapProjectTaskRow(asRecord(t)));
}

export function mapProjectChecklist(value: unknown): ProjectChecklistItemVM[] {
  if (!Array.isArray(value)) return [];
  return value.map((raw, i) => {
    const r = asRecord(raw);
    return { id: idOf(r, i), title: str(r, ['title']), isChecked: bool(r, ['is_checked']) };
  });
}

export function mapProjectChecklistItem(data: unknown): ProjectChecklistItemVM {
  const r = asRecord(domainBody(data, ['item']));
  return { id: idOf(r), title: str(r, ['title']), isChecked: bool(r, ['is_checked']) };
}

export function mapProjectTaskDetail(data: unknown): ProjectTaskDetailVM {
  const r = asRecord(domainBody(data, ['task']));
  return {
    ...mapProjectTaskRow(r),
    description: str(r, ['description']),
    checklist: mapProjectChecklist(pick<unknown>(r, ['checklist'], [])),
  };
}

/** One task unwrapped from its domain key — the status-change response. */
export function mapProjectTask(data: unknown): ProjectTaskVM {
  return mapProjectTaskRow(asRecord(domainBody(data, ['task'])));
}

export function mapProjectMilestones(data: unknown): ProjectMilestoneVM[] {
  return toList(data).map((raw, i) => {
    const r = asRecord(raw);
    const due = str(r, ['due_date']);
    return {
      id: idOf(r, i),
      title: str(r, ['title']),
      projectId: str(r, ['project_id'], ''),
      dueDate: due,
      dueDateLabel: safeDateLabel(due),
      progress: numOr(r, ['progress'], 0),
      points: mapProjectPoints(pick<unknown>(r, ['points'], {})),
    };
  });
}

export function mapProjectMembers(data: unknown): ProjectMemberVM[] {
  return toList(data).map((raw, i) => {
    const r = asRecord(raw);
    return {
      userId: str(r, ['user_id', 'id'], String(i)),
      name: str(r, ['name']),
      avatar: str(r, ['avatar', 'image']),
      jobTitle: str(r, ['job_title']),
      userType: str(r, ['user_type']),
      isLeader: bool(r, ['is_leader']),
    };
  });
}

function mapProjectCommentRow(r: Json, i = 0): ProjectCommentVM {
  const createdAt = str(r, ['created_at']);
  return {
    id: idOf(r, i),
    body: str(r, ['body', 'description']),
    author: mapProjectUserRef(pick<unknown>(r, ['author'], null)) ?? { id: '', name: '', avatar: '' },
    totalReplies: numOr(r, ['total_replies'], 0),
    totalLikes: numOr(r, ['total_likes'], 0),
    createdAt,
    createdAtLabel: formatRelative(createdAt) || safeDateLabel(createdAt),
  };
}

export function mapProjectComments(data: unknown): ProjectCommentVM[] {
  return toList(data).map((c, i) => mapProjectCommentRow(asRecord(c), i));
}

export function mapProjectComment(data: unknown): ProjectCommentVM {
  return mapProjectCommentRow(asRecord(domainBody(data, ['comment'])));
}

/**
 * Seconds -> "2h 30m" / "30m" / "45s".
 *
 * Not simply `formatDurationMinutes`: that helper always emits `Xh MMm` because
 * the attendance surfaces depend on a fixed shape, so a half-hour time log would
 * read "0h 30m". A logged duration is prose, not a clock face — drop the hour
 * part when there isn't one. (Widening the shared helper instead would change
 * every attendance and widget total.)
 */
function projectDurationLabel(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '';
  if (seconds < 60) return `${Math.floor(seconds)}s`;

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  return formatDurationMinutes(minutes);
}

export function mapProjectTimeLogs(data: unknown): ProjectTimeLogVM[] {
  return toList(data).map((raw, i) => {
    const r = asRecord(raw);
    const project = asRecord(pick<unknown>(r, ['project'], {}));
    const task = pick<unknown>(r, ['task'], null);
    const taskRec = task == null ? null : asRecord(task);
    const seconds = numOr(r, ['duration_seconds'], 0);
    return {
      id: idOf(r, i),
      projectId: str(project, ['id'], ''),
      projectTitle: str(project, ['title']),
      taskId: taskRec ? str(taskRec, ['id'], '') : '',
      taskTitle: taskRec ? str(taskRec, ['title']) : '',
      status: str(r, ['status']),
      startTime: str(r, ['start_time']),
      endTime: str(r, ['end_time']),
      durationSeconds: seconds,
      durationLabel: projectDurationLabel(seconds),
      note: str(r, ['note']),
    };
  });
}

function mapProjectTimerRow(r: Json, fetchedAtMs: number, i = 0): ProjectTimerVM {
  const project = asRecord(pick<unknown>(r, ['project'], {}));
  const task = pick<unknown>(r, ['task'], null);
  const taskRec = task == null ? null : asRecord(task);
  return {
    id: idOf(r, i),
    projectId: str(project, ['id'], ''),
    projectTitle: str(project, ['title']),
    taskId: taskRec ? str(taskRec, ['id'], '') : '',
    taskTitle: taskRec ? str(taskRec, ['title']) : '',
    note: str(r, ['note']),
    startedAt: str(r, ['started_at']),
    elapsedSeconds: numOr(r, ['elapsed_seconds'], 0),
    fetchedAtMs,
  };
}

/**
 * `GET /me/timers`.
 *
 * `fetchedAtMs` is stamped HERE, once per response, so the running-timer bar can
 * render `elapsedSeconds + (now - fetchedAtMs)` and never parse `startedAt` —
 * which is a tenant-local wall clock with no offset and would be misread by a
 * device in a different timezone.
 */
export function mapProjectTimers(data: unknown, fetchedAtMs: number = Date.now()): ProjectTimersVM {
  const rec = domainBody(data, ['timers']);
  const meta = asRecord(pick<unknown>(rec, ['meta'], {}));
  return {
    timers: toList(data).map((t, i) => mapProjectTimerRow(asRecord(t), fetchedAtMs, i)),
    multipleAllowed: metaTruthy(pick<unknown>(meta, ['multiple_allowed'], false)),
    canManage: metaTruthy(pick<unknown>(meta, ['can_manage'], false)),
  };
}

/** The single timer returned by start / the log returned by stop. */
export function mapProjectTimer(data: unknown, fetchedAtMs: number = Date.now()): ProjectTimerVM {
  return mapProjectTimerRow(asRecord(domainBody(data, ['timer'])), fetchedAtMs);
}

// ── Security PIN (confidential-value unlocks) ──────────────────────────────
/**
 * The server's PIN rules, as `GET /v1/api/me/security-pin` publishes them.
 * `disallowed` is the GENERIC deny-list only (runs, repeats, the most-guessed
 * PINs) — the personal checks (birth date, ID tails) stay server-side, so a PIN
 * that passes this list can still come back as a 422.
 */
export interface SecurityPinPolicyVM {
  length: number;
  maxAttempts: number;
  disallowed: string[];
}

/**
 * The caller's Security PIN state. Contains NO secret — never a PIN, a hash or
 * a hash length — only what the server derives from them.
 */
export interface SecurityPinStatusVM {
  /** False when the install cannot check PINs at all; every prompt stays on the password. */
  available: boolean;
  /** A PIN is on file (and verifiable). */
  enabled: boolean;
  /** Too many wrong PINs: only the password works until it is used, the PIN is reset or re-set. */
  locked: boolean;
  attemptsRemaining: number;
  maxAttempts: number;
  setAtUtc: string;
  setAtLocal: string;
  /** Seconds left on a password cooldown, 0 when none. */
  passwordRetryAfter: number;
  policy: SecurityPinPolicyVM;
}

const SECURITY_PIN_DEFAULT_LENGTH = 4;
const SECURITY_PIN_DEFAULT_MAX_ATTEMPTS = 5;

/** A non-negative whole number from a number or a numeric string; `fallback` otherwise. */
function securityCount(rec: Json, keys: string[], fallback: number): number {
  const v = pick<unknown>(rec, keys, null);
  const n = typeof v === 'number' ? v : typeof v === 'string' && /^\s*\d+\s*$/.test(v) ? Number(v) : Number.NaN;
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : fallback;
}

function mapSecurityPinPolicy(value: unknown): SecurityPinPolicyVM {
  const rec = asRecord(value);
  const list = pick<unknown>(rec, ['disallowed', 'disallowed_pins'], []);
  return {
    length: securityCount(rec, ['length'], SECURITY_PIN_DEFAULT_LENGTH) || SECURITY_PIN_DEFAULT_LENGTH,
    maxAttempts: securityCount(rec, ['max_attempts', 'maxAttempts'], SECURITY_PIN_DEFAULT_MAX_ATTEMPTS),
    disallowed: Array.isArray(list)
      ? list.filter((p): p is string | number => typeof p === 'string' || typeof p === 'number').map(String)
      : [],
  };
}

/**
 * `GET /me/security-pin`, and the `security_pin` block that rides on the save /
 * remove / verify responses.
 *
 * Fails CLOSED: a body with no `security_pin` object (an unexpected shape, an
 * older backend) maps to `available: false, enabled: false`, so no prompt ever
 * switches to PIN entry on a status it could not read.
 */
export function mapSecurityPinStatus(data: unknown): SecurityPinStatusVM {
  const outer = asRecord(data);
  const inner = outer.security_pin;
  const hasBody = !!inner && typeof inner === 'object' && !Array.isArray(inner);
  const rec = hasBody ? domainBody(data, ['security_pin']) : {};
  const maxAttempts = securityCount(rec, ['max_attempts', 'maxAttempts'], SECURITY_PIN_DEFAULT_MAX_ATTEMPTS);

  return {
    available: hasBody && bool(rec, ['available'], false),
    enabled: hasBody && bool(rec, ['enabled'], false),
    locked: hasBody && bool(rec, ['locked'], false),
    attemptsRemaining: securityCount(rec, ['attempts_remaining', 'attemptsRemaining'], maxAttempts),
    maxAttempts,
    setAtUtc: str(rec, ['set_at_utc', 'set_at']),
    setAtLocal: str(rec, ['set_at_local']),
    passwordRetryAfter: securityCount(rec, ['password_retry_after', 'passwordRetryAfter'], 0),
    policy: mapSecurityPinPolicy(pick<unknown>(rec, ['policy'], {})),
  };
}

// ── Cut-off earnings (GET /me/cutoff-earnings) ───────────────────────────────

/** One allowance the payroll engine priced for the cut-off. */
export interface CutoffEarningsAllowanceVM {
  templateId: number;
  itemKey: string;
  title: string;
  category: string;
  frequency: string;
  computationMode: string;
  conditions: string[];
  isTaxable: boolean;
  isDeminimis: boolean;
  /** The amount at full attendance — what Gross Earnings includes. */
  entitled: number;
  /** What attendance so far still earns, remaining days assumed complete. */
  projected: number;
  status: 'full' | 'reduced' | 'forfeited';
}

/** One deduction template or loan repayment the run would take on the cut-off. */
export interface CutoffEarningsDeductionVM {
  itemKey: string;
  title: string;
  kind: 'template' | 'loan';
  category: string;
  amount: number;
}

/**
 * The `cutoff_earnings` payload — see `cutoffEarningsService` for the contract. Money is
 * in `currency`, already rounded to 2 decimals by the server.
 */
export interface CutoffEarningsVM {
  startKey: string;
  endKey: string;
  asOf: string;
  currency: string;
  hasSalary: boolean;
  /** `payslip` = the period's released payslip lines; `computed` = a live estimate. */
  source: 'computed' | 'payslip';
  payslipId: number | null;
  payTerm: string;
  payTermSource: string;
  payDate: string;
  attendance: {
    measuredThrough: string | null;
    scheduledDays: number;
    remainingDays: number;
    pendingDays: number;
  };
  basePay: { entitled: number; projected: number };
  /** What attendance so far has cost — base and allowances together. */
  attendanceDeduction: number;
  allowances: CutoffEarningsAllowanceVM[];
  deductions: CutoffEarningsDeductionVM[];
  totals: {
    allowancesEntitled: number;
    allowancesProjected: number;
    deductions: number;
    /** base entitled + allowances entitled — the card's Gross Earnings. */
    grossEntitled: number;
    /** max(0, base projected + allowances projected − deductions) — the card's Projected Take Home. */
    takeHomeProjected: number;
  };
  excludes: string[];
}

function cutoffLineStatus(v: unknown): CutoffEarningsAllowanceVM['status'] {
  return v === 'reduced' || v === 'forfeited' ? v : 'full';
}

/**
 * Map the `cutoff_earnings` body (already unwrapped from its domain key). Defensive like
 * every mapper here: a missing block reads as zeros and empty lists, never as a throw, so a
 * partial answer renders a partial card rather than an error.
 */
export function mapCutoffEarnings(data: unknown): CutoffEarningsVM {
  const rec = asRecord(data);
  const attendance = asRecord(pick(rec, ['attendance'], {}));
  const basePay = asRecord(pick(rec, ['base_pay'], {}));
  const totals = asRecord(pick(rec, ['totals'], {}));
  const source = str(rec, ['estimate_source']) === 'payslip' ? 'payslip' : 'computed';
  const payslipId = numOf(rec, ['payslip_id'], 0);

  const allowances: CutoffEarningsAllowanceVM[] = toList(pick(rec, ['allowances'], [])).map((raw) => {
    const line = asRecord(raw);
    return {
      templateId: numOf(line, ['template_id']),
      itemKey: str(line, ['item_key']),
      title: str(line, ['title']),
      category: str(line, ['category']),
      frequency: str(line, ['frequency']),
      computationMode: str(line, ['computation_mode']),
      conditions: stringList(pick<unknown>(line, ['conditions'], [])),
      isTaxable: bool(line, ['is_taxable'], false),
      isDeminimis: bool(line, ['is_deminimis'], false),
      entitled: numOf(line, ['entitled']),
      projected: numOf(line, ['projected']),
      status: cutoffLineStatus(pick<unknown>(line, ['status'], 'full')),
    };
  });

  const deductions: CutoffEarningsDeductionVM[] = toList(pick(rec, ['deductions'], [])).map((raw) => {
    const line = asRecord(raw);
    return {
      itemKey: str(line, ['item_key']),
      title: str(line, ['title']),
      kind: str(line, ['kind']) === 'loan' ? 'loan' : 'template',
      category: str(line, ['category']),
      amount: numOf(line, ['amount']),
    };
  });

  return {
    startKey: str(rec, ['start_date']),
    endKey: str(rec, ['end_date']),
    asOf: str(rec, ['as_of']),
    currency: str(rec, ['currency'], 'PHP') || 'PHP',
    hasSalary: bool(rec, ['has_salary'], false),
    source,
    payslipId: payslipId > 0 ? payslipId : null,
    payTerm: str(rec, ['pay_term']),
    payTermSource: str(rec, ['pay_term_source']),
    payDate: str(rec, ['pay_date']),
    attendance: {
      measuredThrough: str(attendance, ['measured_through']) || null,
      scheduledDays: numOf(attendance, ['scheduled_days']),
      remainingDays: numOf(attendance, ['remaining_days']),
      pendingDays: numOf(attendance, ['pending_days']),
    },
    basePay: {
      entitled: numOf(basePay, ['entitled']),
      projected: numOf(basePay, ['projected']),
    },
    attendanceDeduction: numOf(rec, ['attendance_deduction']),
    allowances,
    deductions,
    totals: {
      allowancesEntitled: numOf(totals, ['allowances_entitled']),
      allowancesProjected: numOf(totals, ['allowances_projected']),
      deductions: numOf(totals, ['deductions']),
      grossEntitled: numOf(totals, ['gross_entitled']),
      takeHomeProjected: numOf(totals, ['take_home_projected']),
    },
    excludes: stringList(pick<unknown>(rec, ['excludes'], [])),
  };
}
